import { PageHeader } from "@/components/shared/page-help";
import { AiCard } from "@/components/ui/ai-card";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	CHART,
	DayBars,
	DeltaBadge,
	Donut,
	SegmentBar,
	StatTile,
	TrendChart,
	fmtCompact,
} from "@/components/ui/charts";
import { LoadingInline } from "@/components/ui/feedback";
import { Input, Textarea } from "@/components/ui/input";
import { LabeledSelect, Select as StyledSelect } from "@/components/ui/select";
import { KpiCard, MiniBar } from "@/components/ui/stats";
import { useToast } from "@/components/ui/toast";
import {
	type AnswerRule,
	type AnswerRules,
	type AnswerStatRow,
	type CatalogResponse,
	type CxReasonMeta,
	type CxRecord,
	DEFAULT_STATS_DAYS,
	type EvalPromptsResponse,
	type EvalRules,
	type EvalStatRow,
	type GuidanceEntry,
	type GuidanceResponse,
	type HistoryRecord,
	type IoContractResponse,
	type IoStage,
	type JudgeTraceEntry,
	type LoopMapNode,
	type NodeEvent,
	type OutputBlock,
	type OverviewByDate,
	type OverviewKpi,
	type PromptNodeKey,
	type PromptPart,
	type PromptVar,
	type RcaEvidenceQuote,
	type RecordSummary,
	type Scenario,
	type ScenarioRunResult,
	type ScenariosResponse,
	type SessionFocus,
	type SessionPending,
	type SimCard,
	type SimRunReport,
	type SimScenario,
	type SimScenarioEval,
	type SimScenarioLive,
	type SkillRow,
	type Timing,
	type TokenStatsRow,
	type TraceStep,
	type TurnView,
	useAnswerRules,
	useAnswerStats,
	useCalibrationDetail,
	useCalibrations,
	useCatalog,
	useClearHistory,
	useClearRecordings,
	useConnect,
	useConnectProbe,
	useCxRecordDetail,
	useCxRecords,
	useCxStandard,
	useCxSummary,
	useEvalPrompts,
	useEvalRules,
	useEvalStats,
	useGuidance,
	useGuidanceStats,
	useIoContract,
	useLoopMap,
	useMethodSkills,
	useNodeModels,
	usePrompts,
	useRcaContractLint,
	useRcaRecord,
	useRcaRecords,
	useRcaStandard,
	useRcaStats,
	useRecordDetail,
	useRecords,
	useRunCxScore,
	useRunRca,
	useRunScenarios,
	useRunSim,
	useSaveEvalPrompts,
	useSavePrompts,
	useScenarios,
	useSessionTurn,
	useSetConnect,
	useSetGuidanceStatus,
	useSetNodeModel,
	useSetSkillPort,
	useSimRun,
	useSimRuns,
	useSimScenarios,
	useSkillPort,
	useStatsOverview,
	useTestSets,
	useTestset,
	useTiming,
	useTokenRecords,
	useTokenStats,
	useUpsertGuidance,
} from "@/lib/api/loop-lab";
import type { SkillPortProbe, SkillPortSkill } from "@/lib/api/loop-lab";
import * as DrawerPrimitive from "@radix-ui/react-dialog";
import { createFileRoute, useLocation, useNavigate } from "@tanstack/react-router";
import { X } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/loop-mvp-lab")({
	component: LoopMvpLabPage,
});

type Tab =
	| "overview"
	| "io-contract"
	| "stats"
	| "history"
	| "reference-intent"
	| "reference-skill"
	| "reference-rules"
	| "guidance"
	| "connect"
	| "reference-data"
	| "prompts"
	| "scenarios"
	| "playground"
	| "eval"
	| "eval-records"
	| "rca-records"
	| "eval-scenarios"
	| "eval-machine-customer"
	| "eval-judges"
	| "eval-rules"
	| "rca-standard"
	| "cx-records"
	| "cx-standard"
	| "guidance-stats"
	| "method-skills";
const TABS: Tab[] = [
	"overview",
	"io-contract",
	"stats",
	"history",
	"reference-intent",
	"reference-skill",
	"reference-rules",
	"guidance",
	"connect",
	"reference-data",
	"prompts",
	"scenarios",
	"playground",
	"eval",
	"eval-records",
	"rca-records",
	"eval-scenarios",
	"eval-machine-customer",
	"eval-judges",
	"eval-rules",
	"rca-standard",
	"cx-records",
	"cx-standard",
	"guidance-stats",
	"method-skills",
];

const TAB_TITLE: Record<Tab, string> = {
	overview: "链路总览",
	"io-contract": "IO 一致性体检",
	stats: "指标统计",
	history: "运行记录",
	"reference-intent": "意图",
	"reference-skill": "SKILL",
	"reference-rules": "规则",
	guidance: "应答指南",
	connect: "连接",
	"reference-data": "评测数据",
	prompts: "应答提示词",
	scenarios: "固定用例",
	playground: "人工提问",
	eval: "模拟场景",
	"eval-records": "评测记录",
	"rca-records": "归因记录",
	"rca-standard": "归因标准与提示词",
	"eval-scenarios": "模拟场景库",
	"eval-machine-customer": "模拟提示词",
	"eval-judges": "评测提示词",
	"eval-rules": "评测规则",
	"cx-records": "CX 评分",
	"cx-standard": "CX 评分标准",
	"guidance-stats": "指南效果",
	"method-skills": "优化打法",
};

/** 每页一句话说明(表头副标题)· 标题 + 一句话,别的都不放。 */
const TAB_DESC: Record<Tab, string> = {
	overview:
		"主链路闭环:提问→应答→评测→归因→优化→回灌。标签 hover 看说明、点「前往」跳对应台;应答可展开内部机理。",
	"io-contract":
		"跨页一致性体检:声明 / 注入 / 产出 三面差异、双源漂移、诊断覆盖度。字段规格看各自的提示词 / SKILL / 规则页。",
	stats: "耗时 / 应答效果 / 场景评测 / 归因分布 / Token 用量,默认近 3 天。",
	history: "三套测试源归一的运行记录,可多维筛选、下钻详情。",
	"reference-intent": "15 类意图识别清单。",
	"reference-skill": "14 个 Skill 的输入输出与参数来源。",
	connect: "Shopify 店铺连接与探针体检(live 执行的数据源)。",
	"reference-rules": "应答链路的确定性护栏与承载,六域规则注册表。",
	guidance: "商家/平台的应答方式指南,按意图标签召回注入 N3,在线可增改与上下线。",
	"reference-data": "评测用的客户 / 订单 / 商品沙盒数据。",
	prompts: "N1 意图 / N2 决策 / N3 生成三节点提示词,在线可改。",
	scenarios: "固定用例,可筛选、勾选批量、逐轮真多轮跑。",
	playground: "人工交互提问,单 / 多轮一套引擎。",
	eval: "机器顾客 × 多维判分 × pass^k 一致性。",
	"eval-records": "评测批次历史,可独立筛选与对比。",
	"rca-records": "失败场景的根因分析结论。",
	"rca-standard": "归因标准与提示词(9 类 / 5 簇),可配置。",
	"eval-scenarios": "模拟场景四件套 + 判分契约 + 关联数据。",
	"eval-machine-customer": "机器顾客怎么生成问题 + 变量注入。",
	"eval-judges": "NL 合规 + 错误归因的评测提示词。",
	"eval-rules": "确定性判分逻辑:三维乘积、一票否决。",
	"cx-records": "会话级服务体验评分(Intercom CX Score 口径),独立于逐维判分。",
	"cx-standard": "会话级 CX 评分的字段契约、确定性规则与提示词,与运行代码同源。",
	"guidance-stats": "应答指南在评测批次里的效果:命中→采纳→遵循漏斗、四象限与召回核验。",
	"method-skills":
		"仓库固化的分析/优化方法论 skill 的现状与版本历史,与 .claude/skills 同源只读,变更走 PR。",
};

/** 跨 tab 把一条场景载入「试跑」交互台:携带客户身份 + 逐轮消息(仅自然语言轮可载) */
export type ScenarioLoad = { id: string; customer_id: string; msgs: string[] };

function LoopMvpLabPage() {
	const testset = useTestset();
	const hasKey = testset.data?.has_key ?? false;
	const location = useLocation();
	const navigate = useNavigate();
	const raw = (location.hash || "").replace(/^#/, "");
	const tab: Tab = (TABS as string[]).includes(raw) ? (raw as Tab) : "overview"; // C26:总览为默认落地页
	const [pendingLoad, setPendingLoad] = useState<ScenarioLoad | null>(null);
	const [histPreset, setHistPreset] = useState<HistPreset | null>(null);

	function tryScenario(load: ScenarioLoad) {
		setPendingLoad(load);
		navigate({ to: "/loop-mvp-lab", hash: "playground" });
	}

	// 统计页「查看详情」→ 跳「记录」并带入筛选
	function drillHistory(preset: HistPreset) {
		setHistPreset(preset);
		navigate({ to: "/loop-mvp-lab", hash: "history" });
	}

	return (
		<div className="space-y-6">
			<PageHeader moduleId="C2-loop-lab" title={TAB_TITLE[tab]} description={TAB_DESC[tab]} />

			{tab === "overview" ? <LoopOverviewTab /> : null}
			{tab === "io-contract" ? <IoContractTab /> : null}
			{tab === "stats" ? (
				<StatsTab
					onDrill={drillHistory}
					onGotoCx={() => navigate({ to: "/loop-mvp-lab", hash: "cx-records" })}
				/>
			) : null}
			{tab === "history" ? (
				<HistoryPanel preset={histPreset ?? undefined} onConsume={() => setHistPreset(null)} />
			) : null}
			{tab === "reference-intent" ? <IntentRefTab /> : null}
			{tab === "reference-skill" ? <SkillRefTab /> : null}
			{tab === "connect" ? <ConnectTab /> : null}
			{tab === "reference-rules" ? <AnswerRulesTab /> : null}
			{tab === "guidance" ? <GuidanceTab /> : null}
			{tab === "reference-data" ? <EvalDataTab /> : null}
			{tab === "prompts" ? <PromptsPanel /> : null}
			{tab === "scenarios" ? <ScenariosTab hasKey={hasKey} onTry={tryScenario} /> : null}
			{tab === "playground" ? (
				<PlaygroundTab
					hasKey={hasKey}
					pendingLoad={pendingLoad}
					onConsumeLoad={() => setPendingLoad(null)}
				/>
			) : null}
			{tab === "eval" ? <EvalTab hasKey={hasKey} /> : null}
			{tab === "eval-records" ? <EvalRecordsTab /> : null}
			{tab === "rca-records" ? <RcaRecordsTab /> : null}
			{tab === "rca-standard" ? <RcaStandardTab /> : null}
			{tab === "cx-records" ? <CxRecordsTab onGotoRecord={(rid) => drillHistory({ rid })} /> : null}
			{tab === "cx-standard" ? <CxStandardTab /> : null}
			{tab === "guidance-stats" ? <GuidanceStatsTab /> : null}
			{tab === "method-skills" ? <MethodSkillsTab /> : null}
			{tab === "eval-scenarios" ? <EvalScenariosTab /> : null}
			{tab === "eval-machine-customer" ? <MachineCustomerPanel /> : null}
			{tab === "eval-judges" ? <JudgesPanel /> : null}
			{tab === "eval-rules" ? (
				<EvalRulesPanel
					onGotoJudges={() => navigate({ to: "/loop-mvp-lab", hash: "eval-judges" })}
				/>
			) : null}
		</div>
	);
}

/* ====================== 通用展示件 ====================== */
function JsonBox({ value }: { value: unknown }) {
	return (
		<pre className="overflow-x-auto rounded-md border border-surface-border bg-surface-muted/40 p-3 font-mono text-xs leading-5 text-text-primary">
			{JSON.stringify(value, null, 2)}
		</pre>
	);
}

type Tone = "success" | "warning" | "danger" | "info" | "muted";
const TONE: Record<Tone, string> = {
	success: "bg-brand-success/10 text-brand-success",
	warning: "bg-brand-warning/10 text-brand-warning",
	danger: "bg-brand-danger/10 text-brand-danger",
	info: "bg-brand-info/10 text-brand-info", // C25 批3:info 真色(原借用近黑=灰)
	muted: "bg-surface-muted text-text-secondary",
};
// C25 批2:Pill 收编为全站 Badge 原语(rounded-full;info 修为真 info 色;tone 单枚举)
const Pill = Badge;

function observeTone(observe: string): Tone {
	if (observe.startsWith("blocked")) return "warning";
	if (["render", "done"].includes(observe)) return "success";
	if (["cannot", "handoff"].includes(observe)) return "danger";
	if (observe === "confirm_gate" || observe === "multi") return "info";
	if (observe === "empty") return "muted";
	return "muted";
}
function decideTone(decide: string): Tone {
	if (decide === "call") return "success";
	if (decide === "call_attempt") return "warning";
	if (decide === "chain") return "info";
	return "muted";
}
const DECIDE_LABEL: Record<string, string> = {
	call: "调用",
	call_attempt: "试探",
	chain: "链式前置",
};

/** 中间过程:逐步铺开 */
function TraceSteps({ trace }: { trace?: TraceStep[] }) {
	const [openIdx, setOpenIdx] = useState<number | null>(null);
	const steps = trace ?? [];
	if (!steps.length) {
		return <div className="text-xs text-text-muted">(本轮无工具调用)</div>;
	}
	return (
		<ol className="space-y-1.5">
			{steps.map((s, i) => (
				<li
					key={`${s.skill}-${i}`}
					className="rounded-md border border-surface-border bg-surface-card"
				>
					<button
						type="button"
						onClick={() => setOpenIdx(openIdx === i ? null : i)}
						className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
					>
						<span className="font-mono text-xs text-text-muted">{i + 1}</span>
						<Pill tone={decideTone(s.decide)}>{DECIDE_LABEL[s.decide] ?? s.decide}</Pill>
						<span className="font-mono text-text-primary">{s.skill}</span>
						<span className="text-text-muted">→</span>
						<Pill tone={observeTone(s.observe)}>{s.observe}</Pill>
						{s.reason ? (
							<span className="truncate text-xs text-text-muted">· {s.reason}</span>
						) : null}
						<span className="ml-auto shrink-0 text-xs text-text-muted">
							{openIdx === i ? "收起入参" : "看入参"}
						</span>
					</button>
					{openIdx === i ? (
						<div className="space-y-2 px-3 pb-3">
							{s.reason ? (
								<div className="text-xs text-text-secondary">主 Agent 决策理由:{s.reason}</div>
							) : null}
							<JsonBox value={s.params ?? {}} />
						</div>
					) : null}
				</li>
			))}
		</ol>
	);
}

/* ====================== 统一执行还原组件:ExecutionTrace ======================
   节点级逐步还原一轮真实执行:N1意图/N2每轮决策/N3生成/接续(LLM·含渲染后提示词+原始响应)
   + B2.5组装/G1必填/G6排除/G4判停(规则)+ 工具(完整 I/O)。全站(场景库/试跑/历史)复用此一处。
   无 node_trace 的旧记录 → 兜底回退到简版 TraceSteps。 */
const NODE_LABEL: Record<string, { label: string; tone: Tone }> = {
	n1_intent: { label: "N1 意图", tone: "info" },
	n2_decide: { label: "N2 决策", tone: "info" },
	n3_generate: { label: "N3 生成", tone: "info" },
	n1_resume: { label: "N1 接续", tone: "info" },
};
function nodeBadge(e: NodeEvent): { label: string; tone: Tone } {
	if (e.kind === "llm") return NODE_LABEL[e.node] ?? { label: e.node, tone: "info" };
	if (e.kind === "tool") return { label: "工具", tone: "success" };
	if (e.kind === "rule") {
		if (e.node === "O2") return { label: "O2 指南召回", tone: "info" };
		return { label: `规则 ${e.node}`, tone: "muted" }; // 常规过程降噪:warning 只留异常
	}
	return { label: "说明", tone: "muted" };
}

// 业务中文名映射(全站统一,让标签一眼业务可读)
const INTENT_ZH: Record<string, string> = {
	product_recommend: "商品推荐",
	product_compare: "商品对比",
	find_similar: "找相似/更便宜",
	product_info: "单品问询",
	discount: "优惠",
	order_status: "查订单",
	shipping_track: "查物流",
	refund: "退款",
	return: "退货",
	cancel: "取消订单",
	update_address: "改地址",
	knowledge_qa: "政策问答",
	complaint: "投诉转人工",
	out_of_scope: "越界",
	chitchat: "闲聊",
};
const SKILL_ZH: Record<string, string> = {
	"search-order": "查订单",
	"shipping-status": "查物流",
	"product-info": "单品问询",
	"product-recommend": "推荐",
	"product-compare": "对比",
	"find-similar": "找相似",
	"discount-code": "优惠码",
	"knowledge-search": "知识检索",
	refund: "退款",
	return: "退货",
	"cancel-order": "取消订单",
	"update-address": "改地址",
	handoff: "转人工",
};
const ACTION_ZH: Record<string, string> = {
	call_skill: "调用工具",
	clarify: "反问澄清",
	done: "收尾",
};
// N3 应答类型(互斥 7 类)中文名
const REPLY_TYPE_ZH: Record<string, string> = {
	inform: "说明(有依据)",
	clarify: "澄清",
	confirm: "确认",
	followup: "追问承接",
	chitchat: "闲聊",
	decline: "越界婉拒",
	handoff: "转人工",
};
function intentZh(i?: string | null): string {
	return i ? (INTENT_ZH[i] ? `${i}(${INTENT_ZH[i]})` : i) : "—";
}
function skillZh(s: string): string {
	return SKILL_ZH[s] ? `${s}(${SKILL_ZH[s]})` : s;
}

function IOBlock({ label, value }: { label: string; value: unknown }) {
	if (value === undefined || value === null || value === "") return null;
	return (
		<div>
			<div className="mb-1 text-xs font-medium text-text-secondary">{label}</div>
			{typeof value === "string" ? (
				<pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md border border-surface-border bg-surface-muted/40 p-3 font-mono text-xs leading-5 text-text-primary">
					{value}
				</pre>
			) : (
				<JsonBox value={value} />
			)}
		</div>
	);
}

/* ---- 核心输出标签:全部由真实 output 结构派生(适配各情况,不写死字段)---- */
// 把任意对象压成一眼可读的紧凑标签:list→key:N,bool→key,标量→key:value,嵌套→key:{子键}
function compactTags(obj: unknown, max = 5): string[] {
	if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
	const out: string[] = [];
	for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
		if (v === null || v === undefined || v === "") continue;
		if (Array.isArray(v)) out.push(`${k}:${v.length}`);
		else if (typeof v === "object") {
			const ks = Object.keys(v as object);
			if (ks.length) out.push(`${k}:{${ks.slice(0, 3).join(",")}}`);
		} else if (typeof v === "boolean") {
			if (v) out.push(k);
		} else out.push(`${k}:${String(v).slice(0, 28)}`);
		if (out.length >= max) break;
	}
	return out;
}

type NodeTagView = { primary?: { label: string; tone: Tone }; tags: string[]; note?: string };
// 每个节点的"核心产出"标签 + 业务说明(note):从真实 output 取关键值,缺字段自然省略
function nodeTagView(e: NodeEvent): NodeTagView {
	const out = e.output ?? {};
	if (e.kind === "tool") {
		const payload = (out.payload ?? {}) as Record<string, unknown>;
		const { status, _backend, _recorded_miss, _live_fallback, ...rest } = payload as {
			status?: unknown;
			_backend?: unknown;
			_recorded_miss?: unknown;
			_live_fallback?: unknown;
		};
		const s = status ? String(status) : "";
		const tags = compactTags(rest);
		// 调用来源(真实执行/录制回放/罐头模拟)——运营一眼分清这次是真数据还是模拟
		if (_backend) {
			const b = String(_backend);
			tags.unshift(
				b === "live" ? "来源:真实执行" : b === "recorded" ? "来源:录制回放" : "来源:罐头模拟",
			);
			if (_recorded_miss) tags.push("存档未命中→已回落罐头");
			if (_live_fallback) tags.push(`真实执行回落:${String(_live_fallback)}`);
		}
		return {
			primary: s ? { label: s, tone: observeTone(s) } : undefined,
			tags,
		};
	}
	if (e.kind === "rule") {
		if (e.node === "O2") {
			// 应答指南召回(P2):命中集与截断一眼可见;采纳看 N3 生成事件的「采纳」标签
			const ids = (out.hit_ids as string[] | undefined) ?? [];
			const trimmed = Number(out.trimmed ?? 0);
			return {
				primary: {
					label: `召回 ${ids.length} 条${trimmed ? `(截断 ${trimmed})` : ""}`,
					tone: "info",
				},
				tags: ids,
			};
		}
		if (e.node === "G1") {
			const missing = (out.missing as unknown[]) ?? [];
			return missing.length
				? { primary: { label: `缺:${missing.join(",")}`, tone: "warning" }, tags: [] }
				: { primary: { label: "必填齐全", tone: "success" }, tags: [] };
		}
		if (e.node === "G4") {
			const stop = out.stop ? String(out.stop) : "";
			return {
				primary: stop ? { label: `stop:${stop}`, tone: observeTone(stop) } : undefined,
				tags: [],
			};
		}
		if (e.node === "G6")
			return { primary: { label: `排除→${out.swapped_to ?? "?"}`, tone: "warning" }, tags: [] };
		if (e.node === "B2.5") {
			const keys = Object.keys((out.params ?? {}) as object);
			const tags = keys.length ? [...keys] : ["(空参)"];
			// resolve=组参内部解析过程(C23 档3):选行模式/多目标取首/地址查表/枚举校验 可见
			const resolve = (out.resolve ?? {}) as {
				line_pick?: { mode?: string };
				order?: { multi_first_only?: boolean };
				addr?: { hit?: boolean };
				reason?: { enum_ok?: boolean };
			};
			if (resolve.line_pick?.mode) tags.unshift(`选行:${resolve.line_pick.mode}`);
			if (resolve.order?.multi_first_only) tags.push("多目标取首个(C24⏸)");
			if (resolve.addr) tags.push(resolve.addr.hit ? "地址簿命中" : "地址簿未命中");
			if (resolve.reason && !resolve.reason.enum_ok) tags.push("退货原因枚举不合法");
			return { tags };
		}
		return { tags: compactTags(out) };
	}
	if (e.kind === "llm") {
		const parsed = (out.parsed ?? null) as Record<string, unknown> | null;
		if (e.node === "n1_intent" && parsed) {
			// 业务视角:意图中文 + 实体带值(order_ref=1001) + 接续动作(多轮已折叠进 N1)
			const ent = parsed.entities as Record<string, unknown> | undefined;
			const tags = ent ? compactTags(ent) : [];
			const r = String(parsed.resume ?? "");
			if (r && r !== "new" && r !== "follow_up") tags.unshift(`接续:${RESUME_LABEL[r] ?? r}`);
			return {
				primary: { label: `意图:${intentZh(parsed.intent as string)}`, tone: "info" },
				tags,
			};
		}
		if (e.node === "n2_decide" && parsed) {
			const act = parsed.action ? String(parsed.action) : "";
			const tags: string[] = [];
			if (parsed.skill) tags.push(skillZh(String(parsed.skill)));
			if (parsed.slot) tags.push(`反问槽:${parsed.slot}`);
			return {
				primary: act ? { label: ACTION_ZH[act] ?? act, tone: "info" } : undefined,
				tags,
				note: parsed.reason ? `理由:${parsed.reason}` : undefined, // 大模型为何这么决策
			};
		}
		if (e.node === "n1_resume" && parsed) {
			const ZH: Record<string, string> = {
				provide: "补充信息",
				confirm: "确认执行",
				cancel: "反悔放弃",
				switch: "切换意图",
			};
			const a = String(parsed.action ?? "");
			return { primary: { label: `接续:${ZH[a] ?? a}`, tone: "info" }, tags: [] };
		}
		if (e.node === "n3_generate") {
			const blocks = (parsed?.blocks as Array<Record<string, unknown>> | undefined) ?? [];
			const texts = blocks
				.filter((b) => b.kind === "text")
				.map((b) => String(b.text ?? ""))
				.join(" ");
			const nProd = blocks.filter((b) => b.kind === "product").length;
			const nCoup = blocks.filter((b) => b.kind === "coupon").length;
			const t = parsed?.reply_type ? String(parsed.reply_type) : "";
			const tags: string[] = [];
			if (nProd) tags.push(`商品卡×${nProd}`);
			if (nCoup) tags.push(`券卡×${nCoup}`);
			const appliedG = (parsed?.applied_guidelines as string[] | undefined) ?? [];
			for (const gid of appliedG) tags.push(`采纳:${gid}`);
			return {
				primary: { label: t ? `应答:${REPLY_TYPE_ZH[t] ?? t}` : "已生成", tone: "success" },
				tags,
				note: texts || (out.raw ? String(out.raw) : undefined),
			};
		}
		return { tags: parsed ? compactTags(parsed) : [] };
	}
	return { tags: compactTags(e.input ?? {}) }; // note
}

/* ---- 节点完整 I/O 详情(③ 抽屉右栏复用)---- */
function NodeDetail({ e }: { e: NodeEvent }) {
	const inp = e.input ?? {};
	const out = e.output ?? {};
	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center gap-2 text-xs text-text-secondary">
				<Pill tone={nodeBadge(e).tone}>{nodeBadge(e).label}</Pill>
				<span>{e.title}</span>
				<Pill tone={msTone(e.ms)}>耗时 {fmtMs(e.ms)}</Pill>
				{e.usage ? (
					<Pill tone="success">
						token {e.usage.total_tokens.toLocaleString("en-US")}(入 {e.usage.prompt_tokens} · 出{" "}
						{e.usage.completion_tokens})
					</Pill>
				) : null}
			</div>
			{e.kind === "llm" ? (
				<>
					<IOBlock label="① system(固定指令)" value={inp.prompt} />
					<IOBlock label="② user(注入变量 + 当前句,真实送给模型)" value={inp.trigger} />
					<IOBlock label="③ 本轮注入的变量值" value={inp.vars} />
					<IOBlock label="④ 模型原始响应" value={out.raw} />
					{out.parsed ? <IOBlock label="⑤ 解析结果" value={out.parsed} /> : null}
				</>
			) : e.kind === "tool" ? (
				<>
					{(() => {
						const b = String(((out.payload ?? {}) as Record<string, unknown>)._backend ?? "");
						if (!b) return null;
						return (
							<div className="flex items-center gap-2 text-xs">
								<span className="text-text-muted">调用来源</span>
								<Badge tone={b === "live" ? "success" : b === "recorded" ? "info" : "muted"}>
									{b === "live" ? "真实执行" : b === "recorded" ? "录制回放" : "罐头模拟"}
								</Badge>
							</div>
						);
					})()}
					<IOBlock label="入参 params" value={inp.params} />
					<IOBlock label="完整返回 payload" value={out.payload} />
				</>
			) : (
				<>
					<IOBlock label="输入" value={inp} />
					<IOBlock label="输出" value={out} />
				</>
			)}
		</div>
	);
}

/* ---- ② 环节过程:一行节点 = 徽章 + 标题 + 核心输出标签;点击选中看 ③ ---- */
function NodeStepRow({
	e,
	selected,
	onSelect,
}: { e: NodeEvent; selected: boolean; onSelect: () => void }) {
	const b = nodeBadge(e);
	const tv = nodeTagView(e);
	return (
		<button
			type="button"
			onClick={onSelect}
			className={`w-full rounded-lg border px-2.5 py-2 text-left text-sm transition-colors duration-150 ${
				selected
					? "border-brand-accent bg-brand-accent/5"
					: "border-surface-border bg-surface-card hover:bg-surface-subtle"
			}`}
		>
			<div className="flex flex-wrap items-center gap-1.5">
				<span className="w-4 shrink-0 font-mono text-xs text-text-muted">{e.seq}</span>
				<Pill tone={b.tone}>{b.label}</Pill>
				{tv.primary ? <Pill tone={tv.primary.tone}>{tv.primary.label}</Pill> : null}
				{tv.tags.map((t) => (
					<span
						key={t}
						className="rounded-full bg-surface-muted px-1.5 py-0.5 font-mono text-[11px] text-text-secondary"
					>
						{t}
					</span>
				))}
				<span className="ml-auto flex shrink-0 items-center gap-1.5">
					<Pill tone={msTone(e.ms)}>{fmtMs(e.ms)}</Pill>
					<span className="text-xs text-text-muted">{selected ? "▶" : ""}</span>
				</span>
			</div>
			{tv.note ? (
				<div className="mt-1 line-clamp-2 pl-6 text-xs text-text-muted">{tv.note}</div>
			) : null}
		</button>
	);
}

/* ====================== 统一执行还原抽屉:ExecutionDrawer ======================
   右侧三栏:① 聊天记录(全轮气泡,user 右/主 Agent 左) | ② 环节过程(节点+核心标签,点选)
   | ③ 详情(点环节才出,完整 I/O)。场景库/历史/试跑 三页共用此一处。 */
export type DrawerTurn = {
	msg: string;
	resume?: string;
	reply?: string;
	stop?: string | null;
	intent?: string | null;
	committed?: boolean;
	calls?: string[];
	terminal?: string | null;
	clarify?: string | null;
	node_trace?: NodeEvent[];
	trace?: TraceStep[];
	timing?: Timing;
	blocks?: OutputBlock[];
	expect?: Record<string, unknown>; // 该轮期望断言(场景脚本)
	pending?: boolean; // 试跑:本轮已发出、正在调模型(乐观 UI + loading)
};

/** 结构化输出渲染:逐块(商品卡/优惠券卡/图片;text 块由气泡正文展示,这里跳过)*/
function ReplyBlocks({ blocks }: { blocks?: OutputBlock[] }) {
	const cards = (blocks ?? []).filter((b) => b.kind !== "text");
	if (!cards.length) return null;
	return (
		<div className="mt-1.5 flex flex-wrap gap-2">
			{cards.map((c, i) => {
				if (c.kind === "product")
					return (
						<div
							key={`p-${c.productId}`}
							className="flex w-44 items-center gap-2 rounded-lg border border-surface-border bg-surface-card p-2"
						>
							<div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-surface-muted text-lg">
								🛍️
							</div>
							<div className="min-w-0">
								<div className="truncate text-sm font-medium text-text-primary" title={c.title}>
									{c.title}
								</div>
								<div className="text-xs text-text-muted">{c.productId}</div>
								<div className="text-sm font-semibold text-brand-danger">¥{c.price}</div>
							</div>
						</div>
					);
				if (c.kind === "coupon")
					return (
						<div
							key={`c-${c.code}`}
							className="flex w-44 items-center gap-2 rounded-lg border border-dashed border-brand-danger/40 bg-brand-danger/5 p-2"
						>
							<div className="whitespace-nowrap text-xl font-bold text-brand-danger">
								{c.type === "percent" ? `${c.amount}%off` : `¥${c.amount}`}
							</div>
							<div className="min-w-0">
								<div className="font-mono text-sm font-medium text-text-primary">{c.code}</div>
								<div className="truncate text-xs text-text-muted" title={c.desc}>
									{c.desc}
								</div>
							</div>
						</div>
					);
				// image(预留)
				return (
					// biome-ignore lint/suspicious/noArrayIndexKey: image 块无稳定 id
					<img key={`img-${i}`} src={c.url} alt="" className="h-20 w-20 rounded object-cover" />
				);
			})}
		</div>
	);
}

/** ③ 详情可看的对象:节点 / 背景客户 / 预期vs实际 */
type DrawerSel = { kind: "node"; seq: number } | { kind: "bg" } | { kind: "expect" } | null;

function fmtMs(ms: number): string {
	return ms >= 1000
		? `${(ms / 1000).toFixed(2)}s`
		: `${ms < 10 ? ms.toFixed(1) : Math.round(ms)}ms`;
}
function msTone(ms: number): Tone {
	return ms >= 1500 ? "danger" : ms >= 500 ? "warning" : "muted";
}

/** 详情抽屉统一面板眉:加粗标题 + 分隔线(三栏 / 评分卡各区共用,立层级、去数字)。 */
function PanelHead({
	title,
	hint,
	extra,
}: {
	title: string;
	hint?: ReactNode;
	extra?: ReactNode;
}) {
	return (
		<div className="flex flex-wrap items-center gap-1.5 border-b border-surface-border pb-1.5">
			<span className="text-xs font-bold text-text-primary">{title}</span>
			{hint ? <span className="text-[11px] text-text-muted">{hint}</span> : null}
			{extra}
		</div>
	);
}

function ChatColumn({
	turns,
	focus,
	onFocus,
	footer,
	emptyHint,
}: {
	turns: DrawerTurn[];
	focus: number;
	onFocus: (i: number) => void;
	footer?: ReactNode; // 试跑模式:底部输入框(只读抽屉不传)
	emptyHint?: ReactNode;
}) {
	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
				<PanelHead title="对话" hint="🧑 用户 · 🤖 客服 · 点轮次看执行" />
				{!turns.length && emptyHint ? (
					<div className="text-sm text-text-muted">{emptyHint}</div>
				) : null}
				{turns.map((t, i) => (
					<button
						key={`${t.msg}-${i}`}
						type="button"
						onClick={() => onFocus(i)}
						className={`space-y-2 rounded-lg border p-2.5 text-left transition-colors duration-150 ${
							i === focus
								? "border-brand-accent bg-brand-accent/5"
								: "border-surface-border hover:bg-surface-subtle/60"
						}`}
					>
						<div className="flex items-center gap-1.5">
							<span className="font-mono text-[11px] text-text-muted">第{i + 1}轮</span>
							{t.pending ? <Pill tone="info">运行中…</Pill> : null}
							{t.resume ? <Pill tone="muted">{RESUME_LABEL[t.resume] ?? t.resume}</Pill> : null}
							{t.stop ? (
								<Pill tone={t.stop === "aborted" ? "danger" : observeTone(t.stop)}>{t.stop}</Pill>
							) : null}
							{t.committed ? <Pill tone="success">已执行</Pill> : null}
						</div>
						{/* 用户=浅灰圆角块(Intercom P9);客服=无气泡文档流(拍板④) */}
						<div className="flex justify-start gap-1.5">
							<span className="mt-0.5 shrink-0 text-sm" aria-hidden>
								🧑
							</span>
							<div className="max-w-[85%] rounded-lg rounded-tl-sm bg-surface-muted px-2.5 py-1.5 text-sm text-text-primary">
								{t.msg}
							</div>
						</div>
						{t.pending ? (
							<div className="flex items-center gap-1.5 pl-6 text-sm text-text-secondary">
								<span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand-accent" />
								正在调模型…
							</div>
						) : t.reply ? (
							<div className="space-y-1 pl-6">
								<div className="text-[11px] text-text-muted">🤖 客服</div>
								<div className="whitespace-pre-wrap text-sm leading-6 text-text-primary">
									{t.reply}
								</div>
								<ReplyBlocks blocks={t.blocks} />
							</div>
						) : (
							<div className="pl-6 text-[11px] text-text-muted">
								(本轮无话术 · 离线机制验证 / 未开 N3)
							</div>
						)}
					</button>
				))}
			</div>
			{footer ? (
				<div className="border-t border-surface-border bg-surface-card p-2">{footer}</div>
			) : null}
		</div>
	);
}

/** 信息块 chip(背景/预期),点击 → ③ 看详情 */
function InfoChip({
	label,
	tone,
	active,
	onClick,
}: { label: string; tone: Tone; active: boolean; onClick: () => void }) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors duration-150 ${active ? "border-brand-accent bg-brand-accent/5 text-text-primary" : "border-surface-border text-text-secondary hover:bg-surface-subtle"}`}
		>
			<span
				className={`h-1.5 w-1.5 rounded-full ${tone === "info" ? "bg-brand-info" : "bg-text-muted"}`}
			/>
			{label}
		</button>
	);
}

function StepsColumn({
	turn,
	selected,
	onSelect,
	customerName,
}: {
	turn?: DrawerTurn;
	selected: DrawerSel;
	onSelect: (s: DrawerSel) => void;
	customerName?: string;
}) {
	const events = turn?.node_trace ?? [];
	const tm = turn?.timing;
	return (
		<div className="flex min-h-0 flex-col gap-2 overflow-y-auto p-3">
			<PanelHead
				title="环节"
				extra={turn?.intent ? <Pill tone="info">{intentZh(turn.intent)}</Pill> : null}
			/>
			{turn?.pending ? (
				<div className="flex items-center gap-2 rounded-lg bg-surface-subtle px-2.5 py-2 text-xs text-text-secondary">
					<span className="inline-block h-2 w-2 animate-pulse rounded-full bg-brand-accent" />
					环节执行中…(N1 意图 → N2 决策 → Skill → N3 应答,完成后逐环节展开)
				</div>
			) : null}
			{/* 信息块:背景 / 预期 → 点击右侧看详情 */}
			<div className="flex flex-wrap items-center gap-2">
				{customerName ? (
					<InfoChip
						label={`背景·客户 ${customerName}`}
						tone="muted"
						active={selected?.kind === "bg"}
						onClick={() => onSelect({ kind: "bg" })}
					/>
				) : null}
				{turn?.expect ? (
					<InfoChip
						label="预期结果"
						tone="info"
						active={selected?.kind === "expect"}
						onClick={() => onSelect({ kind: "expect" })}
					/>
				) : null}
			</div>
			{tm?.total_ms !== undefined ? (
				<div className="flex flex-wrap items-center gap-1.5 rounded-lg bg-surface-muted/40 px-2.5 py-1.5 text-xs text-text-secondary">
					<span className="font-medium text-text-primary">本轮耗时</span>
					<Pill tone={msTone(tm.total_ms)}>总 {fmtMs(tm.total_ms)}</Pill>
					<span>LLM {fmtMs(tm.llm_ms ?? 0)}</span>
					<span>· 工具 {fmtMs(tm.tool_ms ?? 0)}</span>
					<span>· 规则 {fmtMs(tm.rule_ms ?? 0)}</span>
					<span>· LLM 调用 {tm.llm_calls ?? 0} 次</span>
				</div>
			) : null}
			{tm?.tokens_total !== undefined ? (
				<div className="flex flex-wrap items-center gap-1.5 rounded-lg bg-surface-muted/40 px-2.5 py-1.5 text-xs text-text-secondary">
					<span className="font-medium text-text-primary">本轮 token</span>
					<Pill tone="success">合计 {(tm.tokens_total ?? 0).toLocaleString("en-US")}</Pill>
					<span>输入 {(tm.tokens_in ?? 0).toLocaleString("en-US")}</span>
					<span>· 输出 {(tm.tokens_out ?? 0).toLocaleString("en-US")}</span>
				</div>
			) : null}
			{events.length ? (
				events.map((e) => (
					<NodeStepRow
						key={e.seq}
						e={e}
						selected={selected?.kind === "node" && selected.seq === e.seq}
						onSelect={() => onSelect({ kind: "node", seq: e.seq })}
					/>
				))
			) : turn?.trace?.length ? (
				<TraceSteps trace={turn.trace} /> // 旧记录兜底
			) : (
				<div className="text-xs text-text-muted">该轮无执行轨迹(未跑或越界直收)。</div>
			)}
		</div>
	);
}

/** 客户背景画像(姓名/背景/邮箱/订单)*/
function CustomerCard({ customerId }: { customerId?: string }) {
	const cat = useCatalog();
	const seed = cat.data?.seed;
	const c = seed?.customers.find((x) => x.customer_id === customerId);
	const orders = (seed?.orders ?? []).filter((o) => o.customer_id === customerId);
	if (!c)
		return (
			<div className="text-sm text-text-muted">无客户背景(customer_id={customerId ?? "—"})。</div>
		);
	return (
		<div className="space-y-3">
			<div className="rounded-md border border-surface-border p-3 text-sm">
				<div className="flex flex-wrap items-center gap-2">
					<Pill tone="info">{c.customer_id}</Pill>
					<span className="font-medium text-text-primary">{c.name}</span>
					<span className="text-text-muted">{c.email}</span>
				</div>
				<div className="mt-1 text-text-secondary">背景:{c.bg}</div>
				<div className="text-text-muted">名下订单 {c.order_count} 单</div>
			</div>
			<div>
				<div className="mb-1 text-xs font-medium text-text-secondary">该客户订单</div>
				{orders.length ? (
					<div className="overflow-x-auto rounded-md border border-surface-border">
						<table className="w-full text-xs">
							<tbody>
								{orders.map((o) => (
									<tr key={o.orderId} className="border-b border-surface-border last:border-0">
										<td className="px-2 py-1 font-mono text-text-primary">
											{o.orderId.replace("gid://shopify/Order/", "#")}
										</td>
										<td className="px-2 py-1">
											<Pill tone={observeTone(o.fulfillment_status ?? "")}>
												{o.fulfillment_status}
											</Pill>
										</td>
										<td className="px-2 py-1 text-text-secondary">{o.items}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : (
					<div className="text-xs text-text-muted">该客户无订单(新客)。</div>
				)}
			</div>
		</div>
	);
}

function ExpRow({ label, exp, act }: { label: string; exp: unknown; act: unknown }) {
	if (exp === undefined) return null;
	const e = JSON.stringify(exp);
	const a = JSON.stringify(act ?? null);
	const ok = e === a;
	return (
		<tr className="border-b border-surface-border last:border-0">
			<td className="px-2 py-1 text-text-secondary">{label}</td>
			<td className="px-2 py-1 font-mono text-text-primary">{e}</td>
			<td className="px-2 py-1 font-mono text-text-secondary">{a}</td>
			<td className="px-2 py-1">
				{ok ? <Pill tone="success">✓</Pill> : <Pill tone="danger">✗</Pill>}
			</td>
		</tr>
	);
}

/** 预期 vs 实际(golden_skills / stop / clarify / terminal / committed)*/
function ExpectedVsActual({ turn }: { turn?: DrawerTurn }) {
	const exp = turn?.expect ?? {};
	const goldenCalls = (exp.calls as string[] | undefined) ?? undefined;
	return (
		<div className="space-y-3">
			<div className="overflow-hidden rounded-md border border-surface-border">
				<table className="w-full text-xs">
					<thead className="bg-surface-muted text-text-secondary">
						<tr>
							<th className="px-2 py-1 text-left font-medium">维度</th>
							<th className="px-2 py-1 text-left font-medium">预期</th>
							<th className="px-2 py-1 text-left font-medium">实际</th>
							<th className="px-2 py-1 text-left font-medium">符</th>
						</tr>
					</thead>
					<tbody>
						<ExpRow label="判停 stop" exp={exp.stop} act={turn?.stop} />
						<ExpRow label="反问 clarify" exp={exp.clarify} act={turn?.clarify} />
						<ExpRow label="收尾 terminal" exp={exp.terminal} act={turn?.terminal} />
						<ExpRow label="已执行 committed" exp={exp.committed} act={turn?.committed} />
						{goldenCalls ? (
							<ExpRow label="调用链 calls" exp={goldenCalls} act={turn?.calls} />
						) : null}
						<ExpRow label="轮数 rounds" exp={exp.rounds} act={turn?.trace?.length} />
					</tbody>
				</table>
			</div>
			<div className="text-xs text-text-muted">
				预期来自场景脚本(golden);真 LLM 输出会波动,calls 顺序仅参考,机制看 stop/terminal。
			</div>
		</div>
	);
}

function DetailColumn({
	selected,
	turn,
	customerId,
}: { selected: DrawerSel; turn?: DrawerTurn; customerId?: string }) {
	const node =
		selected?.kind === "node" ? turn?.node_trace?.find((e) => e.seq === selected.seq) : null;
	let title = "详情";
	let body: ReactNode;
	if (selected?.kind === "bg") {
		title = "背景 · 客户画像";
		body = <CustomerCard customerId={customerId} />;
	} else if (selected?.kind === "expect") {
		title = "预期 vs 实际";
		body = <ExpectedVsActual turn={turn} />;
	} else if (node) {
		title = "节点完整 I/O";
		body = <NodeDetail e={node} />;
	} else {
		body = (
			<div className="rounded-md border border-dashed border-surface-border p-6 text-center text-sm text-text-muted">
				点击中间的「环节」或顶部「背景 / 预期」信息块,这里显示对应详情。
			</div>
		);
	}
	return (
		<div className="min-h-0 space-y-2 overflow-y-auto p-3">
			<PanelHead title={title} />
			{body}
		</div>
	);
}

/** 三栏执行视图(① 聊天 ② 环节 ③ 详情)—— 抽屉(只读)与试跑(带输入 footer)共用一套 */
function ExecutionColumns({
	turns,
	customerId,
	chatFooter,
	chatEmptyHint,
	initialTurn = 0,
	resetKey = 0,
	focusLast = false,
	className,
}: {
	turns: DrawerTurn[];
	customerId?: string;
	chatFooter?: ReactNode; // 试跑:底部输入框
	chatEmptyHint?: ReactNode;
	initialTurn?: number;
	resetKey?: number; // 变化即重置选中轮(抽屉用 open 切换)
	focusLast?: boolean; // 试跑:新轮到达自动聚焦最新
	className?: string;
}) {
	const cat = useCatalog();
	const customerName = cat.data?.seed.customers.find((c) => c.customer_id === customerId)?.name;
	const [turnIdx, setTurnIdx] = useState(initialTurn);
	const [sel, setSel] = useState<DrawerSel>(null);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 仅在 resetKey/initialTurn 变化时重置选中
	useEffect(() => {
		setTurnIdx(initialTurn);
		setSel(null);
	}, [initialTurn, resetKey]);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 新轮到达时聚焦最新一轮
	useEffect(() => {
		if (focusLast && turns.length) {
			setTurnIdx(turns.length - 1);
			setSel(null);
		}
	}, [turns.length]);
	const idx = Math.min(turnIdx, Math.max(0, turns.length - 1));
	const turn = turns[idx];
	return (
		<div
			className={`grid min-h-0 grid-rows-1 grid-cols-[300px_minmax(0,1.1fr)_minmax(0,1fr)] divide-x divide-surface-border overflow-hidden ${className ?? ""}`}
		>
			<ChatColumn
				turns={turns}
				focus={idx}
				onFocus={(i) => {
					setTurnIdx(i);
					setSel(null);
				}}
				footer={chatFooter}
				emptyHint={chatEmptyHint}
			/>
			<StepsColumn turn={turn} selected={sel} onSelect={setSel} customerName={customerName} />
			<DetailColumn selected={sel} turn={turn} customerId={customerId} />
		</div>
	);
}

function ExecutionDrawer({
	open,
	onClose,
	title,
	badges,
	turns,
	initialTurn = 0,
	customerId,
}: {
	open: boolean;
	onClose: () => void;
	title: string;
	badges?: ReactNode;
	turns: DrawerTurn[];
	initialTurn?: number;
	customerId?: string;
}) {
	return (
		<DrawerPrimitive.Root open={open} onOpenChange={(o) => !o && onClose()}>
			<DrawerPrimitive.Portal>
				<DrawerPrimitive.Overlay className="ui-overlay fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
				<DrawerPrimitive.Content className="ui-drawer fixed right-0 top-0 z-50 flex h-full w-[min(1240px,95vw)] flex-col border-l border-surface-border bg-surface-background shadow-xl focus:outline-none">
					<header className="flex items-center gap-3 border-b border-surface-border px-4 py-3">
						<DrawerPrimitive.Title className="text-sm font-semibold text-text-primary">
							{title}
						</DrawerPrimitive.Title>
						<div className="flex flex-wrap items-center gap-1.5">{badges}</div>
						<DrawerPrimitive.Close
							className="ml-auto rounded-md p-1 text-text-muted hover:bg-surface-muted"
							aria-label="关闭"
						>
							<X className="h-4 w-4" />
						</DrawerPrimitive.Close>
					</header>
					<ExecutionColumns
						turns={turns}
						customerId={customerId}
						initialTurn={initialTurn}
						resetKey={open ? 1 : 0}
						className="flex-1"
					/>
				</DrawerPrimitive.Content>
			</DrawerPrimitive.Portal>
		</DrawerPrimitive.Root>
	);
}

/* ====================== Tab 0:链路总览(C26 v2 · 主干竖向大卡,层级分明;说明同源 /loop-map) ====================== */
const MAP_KIND_DOT: Record<string, string> = {
	llm: "bg-brand-info",
	rule: "bg-text-muted",
	tool: "bg-brand-success",
	flow: "bg-brand-primary",
};
const MAP_KIND_EDGE: Record<string, string> = {
	llm: "border-l-brand-info",
	rule: "border-l-surface-border",
	tool: "border-l-brand-success",
	flow: "border-l-brand-primary",
};
const MAP_KIND_LABEL: Record<string, string> = {
	llm: "LLM 节点",
	rule: "确定性规则",
	tool: "工具执行",
	flow: "输入 / 输出",
};
const MAP_SAFETY = new Set(["G5", "G8"]); // 写安全红线:红点

/** 主干大卡:序号 + kind 左条 + 名称 + 一句定义(不再是光秃标签) */
function MapStageCard({
	node,
	step,
	selected,
	onSelect,
	children,
}: {
	node: LoopMapNode;
	step?: string;
	selected: boolean;
	onSelect: (id: string) => void;
	children?: ReactNode;
}) {
	const dot = MAP_SAFETY.has(node.id)
		? "bg-brand-danger"
		: (MAP_KIND_DOT[node.kind] ?? "bg-text-muted");
	return (
		<div
			className={`w-full rounded-xl border border-l-4 bg-surface-card transition-colors duration-150 ${
				MAP_KIND_EDGE[node.kind] ?? "border-l-surface-border"
			} ${selected ? "border-brand-accent shadow-sm ring-1 ring-brand-accent/40" : "border-surface-border"}`}
		>
			<button
				type="button"
				data-testid={`loop-map-node-${node.id}`}
				onClick={() => onSelect(node.id)}
				className="flex w-full items-start gap-2.5 rounded-xl px-3.5 py-2.5 text-left hover:bg-surface-subtle/50"
			>
				{step ? (
					<span className="mt-0.5 shrink-0 font-mono text-xs font-semibold text-text-muted">
						{step}
					</span>
				) : null}
				<span className="min-w-0">
					<span className="flex items-center gap-1.5 text-sm font-semibold text-text-primary">
						<span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
						{node.name}
					</span>
					<span className="mt-0.5 block truncate text-xs text-text-muted">{node.definition}</span>
				</span>
			</button>
			{children ? <div className="px-3.5 pb-2.5 pl-10">{children}</div> : null}
		</div>
	);
}

/** 次级小 chip(护栏/解析/透出):前缀灰字 + 一排圆角小件 */
function MapChipRow({
	label,
	ids,
	nodes,
	sel,
	onSelect,
}: {
	label: string;
	ids: string[];
	nodes: Record<string, LoopMapNode>;
	sel: string;
	onSelect: (id: string) => void;
}) {
	const items = ids.map((id) => nodes[id]).filter((x): x is LoopMapNode => !!x);
	if (!items.length) return null;
	return (
		<div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs">
			<span className="shrink-0 text-text-muted">{label}</span>
			{items.map((x) => (
				<button
					key={x.id}
					type="button"
					data-testid={`loop-map-node-${x.id}`}
					onClick={() => onSelect(x.id)}
					className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 transition-colors duration-150 ${
						sel === x.id
							? "border-brand-accent bg-brand-accent/5 text-text-primary"
							: "border-surface-border text-text-secondary hover:bg-surface-subtle"
					}`}
				>
					<span
						className={`h-1 w-1 rounded-full ${MAP_SAFETY.has(x.id) ? "bg-brand-danger" : (MAP_KIND_DOT[x.kind] ?? "bg-text-muted")}`}
					/>
					{x.name}
				</button>
			))}
		</div>
	);
}

function MapDownArrow() {
	return (
		<div className="flex justify-center py-0.5 text-text-muted" aria-hidden>
			↓
		</div>
	);
}

/** loop 主链路的五个一级环节 · 应答只是其中一环。每段除标题外露出「实质」(core),chip 走 JumpChip(hover 出说明 + 前往,点才跳)。 */
type StageChip = { label: string; note: string; hash: string };
type ChainStage = {
	step: string;
	key: string;
	name: string;
	desc: string; // 只说这段"干什么/产出什么",不复述核心块里的子结构
	tint: string; // 序号徽标底色+字色
	accent: string; // 卡顶部色条:给每段一个颜色身份,扫视分段
	chips: StageChip[]; // 说明性标签:hover 出一句话+前往,点击才导航
	sourcesAsCore?: boolean; // 「提问」:三来源即核心块(可点入口),不再另设底部 chip 行
	expandable?: boolean; // 仅「应答」:可展开内部机理详图
};
const MAIN_CHAIN: ChainStage[] = [
	{
		step: "①",
		key: "ask",
		name: "提问",
		desc: "一个问题的三处来源 —— 挑一处开始。",
		tint: "bg-brand-info/10 text-brand-info",
		accent: "border-t-brand-info",
		sourcesAsCore: true,
		chips: [
			{
				label: "固定用例",
				note: "沉淀下来的固定回归用例集,每轮必跑、保不回退。",
				hash: "scenarios",
			},
			{
				label: "模拟场景库",
				note: "机器顾客可扮演的场景库,批量造多轮压力。",
				hash: "eval-scenarios",
			},
			{ label: "人工提问", note: "手动输入一句话即时试,调试单条链路。", hash: "playground" },
		],
	},
	{
		step: "②",
		key: "respond",
		name: "应答",
		desc: "把用户一句话变成一次回复。",
		tint: "bg-brand-primary/10 text-brand-primary",
		accent: "border-t-brand-primary",
		chips: [
			{
				label: "应答提示词",
				note: "N1/N2/N3 各节点的系统提示词,优化的主要落点。",
				hash: "prompts",
			},
		],
		expandable: true,
	},
	{
		step: "③",
		key: "eval",
		name: "评测",
		desc: "机器顾客跑多轮,判出红 / 绿。",
		tint: "bg-brand-success/10 text-brand-success",
		accent: "border-t-brand-success",
		chips: [
			{ label: "发起评测", note: "选场景 × 机器顾客跑多轮,产出一次评测。", hash: "eval" },
			{ label: "评测记录", note: "历史评测结果与逐维得分明细。", hash: "eval-records" },
			{ label: "评测规则", note: "判分维度与 rubric 标准的定义。", hash: "eval-rules" },
		],
	},
	{
		step: "④",
		key: "rca",
		name: "归因",
		desc: "对失败场景定位根因。",
		tint: "bg-brand-warning/10 text-brand-warning",
		accent: "border-t-brand-warning",
		chips: [
			{ label: "归因记录", note: "每个失败场景的根因判定与证据。", hash: "rca-records" },
			{ label: "归因标准", note: "9 类根因 / 5 簇的判定口径。", hash: "rca-standard" },
		],
	},
	{
		step: "⑤",
		key: "optimize",
		name: "优化",
		desc: "把归因结论落成改动,回灌下一轮。",
		tint: "bg-brand-accent/10 text-brand-accent",
		accent: "border-t-brand-accent",
		chips: [],
	},
];

/** 说明性标签:默认只是标签,hover 出一句话说明 + 「前往 →」,点击才导航(不再"点了就飞走不知去哪")。 */
function JumpChip({
	label,
	note,
	hash,
	onJump,
}: {
	label: string;
	note: string;
	hash: string;
	onJump: (hash: string) => void;
}) {
	return (
		<span className="group relative inline-flex">
			<button
				type="button"
				onClick={() => onJump(hash)}
				aria-label={`${label} —— ${note}(点击前往)`}
				className="inline-flex items-center rounded-full border border-surface-border px-2 py-0.5 text-xs text-text-secondary transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-accent group-hover:border-brand-accent group-hover:bg-brand-accent/5 group-hover:text-text-primary"
			>
				{label}
			</button>
			{/* 浮层:绝对定位不占布局(改宽度会引发换行抖动),hidden↔block 无动画不闪;hover 与键盘聚焦都可见 */}
			<span
				role="tooltip"
				className="pointer-events-none absolute left-1/2 top-full z-30 mt-1 hidden w-max max-w-[180px] -translate-x-1/2 rounded-md border border-surface-border bg-surface-card px-2 py-1 text-[11px] leading-4 shadow-md group-hover:block group-focus-within:block"
			>
				<span className="text-text-secondary">{note}</span>
				<span className="mt-0.5 block font-medium text-brand-accent">点击前往 →</span>
			</span>
		</span>
	);
}

/** 提问的三处来源:既是「实质」也是入口。cell 宽度恒定、hover/聚焦浮出说明+前往(与 JumpChip 同交互纪律,不抖)。 */
function SourceCell({ label, note, hash, onJump }: StageChip & { onJump: (hash: string) => void }) {
	return (
		<span className="group/cell relative inline-flex">
			<button
				type="button"
				onClick={() => onJump(hash)}
				aria-label={`${label} —— ${note}(点击前往)`}
				className="w-full rounded-md border border-brand-info/30 bg-brand-info/5 px-1 py-1.5 text-center text-[11px] font-medium leading-tight text-brand-info transition-colors duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-accent group-hover/cell:border-brand-accent group-hover/cell:bg-brand-accent/5 group-hover/cell:text-brand-accent"
			>
				{label}
			</button>
			<span
				role="tooltip"
				className="pointer-events-none absolute left-1/2 top-full z-30 mt-1 hidden w-max max-w-[160px] -translate-x-1/2 rounded-md border border-surface-border bg-surface-card px-2 py-1 text-left text-[11px] font-normal leading-4 shadow-md group-hover/cell:block group-focus-within/cell:block"
			>
				<span className="text-text-secondary">{note}</span>
				<span className="mt-0.5 block font-medium text-brand-accent">点击前往 →</span>
			</span>
		</span>
	);
}

/** 应答内部:意图 → 决策循环 ↻ → 生成 三段(N1/N2/N3 大模块包进一级卡)。三等分单元格,与评测三柱同构;循环用内联小 ↻ 表达,不换行不拥挤。 */
function StageMiniFlow({ steps }: { steps: Array<{ label: string; loop?: boolean }> }) {
	return (
		<div className="grid grid-cols-3 gap-1">
			{steps.map((s) => (
				<div
					key={s.label}
					className="rounded-md border border-brand-primary/30 bg-brand-primary/5 px-1 py-1 text-center text-[11px] font-medium leading-tight text-brand-primary"
				>
					<span className="inline-flex items-center gap-0.5">
						{s.label}
						{s.loop ? (
							<span className="text-brand-primary/60" aria-label="多轮往返" title="多轮往返">
								↻
							</span>
						) : null}
					</span>
				</div>
			))}
		</div>
	);
}

/** 评测能力核心三柱:机器顾客 × 多维判分 × pass^k(不再只是罗列菜单)。 */
function StagePillars({ items }: { items: Array<{ t: string; s: string }> }) {
	return (
		<div className="grid grid-cols-3 gap-1">
			{items.map((p) => (
				<div
					key={p.t}
					className="rounded-md border border-brand-success/30 bg-brand-success/5 px-1.5 py-1 text-center"
				>
					<div className="text-[11px] font-semibold leading-tight text-brand-success">{p.t}</div>
					<div className="mt-0.5 text-[10px] leading-tight text-text-muted">{p.s}</div>
				</div>
			))}
		</div>
	);
}

/** 每段的「实质」块 —— 提问/应答/评测/归因统一为三格核心(视觉成体系),优化为 skill 终端。desc 只说干什么,子结构交给这里。 */
function StageCore({ s, onJump }: { s: ChainStage; onJump: (hash: string) => void }) {
	const k = s.key;
	if (k === "ask")
		return (
			<div className="grid grid-cols-3 gap-1">
				{s.chips.map((c) => (
					<SourceCell key={c.hash} label={c.label} note={c.note} hash={c.hash} onJump={onJump} />
				))}
			</div>
		);
	if (k === "respond")
		return (
			<StageMiniFlow
				steps={[{ label: "意图" }, { label: "决策循环", loop: true }, { label: "生成" }]}
			/>
		);
	if (k === "eval")
		return (
			<StagePillars
				items={[
					{ t: "机器顾客", s: "多轮扮演" },
					{ t: "多维判分", s: "逐维打分" },
					{ t: "pass^k", s: "多跑取一致" },
				]}
			/>
		);
	if (k === "rca")
		return (
			<div className="grid grid-cols-3 gap-1">
				{[
					{ t: "9 类根因", s: "定位类别" },
					{ t: "归并 5 簇", s: "聚成簇" },
					{ t: "主因", s: "一句话" },
				].map((p) => (
					<div
						key={p.t}
						className="rounded-md border border-brand-warning/30 bg-brand-warning/5 px-1.5 py-1 text-center"
					>
						<div className="text-[11px] font-semibold leading-tight text-brand-warning">{p.t}</div>
						<div className="mt-0.5 text-[10px] leading-tight text-text-muted">{p.s}</div>
					</div>
				))}
			</div>
		);
	if (k === "optimize")
		return (
			<div className="space-y-1.5">
				<div className="flex items-center gap-1.5 rounded-md border border-brand-accent/40 bg-brand-accent/5 px-2 py-1 text-[11px] font-medium text-brand-accent">
					<span aria-hidden>⌘</span> 由 Claude Code skill 驱动执行
				</div>
				<div className="flex flex-wrap gap-1 text-[11px] text-text-muted">
					{["改提示词", "换节点模型", "补数据 / 用例"].map((t) => (
						<span key={t} className="rounded-md border border-surface-border px-1.5 py-0.5">
							{t}
						</span>
					))}
				</div>
			</div>
		);
	return null;
}

/** 单段一级卡:序号徽标 + 名称 + 一句话 + 实质块 + 说明性 chip(应答另带内部机理开关)。 */
function StageCard({
	s,
	respondOpen,
	onToggleRespond,
	onJump,
}: {
	s: ChainStage;
	respondOpen: boolean;
	onToggleRespond: () => void;
	onJump: (hash: string) => void;
}) {
	const showChipRow = !s.sourcesAsCore && (s.chips.length > 0 || s.expandable);
	return (
		<div
			data-testid={`main-chain-${s.key}`}
			className={`flex h-full flex-col rounded-lg border border-t-2 border-surface-border bg-surface-card p-3 ${s.accent}`}
		>
			<div className="flex items-center gap-2">
				<span
					className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${s.tint}`}
				>
					{s.step}
				</span>
				<span className="text-sm font-semibold text-text-primary">{s.name}</span>
			</div>
			<p className="mt-1.5 text-xs leading-5 text-text-muted">{s.desc}</p>
			<div className="mt-2 grow">
				<StageCore s={s} onJump={onJump} />
			</div>
			{showChipRow ? (
				<div className="mt-2.5 flex flex-wrap gap-1.5 border-t border-surface-border/60 pt-2.5">
					{s.chips.map((c) => (
						<JumpChip
							key={c.hash + c.label}
							label={c.label}
							note={c.note}
							hash={c.hash}
							onJump={onJump}
						/>
					))}
					{s.expandable ? (
						<button
							type="button"
							data-testid="main-chain-respond-toggle"
							onClick={onToggleRespond}
							className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors duration-150 ${
								respondOpen
									? "border-brand-accent bg-brand-accent/5 text-text-primary"
									: "border-surface-border text-text-secondary hover:bg-surface-subtle"
							}`}
						>
							内部机理 {respondOpen ? "▴" : "▾"}
						</button>
					) : null}
				</div>
			) : null}
			{/* ⑤ 是闭环出口:底部一条回灌提示,既补齐与其他卡的底部节奏,又接上下方回灌弧 */}
			{s.key === "optimize" ? (
				<div className="mt-2.5 flex items-center gap-1 border-t border-surface-border/60 pt-2.5 text-[11px] text-brand-accent">
					<span aria-hidden>↺</span> 回灌 ① 提问 / ② 应答
				</div>
			) : null}
		</div>
	);
}

/** 段间连接件:横向=实线 + ▶ 箭头,竖向(窄屏)=竖线 + ▼。把五段串成一条线,不再是孤立箭头。 */
function StageConnector() {
	return (
		<div className="flex shrink-0 items-center justify-center xl:w-9" aria-hidden>
			<div className="hidden w-full items-center xl:flex">
				<span className="h-px flex-1 bg-surface-border" />
				<span className="-ml-1 text-[10px] leading-none text-text-muted">▶</span>
			</div>
			<div className="flex flex-col items-center xl:hidden">
				<span className="h-4 w-px bg-surface-border" />
				<span className="-mt-1 text-[10px] leading-none text-text-muted">▼</span>
			</div>
		</div>
	);
}

/** 主链路总板:五段一级环节串在一条轨道上,末段一条回灌弧绕回起点 → 闭环。应答段可展开下钻到内部机理详图。 */
function MainChainBoard({
	respondOpen,
	onToggleRespond,
	onJump,
}: {
	respondOpen: boolean;
	onToggleRespond: () => void;
	onJump: (hash: string) => void;
}) {
	const items: ReactNode[] = [];
	MAIN_CHAIN.forEach((s, i) => {
		if (i > 0) items.push(<StageConnector key={`conn-${s.key}`} />);
		items.push(
			<div key={s.key} className="min-w-0 xl:flex-1">
				<StageCard
					s={s}
					respondOpen={respondOpen}
					onToggleRespond={onToggleRespond}
					onJump={onJump}
				/>
			</div>,
		);
	});
	return (
		<Card>
			<CardHeader>
				<CardTitle>loop 主链路 · 五段闭环(应答只是其中一环)</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="flex flex-col gap-2 xl:flex-row xl:items-stretch xl:gap-0">{items}</div>
				{/* 回灌闭环:横向下方一条 ∪ 弧从 ⑤ 绕回 ①(窄屏省略,仅留文字) */}
				<div className="mt-3">
					<div className="relative mx-[8%] hidden h-6 xl:block" aria-hidden>
						<div className="absolute inset-x-0 top-0 h-full rounded-b-2xl border-b border-l border-r border-dashed border-brand-accent/50" />
						<span className="absolute -left-[7px] top-0 text-[10px] leading-none text-brand-accent">
							◀
						</span>
					</div>
					<div className="mt-1 flex items-center justify-center gap-1.5 text-[11px] text-brand-accent">
						<span aria-hidden>↺</span>
						<span>优化回灌 → 下一轮重跑 · 闭环收敛才是 loop 运营的目的</span>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function LoopOverviewTab() {
	const q = useLoopMap();
	const [sel, setSel] = useState<string>("N1");
	const [respondOpen, setRespondOpen] = useState(false);
	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline text="加载链路图…" />
				</CardContent>
			</Card>
		);
	const d = q.data;
	if (!d || !d.nodes || !d.graph) return null;
	const nodes = d.nodes;
	const n = (id: string): LoopMapNode | undefined => nodes[id];
	const node = nodes[sel] ?? nodes.N1;
	if (!node) return null;
	const jump = (hash: string) => {
		window.location.hash = hash;
	};
	const stage = (id: string, step?: string, children?: ReactNode) => {
		const x = n(id);
		return x ? (
			<MapStageCard node={x} step={step} selected={sel === x.id} onSelect={setSel}>
				{children}
			</MapStageCard>
		) : null;
	};
	return (
		<div className="space-y-4">
			<MainChainBoard
				respondOpen={respondOpen}
				onToggleRespond={() => setRespondOpen((v) => !v)}
				onJump={jump}
			/>
			{respondOpen ? (
				<div className="flex flex-col items-start gap-4 xl:flex-row">
					<Card className="min-w-0 flex-1">
						<CardHeader>
							<CardTitle>② 应答 · 内部机理(每条用户消息走一遍 · 点节点看定义与实际功能)</CardTitle>
						</CardHeader>
						<CardContent>
							<div className="mx-auto max-w-2xl">
								{/* 跨轮 session 薄条 */}
								<div className="mb-3 rounded-lg bg-surface-subtle/60 px-3 py-2">
									<MapChipRow
										label="跨轮 session(挂起 / 恢复 / 承载):"
										ids={d.graph.session_lane}
										nodes={nodes}
										sel={sel}
										onSelect={setSel}
									/>
								</div>
								{stage("USER", "①")}
								<MapDownArrow />
								{stage(
									"N1",
									"②",
									<>
										<MapChipRow
											label="兜底:"
											ids={d.graph.n1_fallback}
											nodes={nodes}
											sel={sel}
											onSelect={setSel}
										/>
										<MapChipRow
											label="意图落定:"
											ids={d.graph.n1_outputs}
											nodes={nodes}
											sel={sel}
											onSelect={setSel}
										/>
									</>,
								)}
								<MapDownArrow />
								{/* 决策循环容器 */}
								<div className="rounded-2xl border-2 border-dashed border-surface-border bg-surface-subtle/30 p-3">
									<div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold text-text-secondary">
										<span>↺ N2 决策循环</span>
										<MapChipRow
											label=""
											ids={[d.graph.loop.budget]}
											nodes={nodes}
											sel={sel}
											onSelect={setSel}
										/>
									</div>
									{stage(
										"N2",
										"③",
										<>
											<MapChipRow
												label="输入面:"
												ids={d.graph.input_lane}
												nodes={nodes}
												sel={sel}
												onSelect={setSel}
											/>
											<MapChipRow
												label="决策护栏:"
												ids={d.graph.loop.n2_guards}
												nodes={nodes}
												sel={sel}
												onSelect={setSel}
											/>
											<MapChipRow
												label="排除改道:"
												ids={["G6"]}
												nodes={nodes}
												sel={sel}
												onSelect={setSel}
											/>
										</>,
									)}
									<MapDownArrow />
									{stage(
										"B2.5",
										"④",
										<MapChipRow
											label="解析:"
											ids={d.graph.loop.b25_resolve}
											nodes={nodes}
											sel={sel}
											onSelect={setSel}
										/>,
									)}
									<MapDownArrow />
									{stage(
										"SKILL",
										"⑤",
										<MapChipRow
											label="写前闸:"
											ids={["G14", "G22", "G8", "G1", ...d.graph.loop.skill_gates]}
											nodes={nodes}
											sel={sel}
											onSelect={setSel}
										/>,
									)}
									<MapDownArrow />
									{stage(
										"G4",
										"⑥",
										<MapChipRow
											label="分流:"
											ids={[...d.graph.loop.stop_outs, "G12"]}
											nodes={nodes}
											sel={sel}
											onSelect={setSel}
										/>,
									)}
									<div className="pt-1.5 text-center text-[11px] text-text-muted">
										未判停 → 回到 ③ 继续决策(耗尽轮预算强制收尾)
									</div>
								</div>
								<MapDownArrow />
								{stage(
									"N3",
									"⑦",
									<MapChipRow
										label="输出侧:"
										ids={["O1"]}
										nodes={nodes}
										sel={sel}
										onSelect={setSel}
									/>,
								)}
								<MapDownArrow />
								{stage("REPLY", "⑧")}
								{/* 图例 */}
								<div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-3 text-xs text-text-muted">
									{Object.entries(MAP_KIND_LABEL).map(([k, label]) => (
										<span key={k} className="flex items-center gap-1.5">
											<span className={`h-1.5 w-1.5 rounded-full ${MAP_KIND_DOT[k]}`} />
											{label}
										</span>
									))}
									<span className="flex items-center gap-1.5">
										<span className="h-1.5 w-1.5 rounded-full bg-brand-danger" />
										写安全红线
									</span>
								</div>
							</div>
						</CardContent>
					</Card>
					{/* 详情栏:桌面右侧固定,窄屏落到下方 */}
					<Card
						className="w-full shrink-0 xl:sticky xl:top-4 xl:w-[360px]"
						data-testid="loop-map-detail"
					>
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<span
									className={`h-2 w-2 rounded-full ${MAP_SAFETY.has(node.id) ? "bg-brand-danger" : (MAP_KIND_DOT[node.kind] ?? "bg-text-muted")}`}
								/>
								{node.name}
								<Pill tone="muted">{MAP_KIND_LABEL[node.kind] ?? node.kind}</Pill>
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3 text-sm">
							<div>
								<div className="text-xs font-semibold text-text-muted">定义(干什么)</div>
								<p className="leading-6 text-text-primary">{node.definition}</p>
							</div>
							<div>
								<div className="text-xs font-semibold text-text-muted">实际功能(以代码为准)</div>
								<p className="leading-6 text-text-secondary">{node.actual}</p>
							</div>
							{node.stage ? (
								<div>
									<div className="text-xs font-semibold text-text-muted">触发</div>
									<p className="text-xs leading-5 text-text-secondary">
										{node.stage} · 条件:{node.when}
									</p>
								</div>
							) : null}
							{node.output_to ? (
								<div>
									<div className="text-xs font-semibold text-text-muted">输出承接</div>
									<p className="text-xs leading-5 text-text-secondary">{node.output_to}</p>
								</div>
							) : null}
							<div>
								<div className="text-xs font-semibold text-text-muted">实现位置</div>
								<code className="break-all font-mono text-xs text-text-secondary">
									{node.where}
								</code>
								{node.refs ? (
									<div className="mt-0.5 text-xs text-text-muted">出处:{node.refs}</div>
								) : null}
							</div>
							<div className="flex flex-wrap gap-2 pt-1">
								{node.kind === "rule" ? (
									<Button
										type="button"
										size="sm"
										variant="outline"
										onClick={() => jump("reference-rules")}
									>
										在规则页查看
									</Button>
								) : null}
								{["N1", "N2", "N3"].includes(node.id) ? (
									<Button type="button" size="sm" variant="outline" onClick={() => jump("prompts")}>
										查看提示词
									</Button>
								) : null}
								{node.id === "SKILL" ? (
									<Button
										type="button"
										size="sm"
										variant="outline"
										onClick={() => jump("reference-skill")}
									>
										查看 SKILL 清单
									</Button>
								) : null}
							</div>
						</CardContent>
					</Card>
				</div>
			) : null}
		</div>
	);
}

/* ====================== Tab 1:参考 ====================== */
/** 目录数据加载守卫:三个拆分 tab(意图 / SKILL / 评测数据)共用 */
function useCatalogGuard(): { data: CatalogResponse } | { guard: ReactNode } {
	const cat = useCatalog();
	if (cat.isLoading)
		return {
			guard: (
				<Card>
					<CardContent className="py-4">
						<LoadingInline />
					</CardContent>
				</Card>
			),
		};
	if (!cat.data)
		return {
			guard: (
				<Card>
					<CardContent className="py-6 text-sm text-brand-danger">目录加载失败</CardContent>
				</Card>
			),
		};
	return { data: cat.data };
}

/** 应答配置 · 意图(15 类识别清单) */
function IntentRefTab() {
	const g = useCatalogGuard();
	if ("guard" in g) return g.guard;
	return <IntentTable data={g.data} />;
}

/* ====================== 应答配置 · 应答指南(P2 PR-3 · 详设 §8) ====================== */

const GUIDANCE_LAYER_LABEL: Record<string, string> = {
	platform: "平台",
	merchant: "商家",
	default: "默认",
};
const GUIDANCE_CRIT_TONE: Record<string, BadgeTone> = {
	must: "danger",
	should: "info",
	prefer: "muted",
};
const GUIDANCE_NODE_LABEL: Record<string, string> = {
	talk: "话术",
	decide: "决策",
	both: "话术+决策",
};
const GUIDANCE_STATUS_META: Record<string, { label: string; tone: BadgeTone }> = {
	live: { label: "生效中", tone: "success" },
	paused: { label: "已暂停", tone: "warning" },
	draft: { label: "草稿", tone: "muted" },
};

/** axios 400 的中文校验原因(guidance.upsert 写入口硬校验),兜底通用 message */
function guidanceErrDetail(e: unknown): string {
	const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
	return detail || (e instanceof Error ? e.message : "未知错误");
}

const EMPTY_GUIDANCE: GuidanceEntry = {
	id: "",
	title: "",
	layer: "merchant",
	scope: { intents: [], stage: "" },
	condition: "",
	action: "",
	criticality: "should",
	target_node: "talk",
	rationale: "",
	status: "draft",
};

/* ====================== 数据分析 · 指南效果(07 刀D M1) ====================== */

function FunnelBar({
	f,
}: { f: { hit: number; applied: number; followed: number; violated: number } }) {
	// 两段呈现(F0 质疑修正):注入面(命中→采纳自报)与核验面(遵循/违背)是两套
	// 口径——禁止/氛围型指南「没违背即遵循」,采纳自报天然稀疏,followed>applied 正常,
	// 不能画成一条漏斗暗示层层收窄
	const seg = (v: number, max: number, cls: string, label: string, tip: string) => (
		<div className="flex items-center gap-1.5 text-xs" title={tip}>
			<span className="w-16 text-text-muted">{label}</span>
			<div className="h-2 flex-1 rounded-full bg-surface-muted">
				<div
					className={`h-2 rounded-full ${cls}`}
					style={{ width: `${(v / Math.max(max, 1)) * 100}%` }}
				/>
			</div>
			<span className="w-6 text-right font-mono">{v}</span>
		</div>
	);
	const checked = f.followed + f.violated;
	return (
		<div className="w-64 space-y-0.5">
			<div className="text-[10px] text-text-muted">注入面</div>
			{seg(f.hit, f.hit, "bg-brand-primary/50", "命中", "O2 召回注入次数")}
			{seg(
				f.applied,
				f.hit,
				"bg-brand-primary",
				"采纳自报",
				"N3 自报「本次回复受其影响」;禁止/氛围型天然稀疏",
			)}
			<div className="pt-0.5 text-[10px] text-text-muted">核验面(判官,含 auto 兜底;与自报独立)</div>
			{seg(
				f.followed,
				checked,
				"bg-brand-success",
				"遵循",
				"断言核验通过——禁止类没违背即遵循,不以自报为前提",
			)}
			{seg(f.violated, checked, "bg-brand-danger", "违背", "断言核验未过")}
		</div>
	);
}

function GuidanceStatsTab() {
	const [days, setDays] = useState(7);
	const q = useGuidanceStats(days);
	const d = q.data;
	if (q.isLoading) {
		return (
			<Card>
				<CardContent className="py-6 text-sm text-text-muted">加载指南效果…</CardContent>
			</Card>
		);
	}
	if (!d) return null;
	const pctOr = (v: number | null) => (v == null ? "—" : `${Math.round(v * 100)}%`);
	return (
		<div className="space-y-4">
			<Card>
				<CardContent className="flex flex-wrap items-center gap-4 py-3">
					<Select
						label="窗口"
						value={String(days)}
						onChange={(v) => setDays(Number(v))}
						options={[
							{ value: "7", label: "近 7 天" },
							{ value: "30", label: "近 30 天" },
						]}
					/>
					<span className="text-xs text-text-muted">批次 {d.runs_used}(对照批不计)</span>
					<span className="ml-auto flex gap-4 text-sm">
						<span title="有指南命中的会话占比">
							注入覆盖率 <b className="text-text-primary">{pctOr(d.kpi.coverage)}</b>
						</span>
						<span title="采纳/命中">
							采纳率 <b className="text-text-primary">{pctOr(d.kpi.adoption)}</b>
						</span>
						<span title="断言遵循占比(含自动核验)">
							遵循率 <b className="text-text-primary">{pctOr(d.kpi.compliance)}</b>
						</span>
					</span>
				</CardContent>
			</Card>
			{d.recall_misses.length ? (
				<Card>
					<CardContent className="py-3 text-xs text-brand-warning">
						⚠ 召回期望未兑现 {d.recall_misses.length} 例(scope 配置与场景意图脱节的信号):
						{d.recall_misses.map((m) => ` ${m.scenario_id}→缺${m.missing.join("/")}`).join(";")}
					</CardContent>
				</Card>
			) : null}
			<Card>
				<CardHeader>
					<CardTitle>每指南漏斗({d.guidelines.length})</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3">
					{d.guidelines.length === 0 ? (
						<p className="text-sm text-text-muted">窗口内暂无带指南数据的评测批次。</p>
					) : null}
					{d.guidelines.map((g) => (
						<div
							key={g.id}
							className="flex flex-wrap items-center gap-3 rounded-lg border border-surface-border p-3"
						>
							<div className="min-w-48">
								<div className="text-sm font-medium text-text-primary">{g.title || g.id}</div>
								<div className="font-mono text-xs text-text-muted">{g.id}</div>
								<div className="mt-1 flex gap-1">
									<Pill tone={(g.target_node ?? "talk") === "talk" ? "neutral" : "accent"}>
										{GUIDANCE_NODE_LABEL[g.target_node ?? "talk"]}
									</Pill>
									{g.status !== "live" ? <Pill tone="muted">{g.status}</Pill> : null}
								</div>
							</div>
							<FunnelBar f={g.funnel} />
							{g.quadrant ? (
								<div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-text-secondary">
									{Object.entries(g.quadrant).map(([k, v]) => (
										<span key={k}>
											{k}{" "}
											<b
												className={
													v && (k === "误采纳" || k === "采而不遵" || k === "应采未采")
														? "text-brand-danger"
														: "text-text-primary"
												}
											>
												{v}
											</b>
										</span>
									))}
								</div>
							) : (
								<span className="text-xs text-text-muted">无断言样本(仅漏斗)</span>
							)}
						</div>
					))}
				</CardContent>
			</Card>
		</div>
	);
}

function GuidanceTab() {
	const q = useGuidance();
	const stats = useGuidanceStats(7); // M7:配置页直接看效果(配置↔观测闭环)
	const setStatus = useSetGuidanceStatus();
	const { toast } = useToast();
	const [fStage, setFStage] = useState(ALL);
	const [fStatus, setFStatus] = useState(ALL);
	const [editing, setEditing] = useState<GuidanceEntry | null>(null);
	const [isNew, setIsNew] = useState(false);
	const d = q.data;
	if (q.isLoading) {
		return (
			<Card>
				<CardContent className="py-6 text-sm text-text-muted">加载应答指南…</CardContent>
			</Card>
		);
	}
	if (!d) return null;
	const filtered = d.items.filter((g) => {
		if (fStage !== ALL && (g.scope.stage || "通用") !== fStage) return false;
		if (fStatus !== ALL && (g.status ?? "live") !== fStatus) return false;
		return true;
	});
	// 按场景(stage)分组:售前/售中/售后/通用,组内保持后端序(criticality 已排)
	const groups = ["售前", "售中", "售后", "通用"]
		.map((st) => ({
			stage: st,
			items: filtered.filter((g) => (g.scope.stage || "通用") === st),
		}))
		.filter((grp) => grp.items.length > 0);
	return (
		<div className="space-y-4">
			<Card>
				<CardContent className="flex flex-wrap items-center gap-3 py-3">
					<Select
						label="场景"
						value={fStage}
						onChange={setFStage}
						options={[
							{ value: ALL, label: "全部" },
							...d.stages.map((st) => ({ value: st, label: st })),
						]}
					/>
					<Select
						label="状态"
						value={fStatus}
						onChange={setFStatus}
						options={[
							{ value: ALL, label: "全部" },
							{ value: "live", label: "生效中" },
							{ value: "paused", label: "已暂停" },
							{ value: "draft", label: "草稿" },
						]}
					/>
					<span className="text-xs text-text-muted">
						生效 {d.snapshot.count}/{d.limits.max_live} 条 · 指纹 {d.snapshot.digest}
					</span>
					<span className="ml-auto">
						<Button
							type="button"
							size="sm"
							onClick={() => {
								setIsNew(true);
								setEditing({ ...EMPTY_GUIDANCE, scope: { intents: [], stage: "" } });
							}}
						>
							新增指南
						</Button>
					</span>
				</CardContent>
			</Card>
			{groups.map((grp) => (
				<Card key={grp.stage}>
					<CardHeader>
						<CardTitle>
							{grp.stage}({grp.items.length})
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						{grp.items.map((g) => (
							<GuidanceRow
								key={g.id}
								g={g}
								mini={stats.data?.guidelines.find((x) => x.id === g.id) ?? null}
								onEdit={() => {
									setIsNew(false);
									setEditing({ ...g, scope: { ...g.scope, intents: [...g.scope.intents] } });
								}}
								onToggle={() => {
									const next = (g.status ?? "live") === "live" ? "paused" : "live";
									setStatus.mutate(
										{ id: g.id, status: next },
										{
											onSuccess: () =>
												toast(`${g.id} 已${next === "live" ? "上线" : "暂停"}`, "success"),
											onError: (e) => toast(`操作失败:${guidanceErrDetail(e)}`, "error"),
										},
									);
								}}
								busy={setStatus.isPending}
							/>
						))}
					</CardContent>
				</Card>
			))}
			{!groups.length ? (
				<Card>
					<CardContent className="py-6 text-sm text-text-muted">当前筛选无指南。</CardContent>
				</Card>
			) : null}
			<GuidanceEditDrawer entry={editing} isNew={isNew} meta={d} onClose={() => setEditing(null)} />
		</div>
	);
}

function GuidanceRow({
	g,
	mini,
	onEdit,
	onToggle,
	busy,
}: {
	g: GuidanceEntry;
	mini: { funnel: { hit: number; applied: number; followed: number; violated: number } } | null;
	onEdit: () => void;
	onToggle: () => void;
	busy: boolean;
}) {
	const st = GUIDANCE_STATUS_META[g.status ?? "live"] ?? {
		label: "生效中",
		tone: "success" as BadgeTone,
	};
	return (
		<div className="rounded-lg border border-surface-border p-3">
			<div className="flex flex-wrap items-center gap-2">
				<span className="text-sm font-medium text-text-primary">{g.title || g.id}</span>
				<span className="font-mono text-xs text-text-muted">{g.id}</span>
				<Pill tone="neutral">{GUIDANCE_LAYER_LABEL[g.layer] ?? g.layer}</Pill>
				<Pill tone={GUIDANCE_CRIT_TONE[g.criticality ?? "should"] ?? "muted"}>
					{g.criticality ?? "should"}
				</Pill>
				<Pill tone={(g.target_node ?? "talk") === "talk" ? "neutral" : "accent"}>
					{GUIDANCE_NODE_LABEL[g.target_node ?? "talk"]}
				</Pill>
				<Pill tone={st.tone}>{st.label}</Pill>
				<span className="ml-auto flex gap-2">
					<Button type="button" size="sm" variant="outline" onClick={onEdit}>
						编辑
					</Button>
					<Button type="button" size="sm" variant="outline" disabled={busy} onClick={onToggle}>
						{(g.status ?? "live") === "live" ? "暂停" : "上线"}
					</Button>
				</span>
			</div>
			<div className="mt-2 space-y-1 text-sm">
				<p className="text-text-secondary">
					<span className="text-text-muted">当</span> {g.condition}
				</p>
				<p className="text-text-primary">{g.action}</p>
				<p className="font-mono text-xs text-text-muted">
					意图:{g.scope.intents.join(" / ") || "—"}
					{g.rationale ? ` · 依据:${g.rationale}` : ""}
				</p>
				{mini ? (
					<p className="text-xs text-text-muted" title="近 7 天评测批次里的效果(指南效果页看全量)">
						近 7 天:命中 {mini.funnel.hit} · 采纳 {mini.funnel.applied} · 遵循{" "}
						{mini.funnel.followed}
						{mini.funnel.violated ? (
							<span className="text-brand-danger"> · 违背 {mini.funnel.violated}</span>
						) : null}
					</p>
				) : null}
			</div>
		</div>
	);
}

/** 编辑抽屉:5 必填字段(id/layer/scope/condition/action)+ 可选项;校验错误 400 中文原因直显 */
function GuidanceEditDrawer({
	entry,
	isNew,
	meta,
	onClose,
}: {
	entry: GuidanceEntry | null;
	isNew: boolean;
	meta: GuidanceResponse;
	onClose: () => void;
}) {
	const upsert = useUpsertGuidance();
	const { toast } = useToast();
	const [form, setForm] = useState<GuidanceEntry | null>(entry);
	// 打开/切换条目时同步表单(受控:entry 变化即重置)
	useEffect(() => setForm(entry), [entry]);
	if (!entry || !form) return null;
	const set = (patch: Partial<GuidanceEntry>) => setForm({ ...form, ...patch });
	const toggleIntent = (it: string) => {
		const cur = form.scope.intents;
		set({
			scope: {
				...form.scope,
				intents: cur.includes(it) ? cur.filter((x) => x !== it) : [...cur, it],
			},
		});
	};
	const actionLen = form.action.length;
	return (
		<DrawerPrimitive.Root open onOpenChange={(o) => !o && onClose()}>
			<DrawerPrimitive.Portal>
				<DrawerPrimitive.Overlay className="ui-overlay fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
				<DrawerPrimitive.Content className="ui-drawer fixed right-0 top-0 z-50 flex h-full w-[min(640px,95vw)] flex-col border-l border-surface-border bg-surface-background shadow-xl focus:outline-none">
					<div className="flex items-center justify-between border-b border-surface-border px-4 py-3">
						<DrawerPrimitive.Title className="text-sm font-semibold text-text-primary">
							{isNew ? "新增指南" : `编辑指南 ${entry.id}`}
						</DrawerPrimitive.Title>
						<DrawerPrimitive.Close asChild>
							<Button type="button" size="sm" variant="ghost" aria-label="关闭">
								<X className="h-4 w-4" />
							</Button>
						</DrawerPrimitive.Close>
					</div>
					<div className="flex-1 space-y-4 overflow-y-auto p-4 text-sm">
						<label htmlFor="g-edit-id" className="block space-y-1">
							<span className="text-xs text-text-muted">id(语义化,评测断言/归因引用;必填)</span>
							<Input
								id="g-edit-id"
								value={form.id}
								disabled={!isNew}
								placeholder="G-presale-xxx"
								onChange={(e) => set({ id: e.target.value })}
							/>
						</label>
						<label htmlFor="g-edit-title" className="block space-y-1">
							<span className="text-xs text-text-muted">标题(列表展示名)</span>
							<Input
								id="g-edit-title"
								value={form.title ?? ""}
								placeholder="需求挖掘·追问上限"
								onChange={(e) => set({ title: e.target.value })}
							/>
						</label>
						<div className="flex flex-wrap gap-3">
							<Select
								label="层级"
								value={form.layer}
								onChange={(v) => set({ layer: v as GuidanceEntry["layer"] })}
								options={meta.layers.map((l) => ({
									value: l,
									label: GUIDANCE_LAYER_LABEL[l] ?? l,
								}))}
							/>
							<Select
								label="约束度"
								value={form.criticality ?? "should"}
								onChange={(v) => set({ criticality: v as GuidanceEntry["criticality"] })}
								options={meta.criticalities.map((c) => ({ value: c, label: c }))}
							/>
							<Select
								label="阶段"
								value={form.scope.stage || "通用"}
								onChange={(v) => set({ scope: { ...form.scope, stage: v === "通用" ? "" : v } })}
								options={meta.stages.map((st) => ({ value: st, label: st }))}
							/>
							<Select
								label="状态"
								value={form.status ?? "live"}
								onChange={(v) => set({ status: v as GuidanceEntry["status"] })}
								options={[
									{ value: "live", label: "生效中" },
									{ value: "paused", label: "已暂停" },
									{ value: "draft", label: "草稿" },
								]}
							/>
							<Select
								label="作用环节"
								value={form.target_node ?? "talk"}
								onChange={(v) => set({ target_node: v as GuidanceEntry["target_node"] })}
								options={(meta.target_nodes ?? ["talk", "decide", "both"]).map((n) => ({
									value: n,
									label: GUIDANCE_NODE_LABEL[n] ?? n,
								}))}
							/>
						</div>
						<div className="space-y-1">
							<span className="text-xs text-text-muted">适用意图(至少一个;global=全意图常驻)</span>
							<div className="flex flex-wrap gap-1.5">
								{["global", ...meta.intents].map((it) => {
									const on = form.scope.intents.includes(it);
									return (
										<button
											key={it}
											type="button"
											onClick={() => toggleIntent(it)}
											className={`rounded-full border px-2.5 py-0.5 font-mono text-xs transition-colors ${
												on
													? "border-brand-accent bg-brand-accent/5 text-text-primary"
													: "border-surface-border text-text-muted hover:bg-surface-subtle"
											}`}
										>
											{it}
										</button>
									);
								})}
							</div>
						</div>
						<label htmlFor="g-edit-condition" className="block space-y-1">
							<span className="text-xs text-text-muted">条件 condition(「当…时」;必填)</span>
							<Textarea
								id="g-edit-condition"
								value={form.condition}
								rows={2}
								placeholder="当顾客表达购买意向但未给出具体需求时"
								onChange={(e) => set({ condition: e.target.value })}
							/>
						</label>
						<label htmlFor="g-edit-action" className="block space-y-1">
							<span className="text-xs text-text-muted">
								动作 action(必填;{actionLen}/{meta.limits.max_action_len} 字
								{actionLen > meta.limits.max_action_len ? " · 超长,请拆条或升格为流程" : ""})
							</span>
							<Textarea
								id="g-edit-action"
								value={form.action}
								rows={4}
								placeholder="最多追问一轮;得不到答案就直接给 2-3 款热门商品并各附一句选择理由"
								onChange={(e) => set({ action: e.target.value })}
							/>
						</label>
						<label htmlFor="g-edit-requires-slot" className="block space-y-1">
							<span className="text-xs text-text-muted">
								前置反问槽 requires_slot(可选;仅 must 生效:办理确认前保证问过一次,如 returnReason)
							</span>
							<Input
								id="g-edit-requires-slot"
								value={form.requires_slot ?? ""}
								placeholder="留空=不启用"
								onChange={(e) => set({ requires_slot: e.target.value || undefined })}
							/>
						</label>
						<label htmlFor="g-edit-rationale" className="block space-y-1">
							<span className="text-xs text-text-muted">依据 rationale(为什么有这条,可选)</span>
							<Textarea
								id="g-edit-rationale"
								value={form.rationale ?? ""}
								rows={2}
								onChange={(e) => set({ rationale: e.target.value })}
							/>
						</label>
					</div>
					<div className="flex justify-end gap-2 border-t border-surface-border px-4 py-3">
						<Button type="button" variant="outline" onClick={onClose}>
							取消
						</Button>
						<Button
							type="button"
							disabled={upsert.isPending}
							onClick={() =>
								upsert.mutate(form, {
									onSuccess: (d) => {
										const warn = d.warnings?.length ? `;提示:${d.warnings[0]}` : "";
										toast(`${form.id} 已保存,即时生效${warn}`, warn ? "info" : "success");
										onClose();
									},
									onError: (e) => toast(`保存失败:${guidanceErrDetail(e)}`, "error"),
								})
							}
						>
							{upsert.isPending ? "保存中…" : "保存"}
						</Button>
					</div>
				</DrawerPrimitive.Content>
			</DrawerPrimitive.Portal>
		</DrawerPrimitive.Root>
	);
}

/** 应答配置 · 规则(C23 档1):六域确定性规则注册表,只读忠实(后端 /answer-rules,源自 rules.RULE_REGISTRY 与真实常量)。 */
const GENERIC_BADGE: Record<
	AnswerRule["generic"],
	{ label: string; tone: "success" | "info" | "warning" }
> = {
	ok: { label: "✓ 通用", tone: "success" },
	limit: { label: "△ 结构限制", tone: "info" },
	custom: { label: "✗ 定制味", tone: "warning" },
};

function AnswerRulesTab() {
	const q = useAnswerRules();
	const d: AnswerRules | undefined = q.data;
	if (q.isLoading) {
		return (
			<Card>
				<CardContent className="py-6 text-sm text-text-muted">加载应答规则…</CardContent>
			</Card>
		);
	}
	if (!d) return null;
	return (
		<div className="space-y-4">
			<Card>
				<CardHeader>
					<CardTitle>这页是什么</CardTitle>
				</CardHeader>
				<CardContent className="space-y-2 text-sm text-text-secondary">
					<p>
						客服 AI 的每一次回复背后,有 <strong>{d.registry.length} 条确定性保障规则</strong>
						在兜底——该确认的必确认、该问清的问清、金额不心算、卡片不编造。本页按业务作用分组展示每条规则
						<strong>为顾客做了什么</strong>;点「技术实现」可看它在链路哪一环、怎么实现。
						内容与运行代码同源(注册表单测锁),所见即真实生效的逻辑。
					</p>
					<details>
						<summary className="cursor-pointer text-xs text-brand-primary">
							技术总则(分工铁律 / 变更四问 / 执行链路图)
						</summary>
						<div className="mt-2 space-y-2">
							<p className="text-xs text-text-muted">
								分工铁律:LLM 干语义(N1/N2/N3),确定性规则干承载与安全。规则即代码,改规则=改代码=过 CI
								门+同 PR 改注册表(单测锁)。变更四问(C23 §五/铁律9):通用吗 · 必要吗 · 看得见吗 ·
								放哪排第几。安全红线是"别堆硬规则"的例外——该硬就硬。
							</p>
							<pre className="overflow-x-auto rounded-md bg-surface-card p-3 font-mono text-xs leading-5">
								{`单轮(每用户消息,≤6 轮 N2 决策 = N2-BUDGET):
  N1 意图契约 ─ N1-NORM(契约归一化)
   └→ [N2 决策 ─ N2-ALLOWED 圈定 · G9 反问护栏 · G10 空手护栏 · G11 越界 · G13 解析兜底
        └→ G6 排除改道
            └→ B2.5 组参(REF 指代 / LINE 选行 / ADDR 地址 / REASON 枚举 / G17 推荐降级 → resolve 透出)
                └→ G14 行项消歧(同名多行→clarify) → G22 写对象一致 → G8 写归属 → G1 必填(B3 分类:链式/反问)
                    └→ skill 执行(写类经 G5 确认门 dry-run;确认后经 G23 确认参数一致才真执行)
                        └→ G4 判停 · G16 无进展拦停(multi→MULTI-CAND 候选透出 / confirm_gate…) · G12 换道收尾] ×循环
   └→ N3 话术 → O1 块回填
跨轮(session):S5 挂起态机 ⇄ S4 补槽合并(provide) · S1 焦点 / S2 商品卡 / S3 订单摘要 / S6 对话窗口
N2 输入面:V1 last 摘要 · V2 降采样`}
							</pre>
						</div>
					</details>
				</CardContent>
			</Card>
			{d.domains.map((dom) => {
				const items = d.registry.filter((r) => r.domain === dom);
				if (!items.length) return null;
				const meta = d.domain_meta?.[dom];
				return (
					<Card key={dom}>
						<CardHeader>
							<CardTitle>
								{meta?.biz ?? dom}
								<span className="ml-2 text-sm font-normal text-text-muted">
									{items.length} 条 · 技术域:{dom}
								</span>
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							{meta?.desc ? <p className="text-xs text-text-muted">{meta.desc}</p> : null}
							{items.map((r) => (
								<div key={r.id} className="space-y-1.5 rounded-lg border border-surface-border p-3">
									<div className="flex flex-wrap items-center gap-2">
										<span className="text-sm font-semibold text-text-primary">{r.name}</span>
										{r.generic !== "ok" ? (
											<Pill tone={GENERIC_BADGE[r.generic].tone}>
												{GENERIC_BADGE[r.generic].label}
											</Pill>
										) : null}
									</div>
									<p className="text-sm text-text-secondary">{r.biz_role}</p>
									<p className="rounded-md bg-surface-card px-2 py-1.5 text-xs text-text-secondary">
										例:{r.biz_example}
									</p>
									<details>
										<summary className="cursor-pointer text-xs text-brand-primary">
											技术实现({r.id})
										</summary>
										<div className="mt-1.5 space-y-1 border-l-2 border-surface-border pl-2.5">
											<div className="flex flex-wrap items-center gap-1.5 text-xs">
												<code className="rounded-md bg-surface-card px-1.5 font-mono font-semibold text-text-primary">
													{r.id}
												</code>
												{r.trace ? (
													<Pill tone="info">trace: {r.trace}</Pill>
												) : (
													<span className="text-text-muted">无 trace 标签(经 resolve 透出)</span>
												)}
											</div>
											<p className="text-xs text-text-secondary">机制:{r.mechanism}</p>
											<p className="text-xs">
												<span className="rounded-md bg-surface-card px-1.5 py-0.5 font-medium text-text-primary">
													{r.stage}
												</span>
												<span className="ml-1.5 text-text-secondary">条件:{r.when}</span>
											</p>
											<p className="text-xs text-text-secondary">→ 输出承接:{r.output_to}</p>
											{/* 字段级 IO:真卡观测(rule_io),补上 output_to 这句散文答不出的"吃什么吐什么" */}
											{r.io_observed ? (
												<div className="space-y-1 rounded-md border border-surface-border bg-surface-subtle p-2">
													<p className="flex flex-wrap items-center gap-1.5 text-xs">
														<Pill tone="success">真卡观测 {r.io_hits} 次</Pill>
														<Pill tone="muted">{r.io_channel}</Pill>
														<span className="text-text-muted">输入键</span>
														{r.io_in_keys.map((k) => (
															<code key={k} className="font-mono text-[11px] text-text-secondary">
																{k}
															</code>
														))}
														<span className="ml-2 text-text-muted">输出键</span>
														{r.io_out_keys.map((k) => (
															<code key={k} className="font-mono text-[11px] text-text-secondary">
																{k}
															</code>
														))}
													</p>
													{r.io_sample ? (
														<>
															<p className="break-all font-mono text-[11px] text-text-muted">
																样例入:{r.io_sample.input}
															</p>
															<p className="break-all font-mono text-[11px] text-text-muted">
																样例出:{r.io_sample.output}
															</p>
														</>
													) : null}
												</div>
											) : r.io_channel === "无观测通道" ? (
												<p className="text-xs text-text-muted">
													字段级 IO:<span className="text-text-secondary">无观测通道</span>
													—— 该规则不落 trace,结果经 state / params 直接承接(如 V1/V2/O1 与 B2.5
													常驻分类表)。不是没触发,是看不到。
												</p>
											) : (
												<p className="text-xs text-text-muted">
													字段级 IO:有观测通道(
													<span className="font-mono">{r.io_channel}</span>
													)但最近批次未触发 —— 低频规则,需更多批次或专门场景才会出现。
												</p>
											)}
											<p className="text-xs text-text-muted">
												为什么这么设计:{r.serves}
												{r.note ? <> · 备注:{r.note}</> : null}
											</p>
											<p className="text-xs text-text-muted">
												实现:<code className="font-mono">{r.where}</code>
												{r.refs ? <> · 出处:{r.refs}</> : null}
											</p>
										</div>
									</details>
								</div>
							))}
						</CardContent>
					</Card>
				);
			})}
			<Card>
				<CardHeader>
					<CardTitle>活配置快照(直接读代码常量)</CardTitle>
				</CardHeader>
				<CardContent className="space-y-2 text-sm text-text-secondary">
					<p className="text-xs text-text-muted">
						轮预算 budget={d.config.budget} · N2 摘要上限 digest_max={d.config.digest_max} · N2
						基础可选:{d.config.n2_base_skills.join(" / ")} · 金额口径标量{" "}
						{d.config.scalar_keys.length} 个
					</p>
					<details>
						<summary className="cursor-pointer text-xs text-brand-primary">
							展开完整快照 JSON(REQUIRED / INTENT_SKILL / SKILL_TYPE / EXCLUDE_RULES / B3 …)
						</summary>
						<pre className="mt-2 max-h-96 overflow-auto rounded bg-surface-card p-2 font-mono text-xs">
							{JSON.stringify(d.config, null, 2)}
						</pre>
					</details>
				</CardContent>
			</Card>
		</div>
	);
}

/** 应答配置 · SKILL(输入输出 + 参数来源) */
function SkillRefTab() {
	const g = useCatalogGuard();
	if ("guard" in g) return g.guard;
	return (
		<div className="space-y-6">
			<SkillPortPanel />
			<SkillIO skills={g.data.skills} />
			<ParamSources data={g.data} />
		</div>
	);
}

/** Shopify 店铺连接(单商户;secret 走 .env 只报有无) */
function ConnectTab() {
	const q = useConnect();
	const set = useSetConnect();
	const probe = useConnectProbe();
	if (!q.data) return null;
	const m = q.data.merchant;
	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle>店铺连接</CardTitle>
					<p className="text-xs text-text-secondary">
						live 执行的数据源;secret 配置在服务端 .env,此处只显示有无。
					</p>
				</CardHeader>
				<CardContent className="space-y-3">
					<div className="flex flex-wrap items-center gap-3 text-sm">
						<span className="text-text-secondary">域名</span>
						<span className="font-mono">{m.shop_domain || "未配置"}</span>
						<span className="text-text-secondary">API 版本</span>
						<span className="font-mono">{m.api_version}</span>
						<Badge tone={m.token_configured ? "success" : "danger"}>
							{m.token_configured
								? `凭证 ${q.data.key_fingerprint || "已配置"}`
								: "凭证未配置(.env)"}
						</Badge>
						<Badge tone={m.enabled ? "success" : "muted"}>{m.enabled ? "启用" : "停用"}</Badge>
					</div>
					<div className="flex flex-wrap items-center gap-3">
						<LabeledSelect
							label="鉴权模式"
							value={m.auth_mode}
							onChange={(v) => set.mutate({ auth_mode: v })}
							options={q.data.auth_modes.map((a) => ({
								value: a,
								label: a === "static_token" ? "静态 token(dev store)" : "client_credentials",
							}))}
						/>
						<Button variant="outline" size="sm" onClick={() => set.mutate({ enabled: !m.enabled })}>
							{m.enabled ? "停用连接" : "启用连接"}
						</Button>
						<Button size="sm" disabled={probe.isPending} onClick={() => probe.mutate()}>
							{probe.isPending ? "体检中…" : "连接体检"}
						</Button>
					</div>
				</CardContent>
			</Card>
			{q.data.probe_history.length ? (
				<Card>
					<CardHeader>
						<CardTitle>体检历史(近 {q.data.probe_history.length} 次)</CardTitle>
					</CardHeader>
					<CardContent className="space-y-1">
						{[...q.data.probe_history].reverse().map((h) => (
							<div key={h.ts} className="flex items-center gap-2 text-xs">
								<Badge tone={h.ok ? "success" : "danger"}>{h.ok ? "全通" : "有失败"}</Badge>
								<span className="text-text-secondary">{h.ts}</span>
							</div>
						))}
					</CardContent>
				</Card>
			) : null}
			{probe.data ? (
				<Card>
					<CardHeader>
						<CardTitle>体检结果 {probe.data.ok ? "· 全通" : "· 有失败"}</CardTitle>
					</CardHeader>
					<CardContent className="space-y-1">
						{probe.data.items.map((it) => (
							<div key={it.name} className="flex items-center gap-2 text-sm">
								<Badge tone={it.ok ? "success" : "danger"}>{it.ok ? "通" : it.error_code}</Badge>
								<span className="font-mono text-xs">{it.name}</span>
								<span className="truncate text-xs text-text-secondary">{it.detail}</span>
							</div>
						))}
					</CardContent>
				</Card>
			) : null}
		</div>
	);
}

/** 每 skill 执行来源徽标:罐头(模拟)/录制回放/真实执行 —— 状态一目了然(V2-P1) */
function BackendBadge({ backend }: { backend?: string }) {
	if (!backend) return null;
	const tone = backend === "live" ? "success" : backend === "recorded" ? "info" : "muted";
	const label =
		backend === "live" ? "真实执行" : backend === "recorded" ? "录制回放" : "罐头(模拟)";
	return <Badge tone={tone}>{label}</Badge>;
}

const BACKEND_LABEL: Record<string, string> = {
	fixture: "罐头模拟",
	recorded: "录制回放",
	live: "真实执行",
};

/** 技能数据源(SkillPort):每个技能被调用时用哪种数据。表格化(技能/数据源/真实连通/存档),
 * 概念一句话讲清;评测跑批要求全罐头(非罐头被 /sim/run 拦截,须显式放行)。 */
function SkillPortPanel() {
	const q = useSkillPort();
	const set = useSetSkillPort();
	const clear = useClearRecordings();
	if (!q.data) return null;
	const d = q.data;
	return (
		<Card>
			<CardHeader>
				<CardTitle>技能数据源(SkillPort)</CardTitle>
				<p className="text-xs text-text-secondary">
					每个技能被调用时用哪种数据:<b>罐头模拟</b>=内置模拟数据(评测基准,始终可用);
					<b>录制回放</b>=之前真实调用的存档,离线重放(无存档时回落罐头);
					<b>真实执行</b>=实时调 Shopify 店铺(需连通)。评测跑批要求全罐头,非罐头会被拦截。
				</p>
				<div className="flex flex-wrap items-center gap-2 text-xs">
					<span className="text-text-muted">真实数据源</span>
					<span className="font-mono">{d.merchant.shop_domain || "未配置"}</span>
					{d.probe ? (
						<Badge tone={d.probe.ok ? "success" : "danger"}>
							连通体检{d.probe.ok ? "全通" : "有失败"} · {d.probe.ts?.slice(5, 16)}
						</Badge>
					) : (
						<Badge tone="muted">未体检(去连接页)</Badge>
					)}
					<button
						type="button"
						className="text-brand-accent underline-offset-2 hover:underline"
						onClick={() => {
							window.location.hash = "connect";
						}}
					>
						连接页
					</button>
					{d.last_change ? (
						<span className="text-text-muted">配置最近变更 {d.last_change.ts.slice(5, 16)}</span>
					) : null}
				</div>
			</CardHeader>
			<CardContent className="space-y-3">
				<div className="flex flex-wrap items-center gap-3">
					<LabeledSelect
						label="全局默认"
						value={d.config.default}
						onChange={(v) => set.mutate({ default: v })}
						options={d.backends.map((b) => ({ value: b, label: BACKEND_LABEL[b] ?? b }))}
					/>
					<LabeledSelect
						label="录制未命中时"
						value={d.config.replay_miss}
						onChange={(v) => set.mutate({ replay_miss: v })}
						options={d.miss_policies.map((m) => ({
							value: m,
							label: m === "error" ? "报错(严格)" : "回落罐头",
						}))}
					/>
					<Button
						variant="outline"
						size="sm"
						disabled={clear.isPending || d.recordings.total === 0}
						onClick={() => clear.mutate()}
					>
						清空存档({d.recordings.total})
					</Button>
				</div>
				<div className="overflow-x-auto rounded-lg border border-surface-border">
					<table className="w-full text-sm">
						<thead className="bg-surface-muted text-xs text-text-secondary">
							<tr>
								<th className="px-3 py-2 text-left font-medium">技能</th>
								<th className="px-3 py-2 text-left font-medium">数据源</th>
								<th className="px-3 py-2 text-left font-medium">真实连通</th>
								<th className="px-3 py-2 text-left font-medium">存档</th>
							</tr>
						</thead>
						<tbody>
							{d.skills.map((sk) => (
								<tr key={sk.name} className="border-t border-surface-border">
									<td className="px-3 py-1.5 font-mono text-xs text-text-primary">{sk.name}</td>
									<td className="px-3 py-1.5">
										<StyledSelect
											value={d.config.skills[sk.name] ?? ""}
											onChange={(e) => set.mutate({ skills: { [sk.name]: e.target.value } })}
										>
											<option value="">跟随全局({BACKEND_LABEL[d.config.default]})</option>
											{d.backends.map((b) => (
												<option key={b} value={b}>
													{BACKEND_LABEL[b] ?? b}
												</option>
											))}
										</StyledSelect>
									</td>
									<td className="px-3 py-1.5">
										<LiveReadyBadge domain={sk.live_domain} probe={d.probe} />
									</td>
									<td className="px-3 py-1.5 text-xs text-text-secondary">
										{sk.recorded_count ? `${sk.recorded_count} 条` : "—"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</CardContent>
		</Card>
	);
}

/** 评测配置 · 评测数据(客户 / 订单 / 商品沙盒) */
function EvalDataTab() {
	const g = useCatalogGuard();
	if ("guard" in g) return g.guard;
	return <SeedDataView seed={g.data.seed} />;
}

function SeedDataView({ seed }: { seed: CatalogResponse["seed"] }) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>
					客户 {seed.customers.length} · 订单 {seed.orders.length} · 商品 {seed.products.length}
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4">
				<div>
					<div className="mb-1 text-xs font-medium text-text-secondary">
						客户(可按场景选当前客户)
					</div>
					<div className="overflow-x-auto rounded-md border border-surface-border">
						<table className="w-full text-xs">
							<thead className="bg-surface-muted text-text-secondary">
								<tr>
									<th className="px-3 py-1.5 text-left font-medium">customer_id</th>
									<th className="px-3 py-1.5 text-left font-medium">姓名</th>
									<th className="px-3 py-1.5 text-left font-medium">背景</th>
									<th className="px-3 py-1.5 text-left font-medium">订单数</th>
								</tr>
							</thead>
							<tbody>
								{seed.customers.map((c) => (
									<tr key={c.customer_id} className="border-t border-surface-border">
										<td className="px-3 py-1.5 font-mono text-brand-primary">{c.customer_id}</td>
										<td className="px-3 py-1.5 text-text-secondary">{c.name}</td>
										<td className="px-3 py-1.5 text-text-secondary">{c.bg}</td>
										<td className="px-3 py-1.5 text-text-secondary">{c.order_count}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
				<div>
					<div className="mb-1 text-xs font-medium text-text-secondary">订单(全状态)</div>
					<div className="overflow-x-auto rounded-md border border-surface-border">
						<table className="w-full text-xs">
							<thead className="bg-surface-muted text-text-secondary">
								<tr>
									<th className="px-3 py-1.5 text-left font-medium">订单</th>
									<th className="px-3 py-1.5 text-left font-medium">客户</th>
									<th className="px-3 py-1.5 text-left font-medium">履约状态</th>
									<th className="px-3 py-1.5 text-left font-medium">商品</th>
								</tr>
							</thead>
							<tbody>
								{seed.orders.map((o) => (
									<tr key={o.orderId} className="border-t border-surface-border">
										<td className="px-3 py-1.5 font-mono text-text-primary">
											{o.orderId.replace("gid://shopify/Order/", "#")}
										</td>
										<td className="px-3 py-1.5 font-mono text-text-muted">{o.customer_id}</td>
										<td className="px-3 py-1.5">
											<Pill tone={observeTone(o.fulfillment_status ?? "")}>
												{o.fulfillment_status}
											</Pill>
										</td>
										<td className="px-3 py-1.5 text-text-secondary">{o.items}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
				<div>
					<div className="mb-1 text-xs font-medium text-text-secondary">商品(变体 / 库存)</div>
					<div className="space-y-1.5">
						{seed.products.map((p) => (
							<div
								key={p.productId}
								className="rounded-md border border-surface-border px-3 py-2 text-xs"
							>
								<span className="font-mono text-text-primary">{p.productId}</span>{" "}
								<span className="text-text-secondary">{p.title}</span>
								{p.discontinued ? <Pill tone="danger">已停产</Pill> : null}
								<div className="mt-1 flex flex-wrap gap-1">
									{p.variants.map((v) => (
										<span
											key={v.variantId}
											className="rounded border border-surface-border px-1.5 py-0.5 text-text-muted"
										>
											{Object.values(v.options).join("/")} ¥{v.price} ·{" "}
											<span
												className={v.inventory === 0 ? "text-brand-danger" : "text-brand-success"}
											>
												{v.inventory === 0 ? "缺货" : `库存${v.inventory}`}
											</span>
										</span>
									))}
								</div>
							</div>
						))}
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function IntentTable({ data }: { data: CatalogResponse }) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>意图清单({data.intents.length})</CardTitle>
			</CardHeader>
			<CardContent>
				<div className="overflow-x-auto rounded-lg border border-surface-border">
					<table className="w-full text-sm">
						<thead className="bg-surface-muted text-text-secondary">
							<tr>
								<th className="px-3 py-2 text-left font-medium">意图</th>
								<th className="px-3 py-2 text-left font-medium">阶段</th>
								<th className="px-3 py-2 text-left font-medium">映射主 Skill</th>
								<th className="px-3 py-2 text-left font-medium">说明</th>
								<th className="px-3 py-2 text-left font-medium">边界样例(注入 N1)</th>
							</tr>
						</thead>
						<tbody>
							{data.intents.map((it) => (
								<tr key={it.intent} className="border-t border-surface-border">
									<td className="px-3 py-2 font-mono text-xs text-text-primary">{it.intent}</td>
									<td className="px-3 py-2 text-xs text-text-secondary">{it.stage ?? "—"}</td>
									<td className="px-3 py-2 font-mono text-xs text-text-secondary">
										{it.skill ?? <span className="text-text-muted">—(不调工具)</span>}
									</td>
									<td className="px-3 py-2 text-text-secondary">{it.desc}</td>
									<td className="px-3 py-2 text-xs text-text-secondary">
										{it.boundary || <span className="text-text-muted">—</span>}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</CardContent>
		</Card>
	);
}

function SkillIO({ skills }: { skills: SkillRow[] }) {
	const port = useSkillPort();
	const bySkill = Object.fromEntries((port.data?.skills ?? []).map((s) => [s.name, s]));
	const probe = port.data?.probe ?? null;
	return (
		<Card>
			<CardHeader>
				<CardTitle>Skill 输入 / 输出({skills.length})</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				{skills.map((s) => (
					<SkillCard key={s.name} skill={s} port={bySkill[s.name]} probe={probe} />
				))}
			</CardContent>
		</Card>
	);
}

/** live 可用性徽标:skill 的探针域 × 最近体检结果(orders 被 PCD 拦会亮红,一眼看清) */
function LiveReadyBadge({ domain, probe }: { domain?: string; probe: SkillPortProbe | null }) {
	if (!domain || domain === "none" || domain === "rag" || !probe) return null;
	const hit = probe.by_domain[domain];
	if (!hit) return null;
	return hit.ok ? (
		<Badge tone="success">真实可用</Badge>
	) : (
		<Badge tone="danger">真实受限({hit.error_code})</Badge>
	);
}

function SkillCard({
	skill,
	port,
	probe,
}: {
	skill: SkillRow;
	port?: SkillPortSkill;
	probe: SkillPortProbe | null;
}) {
	const [open, setOpen] = useState(false);
	return (
		<div className="rounded-lg border border-surface-border">
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className="flex w-full items-center gap-3 px-4 py-3 text-left"
			>
				<span className="font-mono text-sm font-medium text-text-primary">{skill.name}</span>
				{skill.tool_type === "WRITE" ? (
					<Badge tone="warning">写操作 · 确认门</Badge>
				) : (
					<Badge tone="muted">{skill.tool_type}</Badge>
				)}
				<BackendBadge backend={port?.backend} />
				<LiveReadyBadge domain={port?.live_domain} probe={probe} />
				{port?.recorded_count ? <Badge tone="muted">录制 {port.recorded_count}</Badge> : null}
				<span className="text-sm text-text-secondary">{skill.desc}</span>
				{skill.required.length ? (
					<span className="ml-auto flex flex-wrap gap-1">
						{skill.required.map((r) => (
							<Pill key={r} tone="warning">
								必填 {r}
							</Pill>
						))}
					</span>
				) : (
					<span className="ml-auto">
						<Pill tone="muted">无必填</Pill>
					</span>
				)}
				<span className="text-xs text-text-muted">{open ? "收起" : "展开"}</span>
			</button>
			{open ? (
				<div className="space-y-3 border-t border-surface-border px-4 py-3">
					{port?.latest_recording ? (
						<div className="rounded-md bg-surface-muted/50 p-2 text-xs">
							<span className="font-medium text-text-primary">
								最近真实调用
								{port.latest_recording.ts ? `(${port.latest_recording.ts.slice(5, 16)})` : ""}:
							</span>{" "}
							<span className="break-all text-text-secondary">
								{JSON.stringify(port.latest_recording.res).slice(0, 300)}
							</span>
						</div>
					) : null}
					{skill.logic ? (
						<div className="rounded-md bg-surface-muted/50 p-2 text-xs text-text-secondary">
							<span className="font-medium text-text-primary">返回逻辑(数据条件→状态):</span>{" "}
							{skill.logic}
						</div>
					) : null}
					<div>
						<div className="mb-1 text-xs font-medium text-text-secondary">输入字段</div>
						<div className="overflow-x-auto rounded-md border border-surface-border">
							<table className="w-full text-xs">
								<tbody>
									{skill.input.length ? (
										skill.input.map((f) => (
											<tr key={f.name} className="border-b border-surface-border last:border-0">
												<td className="px-3 py-1.5 font-mono text-text-primary">{f.name}</td>
												<td className="px-3 py-1.5">
													{f.required ? (
														<Pill tone="warning">必填</Pill>
													) : (
														<Pill tone="muted">可选</Pill>
													)}
												</td>
												<td className="px-3 py-1.5 text-text-secondary">{f.note}</td>
											</tr>
										))
									) : (
										<tr>
											<td className="px-3 py-1.5 text-text-muted">无入参</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
					<div className="grid gap-3 md:grid-cols-2">
						<div>
							<div className="mb-1 text-xs font-medium text-text-secondary">样例入参</div>
							<JsonBox value={skill.sample_input} />
						</div>
						<div>
							<div className="mb-1 text-xs font-medium text-text-secondary">
								样例输出(真实 fixtures)
							</div>
							<JsonBox value={skill.sample_output} />
						</div>
					</div>
					{skill.intents.length ? (
						<div className="text-xs text-text-muted">触发意图:{skill.intents.join(" / ")}</div>
					) : null}
				</div>
			) : null}
		</div>
	);
}

function ParamSources({ data }: { data: CatalogResponse }) {
	return (
		<Card>
			<CardHeader>
				<CardTitle>参数来源(5 类)与退货原因枚举</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<div className="flex flex-wrap gap-2">
					{data.param_sources.map((p) => (
						<span
							key={p.code}
							className="rounded-md border border-surface-border px-3 py-1.5 text-xs text-text-secondary"
						>
							<span className="font-mono font-medium text-text-primary">{p.code}</span> {p.name}
							<span className="text-text-muted"> · {p.note}</span>
						</span>
					))}
				</div>
				<div className="flex flex-wrap gap-1">
					<span className="text-xs text-text-secondary">returnReason 枚举:</span>
					{data.return_reasons.map((r) => (
						<Pill key={r} tone="muted">
							{r}
						</Pill>
					))}
				</div>
			</CardContent>
		</Card>
	);
}

/* ====================== Tab 2:统一场景库(筛选 / 批量真多轮 / 一致性自检) ====================== */
const ALL = "__all__";
const TYPE_TONE: Record<string, Tone> = {
	售前: "info",
	售中: "warning",
	售后: "success",
	其他: "muted",
};
const KIND_LABEL: Record<Scenario["kind"], string> = {
	single: "单轮",
	mt_offline: "离线多轮",
	mt_llm: "真LLM多轮",
};

function ScenariosTab({ hasKey, onTry }: { hasKey: boolean; onTry: (load: ScenarioLoad) => void }) {
	const ov = useScenarios();
	if (ov.isLoading) {
		return (
			<Card>
				<CardContent className="py-6 text-sm text-text-muted">加载场景库…</CardContent>
			</Card>
		);
	}
	if (!ov.data) {
		return (
			<Card>
				<CardContent className="py-6 text-sm text-brand-danger">场景库加载失败</CardContent>
			</Card>
		);
	}
	return <ScenarioLibrary data={ov.data} hasKey={hasKey} onTry={onTry} />;
}

const PAGE_SIZE = 15;

/** 自然语言轮场景(单轮 / 真 LLM 多轮)可载入交互台;离线多轮是契约非消息,不可载 */
function scenarioToLoad(s: Scenario): ScenarioLoad | null {
	if (s.kind === "mt_offline") return null;
	const msgs = s.turns.map((t) => t.msg).filter(Boolean);
	if (!msgs.length) return null;
	return { id: s.id, customer_id: s.customer_id, msgs };
}

// C25 批2:Select 收编为 LabeledSelect 原语(白底细边全圆角筛选 pill,API 相同)
const Select = LabeledSelect;

function SelfcheckBar({ data, rendered }: { data: ScenariosResponse; rendered: number }) {
	const sc = data.selfcheck;
	const ok = rendered === sc.scenarios_total;
	return (
		<div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-surface-subtle p-3 text-xs">
			<span className="font-medium text-text-primary">一致性自检(页面 vs 后端代码真值)</span>
			<span className={ok ? "text-brand-success" : "text-brand-danger"}>
				{ok ? "✓" : "✗"} 场景 页面渲染 {rendered} / 后端真值 {sc.scenarios_total}(离线{" "}
				{sc.scenarios_offline} · 真LLM {sc.scenarios_llm})
			</span>
			<span className="text-text-secondary">
				意图 {sc.intents} · skill {sc.skills} · 客户 {sc.customers} · 订单 {sc.orders} · 商品{" "}
				{sc.products}
			</span>
			{!ok ? <span className="text-brand-danger">展示与实现脱节,请核对适配器!</span> : null}
		</div>
	);
}

function ScenarioLibrary({
	data,
	hasKey,
	onTry,
}: { data: ScenariosResponse; hasKey: boolean; onTry: (load: ScenarioLoad) => void }) {
	const runM = useRunScenarios();
	const scenarios = data.scenarios;
	const [sel, setSel] = useState<Set<string>>(new Set());
	const [results, setResults] = useState<Record<string, ScenarioRunResult>>({});
	const [skipped, setSkipped] = useState<string[]>([]);
	const [lastMode, setLastMode] = useState<"offline" | "llm" | null>(null);
	const [drawerId, setDrawerId] = useState<string | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const [page, setPage] = useState(0);
	// 筛选维度
	const [fLayer, setFLayer] = useState(ALL);
	const [fType, setFType] = useState(ALL);
	const [fIntent, setFIntent] = useState(ALL);
	const [fCustomer, setFCustomer] = useState(ALL);
	const [fSkill, setFSkill] = useState(ALL);
	const [fMulti, setFMulti] = useState(ALL);
	const [fResult, setFResult] = useState(ALL);

	const intents = useMemo(
		() => Array.from(new Set(scenarios.map((s) => s.intent ?? "—"))).sort(),
		[scenarios],
	);
	const customers = useMemo(
		() => Array.from(new Set(scenarios.map((s) => s.customer_id))).sort(),
		[scenarios],
	);
	const skills = useMemo(
		() => Array.from(new Set(scenarios.flatMap((s) => s.golden_skills))).sort(),
		[scenarios],
	);

	const filtered = useMemo(
		() =>
			scenarios.filter((s) => {
				if (fLayer !== ALL && !s.modes.includes(fLayer as "offline" | "llm")) return false;
				if (fType !== ALL && s.type !== fType) return false;
				if (fIntent !== ALL && (s.intent ?? "—") !== fIntent) return false;
				if (fCustomer !== ALL && s.customer_id !== fCustomer) return false;
				if (fSkill !== ALL && !s.golden_skills.includes(fSkill)) return false;
				if (fMulti === "single" && s.multi) return false;
				if (fMulti === "multi" && !s.multi) return false;
				if (fResult !== ALL) {
					const r = results[s.id];
					if (!r) return false;
					if (fResult === "pass" && !r.pass) return false;
					if (fResult === "fail" && r.pass) return false;
				}
				return true;
			}),
		[scenarios, fLayer, fType, fIntent, fCustomer, fSkill, fMulti, fResult, results],
	);

	// 筛选变化 → 回第一页
	// biome-ignore lint/correctness/useExhaustiveDependencies: 仅在筛选维度变化时重置页码
	useEffect(() => setPage(0), [fLayer, fType, fIntent, fCustomer, fSkill, fMulti, fResult]);
	const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const curPage = Math.min(page, pageCount - 1);
	const paged = filtered.slice(curPage * PAGE_SIZE, curPage * PAGE_SIZE + PAGE_SIZE);

	const allChecked = filtered.length > 0 && filtered.every((s) => sel.has(s.id));
	function toggleAll() {
		setSel((prev) => {
			const next = new Set(prev);
			if (allChecked) {
				for (const s of filtered) next.delete(s.id);
			} else {
				for (const s of filtered) next.add(s.id);
			}
			return next;
		});
	}
	function toggleOne(id: string) {
		setSel((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	}

	function runBatch(mode: "offline" | "llm") {
		const pool = sel.size ? scenarios.filter((s) => sel.has(s.id)) : filtered;
		const ids = pool.filter((s) => s.modes.includes(mode)).map((s) => s.id);
		if (!ids.length) {
			setErr(`选中范围内没有支持「${mode === "llm" ? "真 LLM" : "离线"}」的场景`);
			return;
		}
		setErr(null);
		runM.mutate(
			{ ids, mode, with_n3: false },
			{
				onSuccess: (rep) => {
					setResults((prev) => {
						const next = { ...prev };
						for (const r of rep.results) next[r.id] = r;
						return next;
					});
					setSkipped(rep.skipped);
					setLastMode(mode);
				},
				onError: (e) => setErr((e as { message?: string })?.message ?? String(e)),
			},
		);
	}

	const ranResults = filtered.map((s) => results[s.id]).filter(Boolean) as ScenarioRunResult[];
	const ranPassed = ranResults.filter((r) => r.pass).length;
	const runCount = sel.size || filtered.length;

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center justify-between">
					{ranResults.length ? (
						<span
							className={`text-sm ${ranPassed === ranResults.length ? "text-brand-success" : "text-brand-warning"}`}
						>
							已跑 {ranPassed}/{ranResults.length} 通过
							{lastMode === "llm" ? "(真 LLM,仅参考)" : ""}
						</span>
					) : null}
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4">
				<SelfcheckBar data={data} rendered={scenarios.length} />
				{/* 筛选 */}
				<div className="flex flex-wrap items-center gap-3 rounded-lg bg-surface-subtle/60 p-3">
					<Select
						label="层"
						value={fLayer}
						onChange={setFLayer}
						options={[
							{ value: ALL, label: "全部" },
							{ value: "offline", label: "离线" },
							{ value: "llm", label: "真 LLM" },
						]}
					/>
					<Select
						label="类型"
						value={fType}
						onChange={setFType}
						options={[
							{ value: ALL, label: "全部" },
							...Object.keys(data.counts.by_type).map((t) => ({ value: t, label: t })),
						]}
					/>
					<Select
						label="意图"
						value={fIntent}
						onChange={setFIntent}
						options={[
							{ value: ALL, label: "全部" },
							...intents.map((i) => ({ value: i, label: i })),
						]}
					/>
					<Select
						label="客户"
						value={fCustomer}
						onChange={setFCustomer}
						options={[
							{ value: ALL, label: "全部" },
							...customers.map((c) => ({ value: c, label: c })),
						]}
					/>
					<Select
						label="skill"
						value={fSkill}
						onChange={setFSkill}
						options={[
							{ value: ALL, label: "全部" },
							...skills.map((s) => ({ value: s, label: s })),
						]}
					/>
					<Select
						label="轮次"
						value={fMulti}
						onChange={setFMulti}
						options={[
							{ value: ALL, label: "全部" },
							{ value: "single", label: "单轮" },
							{ value: "multi", label: "多轮" },
						]}
					/>
					<Select
						label="结果"
						value={fResult}
						onChange={setFResult}
						options={[
							{ value: ALL, label: "全部" },
							{ value: "pass", label: "已过" },
							{ value: "fail", label: "未过" },
						]}
					/>
					<span className="text-xs text-text-muted">
						命中 {filtered.length} / 共 {scenarios.length}
					</span>
				</div>
				{/* 批量操作 */}
				<div className="flex flex-wrap items-center gap-3">
					<span className="text-xs text-text-secondary">
						已勾选 {sel.size} 条{sel.size ? "" : "(未勾选则跑当前筛选全部)"}
					</span>
					<Button
						type="button"
						variant="outline"
						size="sm"
						disabled={runM.isPending}
						onClick={() => runBatch("offline")}
					>
						批量跑(离线 · {runCount})
					</Button>
					<Button
						type="button"
						size="sm"
						disabled={runM.isPending || !hasKey}
						onClick={() => runBatch("llm")}
					>
						批量跑(真 LLM · {runCount})
					</Button>
					{sel.size ? (
						<Button type="button" variant="outline" size="sm" onClick={() => setSel(new Set())}>
							清空勾选
						</Button>
					) : null}
					{runM.isPending ? (
						<span className="text-sm text-text-muted">
							逐场景逐轮跑中({lastMode === "llm" ? "真 LLM,数分钟" : "离线"})…
						</span>
					) : null}
				</div>
				{err ? <p className="text-sm text-brand-danger">{err}</p> : null}
				{skipped.length ? (
					<p className="text-xs text-text-muted">
						跳过 {skipped.length} 条(不支持该层):{skipped.join("、")}
					</p>
				) : null}
				{/* 场景表 */}
				<div className="overflow-x-auto rounded-lg border border-surface-border">
					<table className="w-full text-sm">
						<thead className="bg-surface-muted text-text-secondary">
							<tr>
								<th className="w-8 px-2 py-2">
									<input
										type="checkbox"
										className="accent-brand-accent"
										checked={allChecked}
										onChange={toggleAll}
										aria-label="全选"
									/>
								</th>
								<th className="px-3 py-2 text-left font-medium">场景</th>
								<th className="px-3 py-2 text-left font-medium">类型 / 意图</th>
								<th className="px-3 py-2 text-left font-medium">客户</th>
								<th className="px-3 py-2 text-left font-medium">golden skill</th>
								<th className="px-3 py-2 text-left font-medium">结果</th>
								<th className="px-3 py-2 text-left font-medium">试跑</th>
							</tr>
						</thead>
						<tbody>
							{paged.map((s) => (
								<ScenarioRow
									key={s.id}
									s={s}
									checked={sel.has(s.id)}
									onCheck={() => toggleOne(s.id)}
									result={results[s.id]}
									onOpen={() => setDrawerId(s.id)}
									onTry={onTry}
									hasKey={hasKey}
								/>
							))}
							{!filtered.length ? (
								<tr>
									<td colSpan={7} className="px-3 py-4 text-center text-sm text-text-muted">
										当前筛选无命中场景。
									</td>
								</tr>
							) : null}
						</tbody>
					</table>
				</div>
				{/* 翻页 */}
				{filtered.length > PAGE_SIZE ? (
					<div className="flex items-center justify-end gap-3 text-sm">
						<span className="text-text-muted">
							第 {curPage + 1} / {pageCount} 页 · 共 {filtered.length} 条
						</span>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={curPage === 0}
							onClick={() => setPage(curPage - 1)}
						>
							上一页
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={curPage >= pageCount - 1}
							onClick={() => setPage(curPage + 1)}
						>
							下一页
						</Button>
					</div>
				) : null}
			</CardContent>
			{drawerId
				? (() => {
						const s = scenarios.find((x) => x.id === drawerId);
						const r = s ? results[s.id] : undefined;
						if (!s || !r) return null;
						const turns = turnViewsToDrawer(r.turns, s.turns);
						return (
							<ExecutionDrawer
								open={true}
								onClose={() => setDrawerId(null)}
								title={`${s.id} · ${s.desc}`}
								badges={
									<>
										<Pill tone={TYPE_TONE[s.type] ?? "muted"}>{s.type}</Pill>
										<Pill tone="info">{intentZh(s.intent)}</Pill>
										{r.pass ? (
											<Pill tone="success">✅ 通过</Pill>
										) : (
											<Pill tone="danger">❌ 未过</Pill>
										)}
									</>
								}
								turns={turns}
								initialTurn={0}
								customerId={s.customer_id}
							/>
						);
					})()
				: null}
		</Card>
	);
}

function ScenarioRow({
	s,
	checked,
	onCheck,
	result,
	onOpen,
	onTry,
	hasKey,
}: {
	s: Scenario;
	checked: boolean;
	onCheck: () => void;
	result?: ScenarioRunResult;
	onOpen: () => void;
	onTry: (load: ScenarioLoad) => void;
	hasKey: boolean;
}) {
	const load = scenarioToLoad(s);
	const canOpen = !!result; // 无真实结果 → 不能看执行详情
	return (
		<tr
			className={`border-t border-surface-border hover:bg-surface-muted/40 ${canOpen ? "cursor-pointer" : ""}`}
			onClick={canOpen ? onOpen : undefined}
			onKeyDown={(e) => {
				if (canOpen && (e.key === "Enter" || e.key === " ")) onOpen();
			}}
			tabIndex={canOpen ? 0 : -1}
		>
			<td className="px-2 py-2 text-center">
				<input
					type="checkbox"
					className="accent-brand-accent"
					checked={checked}
					onChange={onCheck}
					onClick={(e) => e.stopPropagation()}
					aria-label={`选 ${s.id}`}
				/>
			</td>
			<td className="px-3 py-2">
				<span className="font-mono text-xs text-text-muted">{s.id}</span>
				<span className="ml-2 text-text-primary">{s.desc}</span>
				<span className="ml-2">
					<Pill tone="muted">{KIND_LABEL[s.kind]}</Pill>
				</span>
				<span className="ml-1 text-xs text-text-muted">{s.turns.length} 轮</span>
			</td>
			<td className="px-3 py-2">
				<span className="flex flex-wrap items-center gap-1">
					<Pill tone={TYPE_TONE[s.type] ?? "muted"}>{s.type}</Pill>
					<Pill tone="info">{intentZh(s.intent)}</Pill>
				</span>
			</td>
			<td className="px-3 py-2 font-mono text-xs text-text-muted">{s.customer_id}</td>
			<td className="px-3 py-2 font-mono text-xs text-text-secondary">
				{s.golden_skills.length ? s.golden_skills.join(" → ") : "—"}
			</td>
			<td className="px-3 py-2">
				{result ? (
					result.pass ? (
						<Pill tone="success">✅ 通过</Pill>
					) : (
						<Pill tone="danger">❌ 未过</Pill>
					)
				) : (
					<Pill tone="muted">未跑</Pill>
				)}
			</td>
			<td className="whitespace-nowrap px-3 py-2">
				<Button
					type="button"
					size="sm"
					variant="outline"
					disabled={!canOpen}
					title={canOpen ? "看执行还原" : "未跑,先批量跑再看详情"}
					onClick={(e) => {
						e.stopPropagation();
						onOpen();
					}}
				>
					详情
				</Button>
				{load ? (
					<Button
						type="button"
						size="sm"
						variant="outline"
						className="ml-1"
						disabled={!hasKey}
						onClick={(e) => {
							e.stopPropagation();
							onTry(load);
						}}
					>
						试跑 →
					</Button>
				) : null}
			</td>
		</tr>
	);
}

/** TurnView(+可选场景 spec 提供 expect)→ 抽屉统一轮结构 */
function turnViewsToDrawer(turns: TurnView[], spec?: Scenario["turns"]): DrawerTurn[] {
	return turns.map((t, i) => ({
		msg: t.msg,
		resume: t.resume,
		reply: t.reply,
		stop: t.stop,
		intent: t.intent,
		committed: t.committed,
		calls: t.calls,
		terminal: t.terminal,
		clarify: t.clarify,
		node_trace: t.node_trace,
		trace: t.trace,
		timing: t.timing,
		blocks: t.blocks,
		expect: spec?.[i]?.expect,
	}));
}

/* ====================== Tab:评测(机器顾客 × 多维判分 × pass^k) ====================== */
function rewardTone(r: number): Tone {
	return r === 1 ? "success" : "danger";
}
const SOURCE_LABEL: Record<string, string> = {
	agent: "客服",
	customer: "机器顾客",
	none: "—",
};

function DimDot({ label, v }: { label: string; v: boolean | null }) {
	if (v == null) return null;
	return (
		<Pill tone={v ? "success" : "danger"}>
			{label}
			{v ? "✓" : "✗"}
		</Pill>
	);
}

/** 判分说明:把"怎么评的"摊开成常驻 legend(C6-A 判分可解释) */
function JudgeLegend() {
	return (
		<details className="rounded-lg border border-surface-border bg-surface-muted/30 p-3 text-xs text-text-secondary">
			<summary className="cursor-pointer font-medium text-text-primary">
				怎么评的?reward = action × communicate × nl(三维乘积,任一不过即 0)
			</summary>
			<ul className="mt-2 space-y-1">
				<li>
					· <b className="text-text-primary">action</b>(调度·一等):该题「期望
					skill」是否全调到、「禁用 skill」是否零触 —— 确定性比对。
				</li>
				<li>
					· <b className="text-text-primary">communicate</b>
					(必说·辅):该题「必说值」(单号/金额)是否原样出现在客服消息里 —— 子串匹配。
				</li>
				<li>
					· <b className="text-text-primary">nl</b>(合规·主力):整段对话 + 该题「合规断言」喂裁判
					LLM,逐条判真假 + 理由。
				</li>
			</ul>
			<div className="mt-1.5 text-text-muted">
				每题标准 = 该场景自带的判分契约(发起页可展开看);该题没设的维记 None、不计分。
			</div>
		</details>
	);
}

/** 一题的判分契约摊开:期望/禁用 skill · 必说值 · 合规断言全文 */
function JudgeContract({ ev }: { ev: SimScenarioEval }) {
	return (
		<div className="mt-1.5 space-y-1 rounded-md border border-dashed border-surface-border bg-surface-muted/20 p-2 text-xs">
			<div>
				<span className="text-text-muted">期望调用:</span>{" "}
				{(ev.expect_skills ?? []).join(" → ") || "—"}
			</div>
			{(ev.forbid_skills ?? []).length ? (
				<div>
					<span className="text-text-muted">禁止调用:</span> {(ev.forbid_skills ?? []).join(", ")}
				</div>
			) : null}
			{(ev.communicate_info ?? []).length ? (
				<div>
					<span className="text-text-muted">必说值:</span> {(ev.communicate_info ?? []).join(", ")}
				</div>
			) : null}
			{(ev.nl_assertions ?? []).length ? (
				<div>
					<span className="text-text-muted">合规断言:</span>
					<ul className="ml-3 list-disc">
						{(ev.nl_assertions ?? []).map((a) => (
							<li key={a}>{a}</li>
						))}
					</ul>
				</div>
			) : null}
		</div>
	);
}

/** 报告层"为什么不过"摘要:不点抽屉也能看清失败维 + 具体原因 */
function failReason(c: SimCard): string {
	const parts: string[] = [];
	if (c.dims.action === false) {
		if (c.action.missing.length) parts.push(`漏调 ${c.action.missing.join(",")}`);
		if (c.action.forbidden_called.length) parts.push(`误触 ${c.action.forbidden_called.join(",")}`);
	}
	if (c.dims.nl === false) {
		const bad = c.nl.results.find((r) => !r.met);
		if (bad) parts.push(`合规✗:${bad.reason || bad.assertion}`);
	}
	if (c.dims.communicate === false) {
		const miss = c.communicate.items.filter((i) => !i.said).map((i) => i.value);
		if (miss.length) parts.push(`漏说 ${miss.join(",")}`);
	}
	return parts.join(" · ");
}

function EvalTab({ hasKey }: { hasKey: boolean }) {
	const [sub, setSub] = useState<"run" | "report" | "compare">("run");
	const [report, setReport] = useState<SimRunReport | null>(null);
	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex flex-wrap items-center gap-3">
					<div className="flex gap-1 rounded-full border border-surface-border p-0.5">
						<SubTabBtn active={sub === "run"} onClick={() => setSub("run")}>
							发起
						</SubTabBtn>
						<SubTabBtn active={sub === "report"} onClick={() => setSub("report")}>
							本次结果
						</SubTabBtn>
						<SubTabBtn active={sub === "compare"} onClick={() => setSub("compare")}>
							对比
						</SubTabBtn>
					</div>
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<JudgeLegend />
				{sub === "run" ? (
					<EvalRunPanel
						hasKey={hasKey}
						onDone={(r) => {
							setReport(r);
							setSub("report");
						}}
					/>
				) : null}
				{sub === "report" ? <EvalReportPanel report={report} /> : null}
				{sub === "compare" ? (
					<div className="text-sm text-text-muted">
						版本对比 / 门禁 —— 两个评测批次逐题 diff、退化高亮、门禁结论。MVP 预留(后续 M5 接入)。
					</div>
				) : null}
			</CardContent>
		</Card>
	);
}

/* ============ Tab:评测记录(批次独立筛选+分页 · 详情走弹窗复用报告体/评分卡) ============ */
const RECORDS_PAGE = 20;

/** 批次报告弹窗:复用「本次结果」的 EvalReportBody;点场景再开评分卡 EvalDrawer(和之前一样) */
/** 批次里对未满足(reward≠1.0)的题逐个「发起分析」→ 落归因记录 */
function RcaLaunchPanel({ runId, report }: { runId: string; report: SimRunReport }) {
	const run = useRunRca();
	const [done, setDone] = useState<Record<string, "ok" | "err">>({});
	const [busy, setBusy] = useState<string | null>(null);
	const [runningAll, setRunningAll] = useState(false);
	const failed = (report.detail ?? [])
		.map((d) => ({ sid: d.scenario_id, fi: d.cards.findIndex((c) => c.reward !== 1.0) }))
		.filter((x) => x.fi >= 0);
	if (!failed.length) return null;
	async function analyze(sid: string, fi: number) {
		setBusy(sid);
		try {
			await run.mutateAsync({ run_id: runId, scenario_id: sid, trial_index: fi });
			setDone((m) => ({ ...m, [sid]: "ok" }));
			return true;
		} catch {
			setDone((m) => ({ ...m, [sid]: "err" }));
			return false;
		} finally {
			setBusy(null);
		}
	}
	async function analyzeAll() {
		setRunningAll(true);
		try {
			for (const f of failed) {
				if (done[f.sid] === "ok") continue;
				await analyze(f.sid, f.fi); // 逐题串行,单题失败不阻断后续
			}
		} finally {
			setRunningAll(false);
		}
	}
	return (
		<div className="mb-3 rounded-lg border border-surface-border bg-surface-subtle/50 p-2.5">
			<div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-text-primary">
				根因分析 · {failed.length} 题未满足
				<span className="font-normal text-text-muted">
					对失败题跑 5 簇+综合归因,结果进「归因记录」(耗 token)
				</span>
				<Button
					type="button"
					size="sm"
					variant="outline"
					disabled={runningAll || busy != null}
					onClick={analyzeAll}
				>
					{runningAll
						? `全部分析中…(${Object.values(done).filter((x) => x === "ok").length}/${failed.length})`
						: "全部发起"}
				</Button>
			</div>
			<div className="flex flex-wrap gap-1.5">
				{failed.map((f) => (
					<button
						key={f.sid}
						type="button"
						disabled={busy === f.sid || done[f.sid] === "ok"}
						onClick={() => analyze(f.sid, f.fi)}
						className="rounded border border-surface-border bg-surface-card px-2 py-1 text-xs hover:bg-surface-subtle disabled:opacity-50"
					>
						{busy === f.sid
							? `分析中…${f.sid}`
							: done[f.sid] === "ok"
								? `✓ ${f.sid}`
								: done[f.sid] === "err"
									? `✗ ${f.sid}`
									: `发起分析 · ${f.sid}`}
					</button>
				))}
			</div>
		</div>
	);
}

function BatchReportDrawer({
	runId,
	summary,
	onClose,
	onDrill,
}: {
	runId: string | null;
	summary?: { ts: string; persona: string | null; n_scenarios: number };
	onClose: () => void;
	onDrill: (c: SimCard) => void;
}) {
	const loaded = useSimRun(runId);
	return (
		<DrawerPrimitive.Root open={!!runId} onOpenChange={(o) => !o && onClose()}>
			<DrawerPrimitive.Portal>
				<DrawerPrimitive.Overlay className="ui-overlay fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
				<DrawerPrimitive.Content className="ui-drawer fixed right-0 top-0 z-50 flex h-full w-[min(1240px,95vw)] flex-col border-l border-surface-border bg-surface-background shadow-xl focus:outline-none">
					<header className="flex flex-wrap items-center gap-2 border-b border-surface-border px-4 py-3">
						<DrawerPrimitive.Title className="text-sm font-semibold text-text-primary">
							批次报告
						</DrawerPrimitive.Title>
						{summary ? <Pill tone="info">{summary.ts.slice(5, 16).replace("T", " ")}</Pill> : null}
						{summary ? <Pill tone="muted">性格 {summary.persona ?? "随机"}</Pill> : null}
						{summary && loaded.data ? (
							<Pill tone="muted">
								{summary.n_scenarios} 题 × {loaded.data.trials}
							</Pill>
						) : null}
						<DrawerPrimitive.Close
							className="ml-auto rounded-md p-1 text-text-muted hover:bg-surface-muted"
							aria-label="关闭"
						>
							<X className="h-4 w-4" />
						</DrawerPrimitive.Close>
					</header>
					<div className="flex-1 overflow-y-auto p-4">
						{loaded.data ? (
							<>
								{runId ? <RcaLaunchPanel runId={runId} report={loaded.data} /> : null}
								<EvalReportBody report={loaded.data} onDrill={onDrill} />
							</>
						) : (
							<div className="text-sm text-text-muted">加载批次…</div>
						)}
					</div>
				</DrawerPrimitive.Content>
			</DrawerPrimitive.Portal>
		</DrawerPrimitive.Root>
	);
}

function EvalRecordsTab() {
	const runs = useSimRuns();
	const all = runs.data?.runs ?? [];
	const [fPersona, setFPersona] = useState("");
	const [fTrials, setFTrials] = useState("");
	const [fMinReward, setFMinReward] = useState("");
	const [sel, setSel] = useState<string | null>(null);
	const [drill, setDrill] = useState<SimCard | null>(null);
	const [page, setPage] = useState(0);
	const shown = all.filter(
		(r) =>
			(!fPersona || (r.persona ?? "") === fPersona) &&
			(!fTrials || String(r.trials) === fTrials) &&
			(!fMinReward || (r.aggregate.avg_reward ?? 0) >= Number(fMinReward)),
	);
	const selRow = all.find((r) => r.id === sel);
	const maxPage = Math.max(0, Math.ceil(shown.length / RECORDS_PAGE) - 1);
	const p = Math.min(page, maxPage);
	const pageItems = shown.slice(p * RECORDS_PAGE, p * RECORDS_PAGE + RECORDS_PAGE);
	const pct = (v: number | null) => (v == null ? "—" : `${Math.round(v * 100)}%`);
	return (
		<Card>
			<CardHeader>
				<CardTitle>共 {all.length} 批</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<p className="text-xs text-text-muted">
					每次「发起评测」跑批的独立记录(与单轮「运行明细」分离)。筛选后点一行看该批次完整报告(逐题评分卡
					+ 轨迹钻取)。
				</p>
				<div className="flex flex-wrap items-center gap-3 text-xs">
					<FilterSel
						label="性格"
						value={fPersona}
						onChange={(v) => {
							setFPersona(v);
							setPage(0);
						}}
						opts={uniqStr(all.map((r) => r.persona ?? ""))}
					/>
					<FilterSel
						label="次数"
						value={fTrials}
						onChange={(v) => {
							setFTrials(v);
							setPage(0);
						}}
						opts={uniqStr(all.map((r) => String(r.trials)))}
					/>
					<label className="flex items-center gap-1">
						平均分≥
						<input
							type="number"
							min={0}
							max={1}
							step={0.1}
							value={fMinReward}
							onChange={(e) => {
								setFMinReward(e.target.value);
								setPage(0);
							}}
							className="w-16 rounded border border-surface-border px-1.5 py-1"
						/>
					</label>
					<span className="self-center text-text-muted">共 {shown.length} 批</span>
				</div>
				<div className="overflow-x-auto rounded-lg border border-surface-border">
					<table className="w-full text-left text-sm">
						<thead>
							<tr className="border-b border-surface-border text-xs text-text-muted">
								<th className="px-2 py-1.5">时间</th>
								<th className="px-2 py-1.5">场景</th>
								<th className="px-2 py-1.5">次数</th>
								<th className="px-2 py-1.5">性格</th>
								<th className="px-2 py-1.5">平均分</th>
								<th className="px-2 py-1.5">action</th>
								<th className="px-2 py-1.5">communicate</th>
								<th className="px-2 py-1.5">nl</th>
								<th className="px-2 py-1.5">pass^k</th>
								<th className="px-2 py-1.5">指南</th>
								<th className="px-2 py-1.5">CX</th>
							</tr>
						</thead>
						<tbody>
							{pageItems.map((r) => (
								<tr
									key={r.id}
									onClick={() => setSel(r.id)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") setSel(r.id);
									}}
									tabIndex={0}
									className={`cursor-pointer border-b border-surface-border/60 hover:bg-surface-muted/40 ${sel === r.id ? "bg-brand-primary/5" : ""}`}
								>
									<td className="px-2 py-1.5">{r.ts.slice(5, 16).replace("T", " ")}</td>
									<td className="px-2 py-1.5">{r.n_scenarios} 题</td>
									<td className="px-2 py-1.5">{r.trials}</td>
									<td className="px-2 py-1.5">{r.persona ?? "随机"}</td>
									<td className="px-2 py-1.5 font-medium text-text-primary">
										{r.aggregate.avg_reward ?? "—"}
									</td>
									<td className="px-2 py-1.5">{pct(r.aggregate.dim_pass.action)}</td>
									<td className="px-2 py-1.5">{pct(r.aggregate.dim_pass.communicate)}</td>
									<td className="px-2 py-1.5">{pct(r.aggregate.dim_pass.nl)}</td>
									<td className="px-2 py-1.5 text-xs text-text-muted">
										{Object.entries(r.pass_k_curve)
											.map(([k, v]) => `k${k}:${v}`)
											.join(" ")}
									</td>
									<td className="px-2 py-1.5 text-xs">
										{r.baseline_of ? (
											<Pill tone="muted">对照批</Pill>
										) : r.guidance_summary ? (
											<span
												title={`指南断言遵循 ${r.guidance_summary.verdicts_met}/${r.guidance_summary.verdicts_total};召回缺失 ${r.guidance_summary.recall_misses.length}`}
											>
												<Pill
													tone={
														r.guidance_summary.recall_misses.length
															? "warning"
															: r.guidance_summary.verdicts_met < r.guidance_summary.verdicts_total
																? "info"
																: "success"
													}
												>
													{r.guidance_summary.verdicts_met}/{r.guidance_summary.verdicts_total}
													{r.guidance_summary.recall_misses.length ? " ⚠" : ""}
												</Pill>
											</span>
										) : (
											<span className="text-text-muted">—</span>
										)}
									</td>
									<td className="px-2 py-1.5 text-xs">
										{r.cx_positive == null ? (
											<span className="text-text-muted">—</span>
										) : (
											<Pill
												tone={
													r.cx_positive >= 0.8
														? "success"
														: r.cx_positive >= 0.5
															? "info"
															: "danger"
												}
											>
												{Math.round(r.cx_positive * 100)}%
											</Pill>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
					{shown.length === 0 ? (
						<div className="p-3 text-center text-xs text-text-muted">
							{all.length === 0 ? "暂无评测批次,去「发起评测」跑一批" : "当前筛选无批次"}
						</div>
					) : null}
				</div>
				{shown.length > RECORDS_PAGE ? (
					<div className="flex items-center justify-end gap-2 text-xs text-text-muted">
						<span>
							第 {p + 1} / {maxPage + 1} 页 · 共 {shown.length} 批
						</span>
						<button
							type="button"
							onClick={() => setPage(Math.max(0, p - 1))}
							disabled={p <= 0}
							className="rounded border border-surface-border px-2 py-1 hover:bg-surface-muted disabled:opacity-40"
						>
							上一页
						</button>
						<button
							type="button"
							onClick={() => setPage(Math.min(maxPage, p + 1))}
							disabled={p >= maxPage}
							className="rounded border border-surface-border px-2 py-1 hover:bg-surface-muted disabled:opacity-40"
						>
							下一页
						</button>
					</div>
				) : null}
				{/* 详情:先看批次汇总报告(弹窗复用报告体),点场景再开评分卡弹窗(与「本次结果」一致) */}
				<BatchReportDrawer
					runId={sel}
					summary={selRow}
					onClose={() => setSel(null)}
					onDrill={setDrill}
				/>
				<EvalDrawer card={drill} onClose={() => setDrill(null)} />
			</CardContent>
		</Card>
	);
}

/* ============ Tab:归因记录(根因分析结论 · 独立筛选 · 详情抽屉)============ */
const RCA_CLUSTER_TONE: Record<string, Tone> = {
	评测侧: "muted",
	支架: "info",
	主干: "warning",
	结构: "danger",
	非缺陷: "success",
};

function RcaRecordsTab() {
	const q = useRcaRecords();
	const all = q.data?.records ?? [];
	const [fCluster, setFCluster] = useState("");
	const [fReview, setFReview] = useState("");
	const [sel, setSel] = useState<string | null>(null);
	const shown = all.filter(
		(r) =>
			(!fCluster || (r.cluster ?? "") === fCluster) &&
			(!fReview || (fReview === "需复核" ? r.needs_human_review : !r.needs_human_review)),
	);
	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	return (
		<Card>
			<CardHeader>
				<CardTitle>共 {all.length} 条</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<p className="text-xs text-text-muted">
					每次「发起分析」对一张失败卡产出的根因结论(5 簇假设 + 综合裁决)。
					<b>需复核</b> = 判官不确定或簇间冲突,别全信,点开看排除说明与证据。
				</p>
				<div className="flex flex-wrap items-center gap-3 text-xs">
					<FilterSel
						label="簇"
						value={fCluster}
						onChange={setFCluster}
						opts={uniqStr(all.map((r) => r.cluster ?? ""))}
					/>
					<FilterSel label="复核" value={fReview} onChange={setFReview} opts={["需复核", "无需"]} />
					<span className="self-center text-text-muted">共 {shown.length} 条</span>
				</div>
				<div className="overflow-x-auto rounded-lg border border-surface-border">
					<table className="w-full text-left text-sm">
						<thead>
							<tr className="border-b border-surface-border text-xs text-text-muted">
								<th className="px-2 py-1.5">时间</th>
								<th className="px-2 py-1.5">场景</th>
								<th className="px-2 py-1.5">主归因</th>
								<th className="px-2 py-1.5">一句话主因</th>
								<th className="px-2 py-1.5">簇</th>
								<th className="px-2 py-1.5">节点</th>
								<th className="px-2 py-1.5">置信</th>
								<th className="px-2 py-1.5">复核</th>
							</tr>
						</thead>
						<tbody>
							{shown.map((r) => (
								<tr
									key={r.id}
									onClick={() => setSel(r.id)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") setSel(r.id);
									}}
									tabIndex={0}
									className={`cursor-pointer border-b border-surface-border/60 hover:bg-surface-subtle ${sel === r.id ? "bg-brand-accent/5" : ""}`}
								>
									<td className="px-2 py-1.5">{r.ts.slice(5, 16).replace("T", " ")}</td>
									<td className="px-2 py-1.5">{r.scenario_id}</td>
									<td className="px-2 py-1.5 font-medium text-text-primary">
										{r.primary_label ?? "—"}
									</td>
									<td
										className="max-w-[320px] truncate px-2 py-1.5 text-xs text-text-secondary"
										title={r.primary_cause_statement ?? ""}
									>
										{r.primary_cause_statement ?? "—"}
									</td>
									<td className="px-2 py-1.5">
										<Pill tone={RCA_CLUSTER_TONE[r.cluster ?? ""] ?? "muted"}>
											{r.cluster ?? "—"}
										</Pill>
									</td>
									<td className="px-2 py-1.5 text-xs text-text-muted">{r.node_locus ?? "—"}</td>
									<td className="px-2 py-1.5">
										{r.confidence == null ? "—" : `${Math.round(r.confidence * 100)}%`}
									</td>
									<td className="px-2 py-1.5">
										{r.needs_human_review ? <Pill tone="warning">需复核</Pill> : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
					{shown.length === 0 ? (
						<div className="p-3 text-center text-xs text-text-muted">
							{all.length === 0 ? "暂无归因记录,去「评测记录」对失败卡发起分析" : "当前筛选无记录"}
						</div>
					) : null}
				</div>
				<RcaRecordDrawer recId={sel} onClose={() => setSel(null)} />
			</CardContent>
		</Card>
	);
}

/** 证据路径解析:把 "score.action.missing" 这类路径按段取到 evidence_digest 里的实际值。
 *  digest 只存 scenario/score/judge/related_data(process/transcript 不入库),解析不到就只显路径。 */
function resolveEvidenceRef(
	digest: Record<string, unknown> | undefined,
	ref: string,
): string | null {
	if (!digest) return null;
	let cur: unknown = digest;
	for (const seg of ref.split(".")) {
		const m = /^([^[\]]+)((?:\[\d+\])*)$/.exec(seg.trim());
		const key = m?.[1];
		if (!key || cur == null || typeof cur !== "object" || Array.isArray(cur)) return null;
		cur = (cur as Record<string, unknown>)[key];
		for (const idx of (m?.[2] ?? "").matchAll(/\[(\d+)\]/g)) {
			if (!Array.isArray(cur)) return null;
			cur = cur[Number(idx[1])];
		}
	}
	if (cur == null) return null;
	const s = typeof cur === "string" ? cur : JSON.stringify(cur);
	return s.length > 400 ? `${s.slice(0, 400)}…` : s;
}

/** 支撑依据:优先 evidence_quotes(路径+原文摘录+为何支撑);老记录(rca-v1)只有
 *  evidence_refs 时,尽力从 evidence_digest 解析出字段实际内容一起显。 */
function RcaQuotesBlock({
	quotes,
	refs,
	digest,
}: {
	quotes?: RcaEvidenceQuote[];
	refs?: string[];
	digest?: Record<string, unknown>;
}) {
	if (quotes?.length) {
		return (
			<div className="space-y-1.5">
				{quotes.map((qt, i) => (
					<div
						key={`${qt.ref ?? ""}#${i}`}
						className="rounded-md border border-surface-border bg-surface-card p-2 text-xs"
					>
						{qt.ref ? <code className="font-mono text-brand-primary">{qt.ref}</code> : null}
						{qt.quote ? (
							<div className="mt-1 whitespace-pre-wrap rounded bg-surface-muted/50 p-1.5 font-mono text-[11px] leading-4 text-text-secondary">
								{qt.quote}
							</div>
						) : null}
						{qt.why ? <div className="mt-1 text-text-muted">→ {qt.why}</div> : null}
					</div>
				))}
			</div>
		);
	}
	if (refs?.length) {
		return (
			<div className="space-y-1.5">
				{refs.map((ref) => {
					const val = resolveEvidenceRef(digest, ref);
					return (
						<div
							key={ref}
							className="rounded-md border border-surface-border bg-surface-card p-2 text-xs"
						>
							<code className="font-mono text-brand-primary">{ref}</code>
							{val ? (
								<div className="mt-1 whitespace-pre-wrap rounded bg-surface-muted/50 p-1.5 font-mono text-[11px] leading-4 text-text-secondary">
									{val}
								</div>
							) : null}
						</div>
					);
				})}
			</div>
		);
	}
	return <div className="text-xs text-text-muted">(无证据引用)</div>;
}

function RcaRecordDrawer({ recId, onClose }: { recId: string | null; onClose: () => void }) {
	const q = useRcaRecord(recId);
	const r = q.data;
	// 「查看当时还原」:按 run_id 拉当时评测批次,取该 trial 评分卡,复用 EvalDrawer(评分卡 + 执行还原)
	const [replayOpen, setReplayOpen] = useState(false);
	const runQ = useSimRun(replayOpen && r ? r.run_id : null);
	const replayHit =
		replayOpen && r && runQ.data
			? (runQ.data.detail ?? []).find((d) => d.scenario_id === r.scenario_id)?.cards?.[
					r.trial_index
				]
			: undefined;
	const replayCard = replayHit ?? null;
	const v = r?.verdict;
	return (
		<DrawerPrimitive.Root open={!!recId} onOpenChange={(o) => !o && onClose()}>
			<DrawerPrimitive.Portal>
				<DrawerPrimitive.Overlay className="ui-overlay fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
				<DrawerPrimitive.Content className="ui-drawer fixed right-0 top-0 z-50 flex h-full w-[min(960px,95vw)] flex-col overflow-y-auto border-l border-surface-border bg-surface-background shadow-xl focus:outline-none">
					<header className="flex flex-wrap items-center gap-2 border-b border-surface-border px-4 py-3">
						<DrawerPrimitive.Title className="text-sm font-semibold text-text-primary">
							根因分析 · {r?.scenario_id ?? ""}
						</DrawerPrimitive.Title>
						{v ? <Pill tone={RCA_CLUSTER_TONE[v.cluster] ?? "muted"}>{v.cluster}</Pill> : null}
						{v?.needs_human_review ? <Pill tone="warning">需复核</Pill> : null}
						{r ? (
							<Button
								type="button"
								size="sm"
								variant="outline"
								onClick={() => setReplayOpen(true)}
								disabled={replayOpen && runQ.isLoading}
							>
								{replayOpen && runQ.isLoading ? "加载当时批次…" : "查看当时还原"}
							</Button>
						) : null}
						{replayOpen && runQ.isError ? (
							<span className="text-xs text-text-muted">
								当时批次已被清理(批次库封顶保留),无法还原
							</span>
						) : null}
						{replayOpen && runQ.data && !replayCard ? (
							<span className="text-xs text-text-muted">当时批次里没找到该 trial,无法还原</span>
						) : null}
						<DrawerPrimitive.Close
							className="ml-auto rounded-md p-1 text-text-muted hover:bg-surface-muted"
							aria-label="关闭"
						>
							<X className="h-4 w-4" />
						</DrawerPrimitive.Close>
					</header>
					<div className="flex-1 space-y-3 p-4 text-sm">
						{!r || !v ? (
							<div className="text-text-muted">加载中…</div>
						) : (
							<>
								{/* ① 第一性主因:唯一主归因 + 一句人话机制 + 根因链 */}
								<div className="rounded-md bg-surface-subtle/60 p-3">
									<div className="mb-1 text-[11px] font-bold text-text-secondary">
										第一性主因(综合裁决 · 唯一主归因)
									</div>
									<div className="flex flex-wrap items-center gap-2">
										<span className="text-base font-semibold text-text-primary">
											{v.primary_label}
										</span>
										<Pill tone={RCA_CLUSTER_TONE[v.cluster] ?? "muted"}>{v.cluster}</Pill>
										{v.node_locus ? <Pill tone="muted">{v.node_locus}</Pill> : null}
										{v.primary_label === "指南侧" ? (
											<a
												href="#guidance"
												className="text-xs text-brand-primary hover:underline"
												title="归因到商家配置:去应答指南页改文本/scope/环节即可修复,不用改代码"
											>
												去改指南配置 →
											</a>
										) : null}
										<span className="text-xs text-text-muted">
											置信 {Math.round((v.confidence ?? 0) * 100)}%
										</span>
									</div>
									{v.calibration ? (
										<div className="mt-1.5 text-[11px] text-text-muted">
											⚠ 已确定性校准:{(v.calibration.tested_yes ?? []).join("/")}
											多簇同时成立 → 强制需复核,置信{" "}
											{Math.round((v.calibration.original_confidence ?? 0) * 100)}%→
											{Math.round((v.confidence ?? 0) * 100)}%(判官自报值不作数)
										</div>
									) : null}
									{v.primary_cause_statement ? (
										<div className="mt-2">
											<AiCard>{v.primary_cause_statement}</AiCard>
										</div>
									) : null}
									{v.root_cause_chain?.length ? (
										<ol className="mt-2 space-y-1">
											{v.root_cause_chain.map((step, i) => (
												<li
													key={step}
													className="flex gap-2 text-xs"
													style={{ paddingLeft: `${i * 14}px` }}
												>
													<span className="shrink-0 font-mono text-text-muted">
														{i === 0 ? "●" : "└"}
													</span>
													<span className="text-text-secondary">{step}</span>
												</li>
											))}
										</ol>
									) : null}
									{v.secondary_labels?.length ? (
										<div className="mt-2 text-xs text-text-muted">
											次要因(非根,不在此修):{v.secondary_labels.join(" · ")}
										</div>
									) : null}
								</div>
								{/* ② 支撑依据:证据引文(路径+原文+为何支撑) */}
								<div>
									<div className="mb-1 text-[11px] font-bold text-text-secondary">
										支撑依据(证据引文)
									</div>
									<RcaQuotesBlock
										quotes={v.evidence_quotes}
										refs={v.evidence_refs}
										digest={r.evidence_digest}
									/>
								</div>
								{/* ③ 排除说明 + 处置方向 */}
								<div className="rounded-md border border-surface-border p-3 text-xs">
									<div className="mb-1">
										<b>为什么不是上层(排除链,按 评测侧→非缺陷→数据→能力→注入→调度→提示词→结构):</b>
									</div>
									<p className="leading-5 text-text-secondary">{v.excluded_reasoning || "—"}</p>
									<div className="mt-2">
										<b>处置方向(数据&gt;能力&gt;结构&gt;提示词,标注是否触脊柱):</b>
									</div>
									<p className="leading-5 text-text-secondary">{v.fix_direction || "—"}</p>
								</div>
								{/* ④ 5 簇假设(判官原始结论,可展开看依据) */}
								<div>
									<div className="mb-1 text-[11px] font-bold text-text-secondary">
										5 簇假设(各簇判官独立结论 · 点开看依据)
									</div>
									<div className="space-y-1">
										{r.group_hypotheses.map((h) => (
											<details
												key={h.cluster}
												className="rounded border border-surface-border px-2 py-1 text-xs"
											>
												<summary className="flex cursor-pointer flex-wrap items-center gap-2">
													<Pill tone={RCA_CLUSTER_TONE[h.cluster] ?? "muted"}>{h.cluster}</Pill>
													<Pill
														tone={
															h.verdict === "yes"
																? "success"
																: h.verdict === "maybe"
																	? "warning"
																	: "muted"
														}
													>
														{h.verdict}
													</Pill>
													{h.sub_label && h.sub_label !== "无" ? (
														<span className="text-text-secondary">{h.sub_label}</span>
													) : null}
													{h.node_locus && h.node_locus !== "—" ? (
														<Pill tone="muted">{h.node_locus}</Pill>
													) : null}
													<span className="text-text-muted">
														{Math.round((h.confidence ?? 0) * 100)}%
													</span>
													<span className="min-w-0 flex-1 truncate text-text-muted">
														{h.reason}
													</span>
												</summary>
												<div className="mt-1.5 space-y-1.5 border-t border-surface-border/60 pt-1.5">
													{h.reason ? (
														<p className="leading-5 text-text-secondary">{h.reason}</p>
													) : null}
													<RcaQuotesBlock
														quotes={h.evidence_quotes}
														refs={h.evidence_refs}
														digest={r.evidence_digest}
													/>
												</div>
											</details>
										))}
									</div>
								</div>
								{/* ⑤ 元信息 */}
								<div className="text-[11px] text-text-muted">
									批次 {r.run_id} · trial #{r.trial_index} · reward {r.reward} · 模型{" "}
									{r.model ?? "—"} · 提示词版本 {r.prompt_version ?? "—"}
								</div>
							</>
						)}
					</div>
					<EvalDrawer card={replayCard} onClose={() => setReplayOpen(false)} />
				</DrawerPrimitive.Content>
			</DrawerPrimitive.Portal>
		</DrawerPrimitive.Root>
	);
}

/* ============ Tab:归因标准与提示词(标准/字段字典只读 · 模型/提示词可配置)============ */
/** 分析模型下拉:限连通性验证过的 4 款(GPT 高/低 + Gemini 高/低),切换运行时生效不走部署 */
/** 统一按节点模型切换器 · 所有大模型节点共用;切换走配置即时生效,成功 toast 提示。 */
function NodeModelSelector({ node }: { node: string }) {
	const q = useNodeModels();
	const setModel = useSetNodeModel();
	const { toast } = useToast();
	const info = q.data?.nodes.find((n) => n.key === node);
	const options = q.data?.options ?? [];
	if (!info || !options.length) return null;
	const inList = options.some((o) => o.key === info.model);
	return (
		<div className="flex flex-wrap items-center gap-2 rounded-md bg-surface-subtle/60 p-2.5 text-xs">
			<span className="font-medium text-text-primary">模型</span>
			<StyledSelect
				value={info.model}
				disabled={setModel.isPending}
				onChange={(e) =>
					setModel.mutate(
						{ node, model: e.target.value },
						{
							onSuccess: (d) => toast(`${info.label} 已切换 → ${d.model},即时生效`, "success"),
							onError: () => toast(`${info.label} 切换失败`, "error"),
						},
					)
				}
			>
				{!inList ? <option value={info.model}>当前 · {info.model}</option> : null}
				{options.map((o) => (
					<option key={o.key} value={o.key}>
						{o.label}
					</option>
				))}
			</StyledSelect>
			{setModel.isPending ? <span className="text-text-muted">切换中…</span> : null}
			<span className="text-text-muted">
				{info.overridden ? `已覆盖(env 默认 ${info.env_default})` : "env 默认"} · 切换即生效不走部署
			</span>
		</div>
	);
}

/** 契约-数据一致性体检:确定性零 LLM,抓「契约要求播报但数据无出处」的陷阱题(只登记不擅改) */
function RcaContractLintCard() {
	const [on, setOn] = useState(false);
	const q = useRcaContractLint(on);
	const d = q.data;
	return (
		<Card>
			<CardHeader>
				<CardTitle>契约-数据一致性体检(确定性 · 零 LLM)</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2">
				<p className="text-xs text-text-muted">
					静态扫全题库:判分契约(communicate_info)要求播报、但该题关联数据里找不到出处的值;以及
					data_refs 引用不属该客户的订单。命中≠一定是坏题——金额类可能是「待
					calculate」的可推导值,查无此单/越权可能是故意设计——
					<b>只登记,由人分类处置(修契约成诚实或标 known_issue,不改 agent 迁就题目)</b>。
				</p>
				<Button type="button" size="sm" variant="outline" onClick={() => setOn(true)}>
					{q.isFetching ? "体检中…" : d ? "重新体检" : "跑体检"}
				</Button>
				{d ? (
					<>
						<div className="text-xs text-text-secondary">
							共查 {d.checked} 题,发现 <b>{d.findings.length}</b> 题存在契约-数据矛盾
						</div>
						<div className="overflow-x-auto rounded-lg border border-surface-border">
							<table className="w-full text-left text-xs">
								<thead>
									<tr className="border-b border-surface-border text-text-muted">
										<th className="px-2 py-1.5">场景</th>
										<th className="px-2 py-1.5">契约要求但数据无出处的值</th>
										<th className="px-2 py-1.5">外引订单</th>
										<th className="px-2 py-1.5">known_issue</th>
									</tr>
								</thead>
								<tbody>
									{d.findings.map((f) => (
										<tr key={f.scenario_id} className="border-b border-surface-border/60">
											<td className="px-2 py-1.5 font-medium text-text-primary">{f.scenario_id}</td>
											<td className="px-2 py-1.5 text-text-secondary">
												{f.missing_communicate_values.join(" · ") || "—"}
											</td>
											<td className="px-2 py-1.5 text-text-secondary">
												{f.foreign_data_refs.join(" · ") || "—"}
											</td>
											<td className="px-2 py-1.5">
												{f.known_issue ? (
													<Pill tone="muted">已标</Pill>
												) : (
													<Pill tone="warning">未标</Pill>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</>
				) : null}
			</CardContent>
		</Card>
	);
}

/* ── 「环节 IO 契约」页(v2 布局重构)─────────────────────────────────────────
 * F0:「整体的信息布局不清楚,机会点台账实施计划这些都是乱的;最核心的应该是
 * 每个节点的输入输出字段及格式」。
 * 重构口径(follow 本页既有标准,不自创):
 *  - 子页签走既有 `SubTabBtn` 胶囊组(与「指标统计」「评测记录」同款);
 *  - 字段文档走既有 SkillIO 范式:**可折叠**,收起一行看清骨架,展开才看字段表;
 *  - **「字段契约」为默认页签**——它是本页主体,其余(诊断覆盖/机会点/实施计划)
 *    是围绕它的进度与线索,不该和它平铺在一起。
 */
type IoSub = "fields" | "coverage" | "opportunity" | "roadmap";
const IO_SUBS: { key: IoSub; label: string }[] = [
	{ key: "fields", label: "一致性体检" },
	{ key: "coverage", label: "诊断覆盖" },
	{ key: "opportunity", label: "机会点" },
	{ key: "roadmap", label: "实施计划" },
];

/** 跳详情页(hash 路由)。体检页只说「哪里不一致」,字段规格在各自页面 —— 一键过去,别让人自己找。 */
function GotoBtn({ hash, label }: { hash: string; label: string }) {
	if (!hash) return null;
	return (
		<button
			type="button"
			onClick={() => {
				window.location.hash = hash;
			}}
			className="rounded-full border border-surface-border px-2 py-0.5 text-[11px] text-text-secondary transition-colors duration-150 hover:border-brand-accent hover:bg-brand-accent/5 hover:text-text-primary"
		>
			{label} →
		</button>
	);
}

function FaceCount({ label, n, hint }: { label: string; n: number; hint: string }) {
	return (
		<span
			title={hint}
			className="rounded-full border border-surface-border px-2 py-0.5 font-mono text-[11px] text-text-muted"
		>
			{label} {n}
		</span>
	);
}

/** 单个环节:收起=一行骨架(名字/域/输入输出字段数/四面状态),展开=字段表。 */
function IoStageCard({ stage }: { stage: IoStage }) {
	const [open, setOpen] = useState(false);
	const f = stage.faces;
	const real = stage.findings.filter((x) => x.level !== "登记");
	return (
		<div className="rounded-lg border border-surface-border">
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left"
			>
				<span className="text-sm font-medium text-text-primary">{stage.label}</span>
				<Pill tone="muted">{stage.domain}</Pill>
				<span className="font-mono text-[11px] text-text-muted">{stage.site}</span>
				<GotoBtn hash={stage.detail_hash} label={stage.detail_label} />
				<span className="ml-auto flex flex-wrap items-center gap-1.5">
					<Pill tone="info">入 {stage.inputs.length}</Pill>
					<Pill tone="info">出 {stage.outputs.length}</Pill>
					{real.length ? (
						<Pill tone="warning">{real.length} 项待处理</Pill>
					) : (
						<Pill tone="success">四面对齐</Pill>
					)}
					<span className="text-xs text-text-muted">{open ? "收起" : "展开"}</span>
				</span>
			</button>
			{open ? (
				<div className="space-y-3 border-t border-surface-border px-4 py-3">
					<div className="flex flex-wrap items-center gap-1.5">
						<FaceCount label="声明" n={f.declared.length} hint="声明面 D:注册表登记了几个变量" />
						<FaceCount label="注入" n={f.rendered.length} hint="注入面 I:模板里真写了 {{}} 的" />
						<FaceCount label="产出" n={f.supplied.length} hint="产出面 P:调用点真提供的键" />
					</div>
					{stage.note ? <p className="text-xs text-text-secondary">{stage.note}</p> : null}

					<p className="text-xs text-text-muted">
						字段规格已归位:LLM 节点看「应答提示词 / 评测提示词 / 归因标准」页,Skill 看「SKILL」页,
						规则看「规则」页。本页只做跨页一致性体检,不再重复展示字段表。
					</p>

					{stage.post_rules.length ? (
						<p className="text-xs text-text-muted">
							输出后经确定性规则:
							{stage.post_rules.map((r) => (
								<span key={r} className="ml-1.5 inline-flex">
									<GotoBtn hash="reference-rules" label={r} />
								</span>
							))}
						</p>
					) : null}

					{stage.findings.length ? (
						<div className="space-y-1.5 rounded-md border border-surface-border bg-surface-subtle p-2">
							{stage.findings.map((x) => (
								<p key={`${x.kind}-${x.field}`} className="text-xs">
									<Pill tone={x.level === "登记" ? "muted" : "warning"}>{x.kind}</Pill>
									<span className="ml-1.5 font-mono">{x.field}</span>
									<span className="ml-1.5 text-text-secondary">{x.detail}</span>
								</p>
							))}
						</div>
					) : null}
				</div>
			) : null}
		</div>
	);
}

/** 页签一(默认):字段契约 —— 本页主体。按域筛选,逐环节折叠。 */
function IoFieldsPanel({ d }: { d: IoContractResponse }) {
	const [domain, setDomain] = useState<string>("全部");
	const domains = ["全部", ...Array.from(new Set(d.stages.map((s) => s.domain)))];
	const stages = domain === "全部" ? d.stages : d.stages.filter((s) => s.domain === domain);
	const pending = d.findings.filter((x) => x.level !== "登记").length;
	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center gap-2">
				<Pill tone="info">{d.summary.stage_count} 个环节</Pill>
				<Pill tone="muted">{d.summary.input_field_count} 输入字段</Pill>
				<Pill tone="muted">{d.summary.output_field_count} 输出字段</Pill>
				{pending ? (
					<Pill tone="warning">{pending} 项四面不一致</Pill>
				) : (
					<Pill tone="success">四面全对齐</Pill>
				)}
				<span className="ml-auto">
					<LabeledSelect
						label="域"
						value={domain}
						onChange={setDomain}
						options={domains.map((x) => ({ value: x, label: x }))}
					/>
				</span>
			</div>
			{stages.map((s) => (
				<IoStageCard key={s.key} stage={s} />
			))}

			<details className="rounded-lg border border-surface-border">
				<summary className="cursor-pointer px-4 py-2.5 text-sm font-medium">
					双源登记({d.dual_sources.length})—— 同一份知识两处各写一份
				</summary>
				<div className="space-y-3 border-t border-surface-border px-4 py-3">
					{d.dual_sources.map((ds) => (
						<div key={ds.id} className="space-y-1.5 rounded-md border border-surface-border p-2">
							<p className="flex flex-wrap items-center gap-2 text-xs">
								<span className="font-mono">{ds.id}</span>
								<span className="font-medium">{ds.what}</span>
								<GotoBtn hash={ds.detail_hash} label={ds.detail_label} />
								{ds.id_mismatch.length ? (
									<Pill tone="danger">枚举 id 不一致</Pill>
								) : (
									<Pill tone="success">枚举 id 一致</Pill>
								)}
								{ds.drift_count ? (
									<Pill tone="warning">
										描述漂移 {ds.drift_count}/{ds.total}
									</Pill>
								) : (
									<Pill tone="success">描述一致</Pill>
								)}
							</p>
							<p className="text-xs text-text-muted">
								A(后台展示):<span className="font-mono">{ds.source_a}</span>
							</p>
							<p className="text-xs text-text-muted">
								B(模型实读):<span className="font-mono">{ds.source_b}</span>
							</p>
							{ds.drift.length ? (
								<div className="overflow-x-auto rounded-md border border-surface-border">
									<table className="w-full text-left text-xs">
										<thead>
											<tr className="border-b border-surface-border text-text-muted">
												<th className="px-2 py-1.5">键</th>
												<th className="px-2 py-1.5">A · 注册表(页面显示)</th>
												<th className="px-2 py-1.5">B · 提示词正文(模型读到)</th>
											</tr>
										</thead>
										<tbody>
											{ds.drift.map((x) => (
												<tr key={x.key} className="border-b border-surface-border/60">
													<td className="whitespace-nowrap px-2 py-1.5 font-mono">{x.key}</td>
													<td className="px-2 py-1.5 text-text-secondary">{x.registry}</td>
													<td className="px-2 py-1.5 text-text-secondary">{x.prompt}</td>
												</tr>
											))}
										</tbody>
									</table>
								</div>
							) : null}
							<p className="text-xs text-text-muted">{ds.why_open}</p>
						</div>
					))}
				</div>
			</details>

			<details className="rounded-lg border border-surface-border">
				<summary className="cursor-pointer px-4 py-2.5 text-sm font-medium">
					装配面清册({d.assembly_surfaces.length})—— 评测 / 归因 / CX,不在规则注册表内
				</summary>
				<div className="border-t border-surface-border px-4 py-3">
					<div className="overflow-x-auto rounded-md border border-surface-border">
						<table className="w-full text-left text-xs">
							<thead>
								<tr className="border-b border-surface-border text-text-muted">
									<th className="px-2 py-1.5">编号</th>
									<th className="px-2 py-1.5">装配面</th>
									<th className="px-2 py-1.5">代码位置</th>
									<th className="px-2 py-1.5">产出什么</th>
									<th className="px-2 py-1.5">喂给谁</th>
									<th className="px-2 py-1.5">备注</th>
								</tr>
							</thead>
							<tbody>
								{d.assembly_surfaces.map((a) => (
									<tr key={a.id} className="border-b border-surface-border/60 align-top">
										<td className="px-2 py-1.5 font-mono">{a.id}</td>
										<td className="whitespace-nowrap px-2 py-1.5">{a.name}</td>
										<td className="px-2 py-1.5 font-mono text-[11px] text-text-muted">{a.where}</td>
										<td className="px-2 py-1.5 text-text-secondary">{a.produces}</td>
										<td className="px-2 py-1.5 text-text-muted">{a.feeds}</td>
										<td className="px-2 py-1.5 text-text-muted">{a.note}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</details>
		</div>
	);
}

const DIM_KEYS = ["d1", "d2", "d3", "d4", "d5", "d6"] as const;

function cellTone(v: string): Tone {
	if (v === "已完成") return "success";
	if (v === "部分") return "info";
	if (v === "不适用") return "muted";
	return "warning"; // 未做
}

/** 页签二:诊断覆盖 —— 六维模型 + 阻塞清册 + 流程债。
 *  实事求是三条:①"部分"必须在说明里写差在哪 ②"不适用"不计分母(它不是欠账)
 *  ③被阻塞的格子指向具体 BLK 编号,不混成"排期靠后"。 */
function IoCoveragePanel({ d }: { d: IoContractResponse }) {
	const s = d.summary;
	const denom = s.cells_done + s.cells_partial + s.cells_todo;
	const pct = denom ? Math.round((100 * s.cells_done) / denom) : 0;
	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-2">
				<Pill tone={pct >= 80 ? "success" : "info"}>真实完成率 {pct}%</Pill>
				<Pill tone="success">已完成 {s.cells_done}</Pill>
				<Pill tone="info">部分 {s.cells_partial}</Pill>
				<Pill tone="warning">未做 {s.cells_todo}</Pill>
				<Pill tone="muted">不适用 {s.cells_na}(不计分母)</Pill>
				{s.blockers ? <Pill tone="danger">阻塞 {s.blockers}</Pill> : null}
				{s.process_debt ? <Pill tone="warning">流程债 {s.process_debt}</Pill> : null}
			</div>

			<div className="rounded-md border border-surface-border bg-surface-subtle p-2 text-xs">
				<p className="mb-1 font-medium text-text-primary">
					六维 —— 一个注入面要算"审透了"必须过的六关
				</p>
				{d.dimensions.map((x) => (
					<p key={x.key} className="text-text-muted">
						<span className="font-mono text-text-secondary">{x.key}</span>{" "}
						<span className="text-text-secondary">{x.name}</span> · {x.desc}
					</p>
				))}
			</div>

			<div className="overflow-x-auto rounded-lg border border-surface-border">
				<table className="w-full text-left text-xs">
					<thead>
						<tr className="border-b border-surface-border text-text-muted">
							<th className="px-2 py-1.5">层</th>
							<th className="px-2 py-1.5">对象</th>
							{d.dimensions.map((x) => (
								<th key={x.key} className="px-2 py-1.5" title={x.desc}>
									{x.name}
								</th>
							))}
							<th className="px-2 py-1.5">去看</th>
							<th className="px-2 py-1.5">差在哪 / 备注</th>
						</tr>
					</thead>
					<tbody>
						{d.coverage.map((c) => (
							<tr
								key={`${c.layer}-${c.target}`}
								className="border-b border-surface-border/60 align-top"
							>
								<td className="whitespace-nowrap px-2 py-1.5 text-text-muted">{c.layer}</td>
								<td className="whitespace-nowrap px-2 py-1.5">{c.target}</td>
								{DIM_KEYS.map((k) => (
									<td key={k} className="whitespace-nowrap px-2 py-1.5">
										<Pill tone={cellTone(c[k])}>{c[k]}</Pill>
									</td>
								))}
								<td className="whitespace-nowrap px-2 py-1.5">
									<GotoBtn hash={c.detail_hash} label={c.detail_label} />
								</td>
								<td className="px-2 py-1.5 text-text-secondary">{c.note}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div>
				<p className="mb-1 text-xs font-medium">
					阻塞清册 —— 不解掉,上表里对应的格子<span className="text-text-secondary">永远做不了</span>
					,不是"排期靠后"
				</p>
				<div className="space-y-2">
					{d.blockers.map((b) => (
						<div key={b.id} className="space-y-1 rounded-md border border-surface-border p-2">
							<p className="flex flex-wrap items-center gap-2 text-xs">
								<span className="font-mono">{b.id}</span>
								<Pill tone="danger">阻塞</Pill>
								<span className="font-medium">{b.title}</span>
							</p>
							<p className="text-xs text-text-muted">
								<span className="text-text-secondary">卡住 </span>
								{b.blocks}
							</p>
							<p className="text-xs text-text-secondary">{b.evidence}</p>
							<p className="text-xs text-text-muted">
								<span className="text-text-secondary">解法 </span>
								{b.fix}
							</p>
						</div>
					))}
				</div>
			</div>

			<div>
				<p className="mb-1 text-xs font-medium">
					流程债 —— 不是功能缺口,是<span className="text-text-secondary">做过但没收尾</span>的账
				</p>
				<div className="space-y-2">
					{d.process_debt.map((x) => (
						<div key={x.id} className="space-y-1 rounded-md border border-surface-border p-2">
							<p className="flex flex-wrap items-center gap-2 text-xs">
								<span className="font-mono">{x.id}</span>
								<Pill tone="warning">欠账</Pill>
								<span className="font-medium">{x.title}</span>
							</p>
							<p className="text-xs text-text-secondary">{x.detail}</p>
							<p className="text-xs text-text-muted">
								<span className="text-text-secondary">补法 </span>
								{x.fix}
							</p>
						</div>
					))}
				</div>
			</div>

			<div>
				<p className="mb-1 text-xs font-medium">内容质量审计(六维里的 d5,四面对齐查不到的那层)</p>
				<div className="overflow-x-auto rounded-lg border border-surface-border">
					<table className="w-full text-left text-xs">
						<thead>
							<tr className="border-b border-surface-border text-text-muted">
								<th className="px-2 py-1.5">编号</th>
								<th className="px-2 py-1.5">对象</th>
								<th className="px-2 py-1.5">要回答什么</th>
								<th className="px-2 py-1.5">为什么重要</th>
							</tr>
						</thead>
						<tbody>
							{d.content_audit_todo.map((q) => (
								<tr key={q.id} className="border-b border-surface-border/60 align-top">
									<td className="whitespace-nowrap px-2 py-1.5 font-mono">{q.id}</td>
									<td className="whitespace-nowrap px-2 py-1.5">{q.target}</td>
									<td className="px-2 py-1.5 text-text-secondary">{q.question}</td>
									<td className="px-2 py-1.5 text-text-muted">{q.why}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);
}

const OPP_TONE: Record<string, Tone> = {
	机会点: "info",
	缺陷: "success", // 缺陷=客观可判、已直接修,与"待 A/B 的机会点"分色
	待归因: "warning",
	已证伪: "muted",
};

/** 页签三:机会点台账。 */
function IoOpportunityPanel({ d }: { d: IoContractResponse }) {
	const [kind, setKind] = useState<string>("全部");
	const kinds = ["全部", ...Array.from(new Set(d.opportunities.map((o) => o.kind)))];
	const rows = kind === "全部" ? d.opportunities : d.opportunities.filter((o) => o.kind === kind);
	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center gap-2">
				<Pill tone="info">{d.summary.opportunity_open} 条待 A/B</Pill>
				{d.summary.defect_fixed ? (
					<Pill tone="success">{d.summary.defect_fixed} 条缺陷已修</Pill>
				) : null}
				{d.summary.opportunity_pending_rca ? (
					<Pill tone="warning">{d.summary.opportunity_pending_rca} 条待归因</Pill>
				) : null}
				<span className="ml-auto">
					<LabeledSelect
						label="类型"
						value={kind}
						onChange={setKind}
						options={kinds.map((x) => ({ value: x, label: x }))}
					/>
				</span>
			</div>
			<p className="text-xs text-text-muted">
				纪律:缺陷=四面不一致,客观可判、直接修、不 A/B;机会点=组装方式可能更优,
				<span className="text-text-secondary">一律 A/B,不达标即弃</span>
				。每条必须带实测证据与误伤面。
			</p>
			{rows.map((o) => (
				<div key={o.id} className="space-y-1.5 rounded-md border border-surface-border p-2">
					<p className="flex flex-wrap items-center gap-2 text-xs">
						<span className="font-mono">{o.id}</span>
						<Pill tone={OPP_TONE[o.kind] ?? "muted"}>{o.kind}</Pill>
						<Pill tone="muted">{o.status}</Pill>
						<span className="font-medium">{o.title}</span>
						<span className="ml-auto font-mono text-[11px] text-text-muted">
							{o.stage} · {o.field}
						</span>
					</p>
					<p className="text-xs text-text-secondary">
						<span className="text-text-muted">实测证据 </span>
						{o.evidence}
					</p>
					{o.hypothesis !== "—" ? (
						<p className="text-xs text-text-secondary">
							<span className="text-text-muted">假设 </span>
							{o.hypothesis}
						</p>
					) : null}
					{o.risk !== "—" ? (
						<p className="text-xs text-text-secondary">
							<span className="text-text-muted">误伤面 </span>
							{o.risk}
						</p>
					) : null}
					<p className="text-xs text-text-secondary">
						<span className="text-text-muted">怎么验 </span>
						{o.verify}
					</p>
				</div>
			))}
		</div>
	);
}

const ROADMAP_TONE: Record<string, Tone> = {
	已上线: "success",
	已完成待合: "warning", // 代码写完但 PR 未合:线上还没有,别当"已上线"读
	"待做(欠账)": "warning",
	待排期: "info",
	未开始: "muted",
};

/** 页签四:实施计划。 */
function IoRoadmapPanel({ d }: { d: IoContractResponse }) {
	return (
		<div className="space-y-3">
			<Pill tone="success">
				{d.summary.roadmap_done}/{d.summary.roadmap_total} 已上线
			</Pill>
			<div className="overflow-x-auto rounded-lg border border-surface-border">
				<table className="w-full text-left text-xs">
					<thead>
						<tr className="border-b border-surface-border text-text-muted">
							<th className="px-2 py-1.5">阶段</th>
							<th className="px-2 py-1.5">状态</th>
							<th className="px-2 py-1.5">范围</th>
							<th className="px-2 py-1.5">出处</th>
						</tr>
					</thead>
					<tbody>
						{d.roadmap.map((r) => (
							<tr key={r.id} className="border-b border-surface-border/60 align-top">
								<td className="whitespace-nowrap px-2 py-1.5 font-mono">
									{r.id} {r.name}
								</td>
								<td className="whitespace-nowrap px-2 py-1.5">
									<Pill tone={ROADMAP_TONE[r.status] ?? "muted"}>{r.status}</Pill>
								</td>
								<td className="px-2 py-1.5 text-text-secondary">{r.scope}</td>
								<td className="whitespace-nowrap px-2 py-1.5 text-text-muted">{r.ref}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}

function IoContractTab() {
	const q = useIoContract();
	const [sub, setSub] = useState<IoSub>("fields");
	const d: IoContractResponse | undefined = q.data;
	if (q.isLoading || !d)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	return (
		<Card>
			<CardHeader>
				<CardTitle>
					<div className="flex flex-wrap gap-1 rounded-full border border-surface-border p-0.5">
						{IO_SUBS.map((s) => (
							<SubTabBtn key={s.key} active={sub === s.key} onClick={() => setSub(s.key)}>
								{s.label}
							</SubTabBtn>
						))}
					</div>
				</CardTitle>
			</CardHeader>
			<CardContent>
				{sub === "fields" ? <IoFieldsPanel d={d} /> : null}
				{sub === "coverage" ? <IoCoveragePanel d={d} /> : null}
				{sub === "opportunity" ? <IoOpportunityPanel d={d} /> : null}
				{sub === "roadmap" ? <IoRoadmapPanel d={d} /> : null}
			</CardContent>
		</Card>
	);
}

function MethodSkillsTab() {
	const q = useMethodSkills();
	const [openName, setOpenName] = useState<string | null>(null);
	const skills = q.data?.skills ?? [];
	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	return (
		<div className="space-y-4">
			{skills.length === 0 ? (
				<Card>
					<CardContent className="py-4 text-xs text-text-muted">
						未发现任何 skill(.claude/skills 为空)——若与预期不符,说明仓库 skill 丢失,先查 git 历史。
					</CardContent>
				</Card>
			) : null}
			{skills.map((s) => (
				<Card key={s.name}>
					<CardHeader>
						<CardTitle className="flex flex-wrap items-center gap-2">
							<span className="font-mono">{s.name}</span>
							<Pill tone="info">{s.versions} 版</Pill>
							{s.latest_update ? (
								<Pill tone="muted">最新 {s.latest_update}</Pill>
							) : (
								<Pill tone="warning">无版本日志</Pill>
							)}
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<p className="text-xs text-text-secondary">{s.description}</p>
						{s.updates.length ? (
							<div className="overflow-x-auto rounded-lg border border-surface-border">
								<table className="w-full text-left text-xs">
									<thead>
										<tr className="border-b border-surface-border text-text-muted">
											<th className="px-2 py-1.5">日期</th>
											<th className="px-2 py-1.5">改动(每行一版)</th>
											<th className="px-2 py-1.5">出处</th>
										</tr>
									</thead>
									<tbody>
										{s.updates.map((u, i) => (
											<tr key={`${u.date}-${i}`} className="border-b border-surface-border/60">
												<td className="whitespace-nowrap px-2 py-1.5 font-mono">{u.date}</td>
												<td className="px-2 py-1.5 text-text-secondary">{u.change}</td>
												<td className="whitespace-nowrap px-2 py-1.5 text-text-muted">
													{u.source}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						) : null}
						<div className="flex items-center gap-2">
							<Button
								type="button"
								size="sm"
								variant="outline"
								onClick={() => setOpenName(openName === s.name ? null : s.name)}
							>
								{openName === s.name ? "收起全文" : "查看全文"}
							</Button>
							<span className="font-mono text-[11px] text-text-muted">{s.path}</span>
						</div>
						{openName === s.name ? (
							<pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded-md border border-surface-border bg-surface-muted/40 p-3 font-mono text-xs leading-5 text-text-primary">
								{s.body}
							</pre>
						) : null}
					</CardContent>
				</Card>
			))}
		</div>
	);
}

function RcaStandardTab() {
	const q = useRcaStandard();
	const d = q.data;
	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	return (
		<div className="space-y-4">
			<Card>
				<CardHeader>
					<CardTitle>归因标准 · 9 类 / 5 簇(只读 · 提示词版本 {d?.prompt_version})</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3">
					<NodeModelSelector node="rca" />
					<p className="text-xs text-text-muted">
						判官按此边界排查(同一份标准也作为 {"{{standard}}"} 注入判官提示词,页面与判官同源)。
						排查顺序:{d?.order}
					</p>
					<div className="space-y-3">
						{(d?.standard ?? []).map((c) => (
							<div key={c.cluster} className="rounded-lg border border-surface-border p-3">
								<div className="mb-1 flex items-center gap-2">
									<Pill tone={RCA_CLUSTER_TONE[c.cluster] ?? "muted"}>{c.cluster}</Pill>
									<span className="text-xs text-text-muted">{c.note}</span>
								</div>
								<div className="space-y-2">
									{c.labels.map((l) => (
										<div key={l.key} className="rounded-md bg-surface-subtle/60 p-2 text-xs">
											<div className="flex flex-wrap items-center gap-2">
												<span className="font-medium text-text-primary">{l.key}</span>
												<Pill tone="muted">{l.four}</Pill>
											</div>
											<div className="mt-0.5 text-text-secondary">
												<b>判据:</b>
												{l.judge}
											</div>
											<div className="mt-0.5 text-text-muted">
												<b>边界:</b>
												{l.boundary}
											</div>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				</CardContent>
			</Card>
			{d?.evidence_fields?.length ? (
				<Card>
					<CardHeader>
						<CardTitle>输入字段字典 · Evidence Pack(只读)</CardTitle>
					</CardHeader>
					<CardContent className="space-y-2">
						<p className="text-xs text-text-muted">
							发起分析时,系统把失败卡+场景契约+环境真实数据组装成 Evidence Pack 喂给判官;
							这份字典说明每个证据字段是什么、看什么问题(同时作为 {"{{evidence_fields}}"}{" "}
							注入判官提示词)。归因记录里的证据引用路径即指向这些字段。
						</p>
						<div className="overflow-x-auto rounded-lg border border-surface-border">
							<table className="w-full text-left text-xs">
								<thead>
									<tr className="border-b border-surface-border text-text-muted">
										<th className="px-2 py-1.5">字段路径</th>
										<th className="px-2 py-1.5">是什么 · 看什么问题</th>
									</tr>
								</thead>
								<tbody>
									{d.evidence_fields.map((f) => (
										<tr key={f.path} className="border-b border-surface-border/60">
											<td className="px-2 py-1.5 font-mono text-brand-primary">{f.path}</td>
											<td className="px-2 py-1.5 text-text-secondary">{f.desc}</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</CardContent>
				</Card>
			) : null}
			<RcaContractLintCard />
			<EvalPromptEditors
				keys={["rca_group", "rca_synth"]}
				title="归因提示词 · 改完保存即对下次「发起分析」生效,不走部署"
				intro={
					<>
						两段提示词驱动整条归因流水线:<strong>5 簇假设判官(共用骨架)</strong>
						——5 个簇判官并行看全量证据,系统按簇注入{" "}
						<code className="rounded bg-surface-card px-1 font-mono">
							{"{{role}}/{{task}}/{{discipline}}/{{sub_labels}}"}
						</code>
						;<strong>首席根因裁决官(综合)</strong>——汇总 5 簇假设逐层排除,定唯一主归因 +
						一句话第一性主因 + 根因链 + 证据引文。
						<code className="rounded bg-surface-card px-1 font-mono">{"{{standard}}"}</code> 与{" "}
						<code className="rounded bg-surface-card px-1 font-mono">{"{{evidence_fields}}"}</code>{" "}
						由系统注入(与本页「归因标准」「输入字段字典」同源);改动请保留结构(第一性原理优先,别堆硬规则)。
					</>
				}
			/>
		</div>
	);
}

function EvalRunPanel({
	hasKey,
	onDone,
}: {
	hasKey: boolean;
	onDone: (r: SimRunReport) => void;
}) {
	const q = useSimScenarios();
	const run = useRunSim();
	const port = useSkillPort(); // 环境防呆:发起前看 backend 快照
	const sets = useTestSets(); // 刀E/M5:命名测试集
	const [pickedSet, setPickedSet] = useState("");
	const [picked, setPicked] = useState<string[]>([]);
	const [trials, setTrials] = useState(1);
	const [persona, setPersona] = useState("");
	const [withCx, setWithCx] = useState(false); // 刀A/M5:CX 批量
	const [autoCheck, setAutoCheck] = useState(false); // 刀B/M5:must 兜底核验
	const [withCompare, setWithCompare] = useState(false); // 刀A/M5:指南全关对照(收口时用)
	const [filter, setFilter] = useState<ScenFilter>(EMPTY_SCEN_FILTER);
	const scenarios = q.data?.scenarios ?? [];
	const personas = q.data?.personas ?? [];
	const shown = filterScenarios(scenarios, filter);

	function toggle(id: string) {
		setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
	}
	function selectShown() {
		setPicked((p) => Array.from(new Set([...p, ...shown.map((s) => s.id)])));
	}
	async function start() {
		if (!picked.length && !pickedSet) return;
		// 环境防呆(V2-P1.5 冤案防复发):金标锚定罐头,非 fixture 环境须显式确认
		const nonFixture = port.data?.snapshot?.non_fixture ?? [];
		let allow = false;
		if (nonFixture.length) {
			allow = window.confirm(
				`当前 SkillPort 非全罐头环境(live/recorded: ${nonFixture.join(", ")})。\n评测金标锚定罐头数据,在此环境下结果可能失真。\n仍要继续吗?(建议先去 SKILL 词典页切回罐头)`,
			);
			if (!allow) return;
		}
		const r = await run.mutateAsync({
			only_set: pickedSet || undefined,
			scenario_ids: pickedSet ? [] : picked,
			trials,
			persona: persona || null,
			attribute: true,
			allow_non_fixture: allow,
			with_cx: withCx,
			auto_check: autoCheck,
			...(withCompare ? { compare: { guidance: "off" } } : {}),
		});
		onDone(r);
	}
	const nonFixture = port.data?.snapshot?.non_fixture ?? [];
	const envBanner = nonFixture.length ? (
		<div className="mb-3 rounded-lg border border-brand-warning/40 bg-brand-warning/10 px-3 py-2 text-xs text-brand-warning">
			⚠ 当前 SkillPort 非全罐头环境(live/recorded:{nonFixture.join(", ")})——评测金标锚定罐头,
			结果可能失真;发起时需确认放行,或先去 SKILL 词典页切回。放行后仅「真实可测」场景可信:
			订单号会按真店锚自动映射(场景卡绿标,悬停看映射),「仅罐头」场景在该环境下会失真。
		</div>
	) : null;
	if (!hasKey)
		return (
			<div className="text-sm text-brand-warning">
				未配置 LLM key —— 评测需真模型对话,无法发起。
			</div>
		);
	return (
		<div className="space-y-3">
			{/* 置顶操作条:筛选 + 选中统计 + trial/persona + 发起(不用滚到底) */}
			<div className="sticky top-0 z-10 space-y-2 rounded-lg border border-surface-border bg-surface-raised/95 p-2.5 backdrop-blur">
				<ScenarioFilters scenarios={scenarios} value={filter} onChange={setFilter} />
				<div className="flex flex-wrap items-center gap-3 text-sm">
					<Select
						label="测试集"
						value={pickedSet}
						onChange={(v) => {
							setPickedSet(v);
							const cfg = sets.data?.sets.find((x) => x.name === v);
							if (cfg) {
								setWithCx(Boolean(cfg.defaults.with_cx));
								setAutoCheck(Boolean(cfg.defaults.auto_check));
							}
						}}
						options={[
							{ value: "", label: "手选场景" },
							...(sets.data?.sets ?? []).map((x) => ({
								value: x.name,
								label: `${x.name}(${x.n})`,
							})),
						]}
					/>
					<span className="text-xs text-text-muted">
						{pickedSet
							? `按集合跑 ${sets.data?.sets.find((x) => x.name === pickedSet)?.n ?? "?"} 道`
							: `筛出 ${shown.length} · 已选`}{" "}
						{pickedSet ? null : <b className="text-text-primary">{picked.length}</b>}
					</span>
					<button
						type="button"
						onClick={selectShown}
						className="rounded border border-surface-border px-2 py-1 text-xs hover:bg-surface-muted"
					>
						全选筛选结果
					</button>
					<button
						type="button"
						onClick={() => setPicked([])}
						className="rounded border border-surface-border px-2 py-1 text-xs hover:bg-surface-muted"
					>
						清空
					</button>
					<label className="flex items-center gap-1.5">
						trial 次数
						<input
							type="number"
							min={1}
							max={5}
							value={trials}
							onChange={(e) => setTrials(Math.max(1, Math.min(5, Number(e.target.value) || 1)))}
							className="w-16 rounded border border-surface-border px-2 py-1"
						/>
					</label>
					<label
						className="flex items-center gap-1.5 text-xs"
						title="逐题跑会话级 CX 评分(约 $0.02/题),批次报告与评测记录带 CX"
					>
						<input type="checkbox" checked={withCx} onChange={(e) => setWithCx(e.target.checked)} />
						CX 评分
					</label>
					<label
						className="flex items-center gap-1.5 text-xs"
						title="无显式指南断言的场景,对命中的 must 指南自动兜底核验(每卡约 +$0.01)"
					>
						<input
							type="checkbox"
							checked={autoCheck}
							onChange={(e) => setAutoCheck(e.target.checked)}
						/>
						must 核验
					</label>
					<label
						className="flex items-center gap-1.5 text-xs"
						title="同集合再跑一遍指南全关的对照批(成本×2),报告出指南增益——版本收口时用"
					>
						<input
							type="checkbox"
							checked={withCompare}
							onChange={(e) => setWithCompare(e.target.checked)}
						/>
						指南对照
					</label>
					<span className="flex items-center gap-1.5">
						性格
						<StyledSelect
							aria-label="性格"
							value={persona}
							onChange={(e) => setPersona(e.target.value)}
						>
							<option value="">随机(稳健性)</option>
							{personas.map((p) => (
								<option key={p.name} value={p.name}>
									{p.name}
								</option>
							))}
						</StyledSelect>
					</span>
					{envBanner}
					<button
						type="button"
						onClick={start}
						disabled={!picked.length || run.isPending}
						className="rounded-full bg-brand-primary px-3.5 py-1.5 text-sm font-semibold text-white transition-colors duration-150 hover:opacity-90 disabled:opacity-50"
					>
						{run.isPending ? "评测中…(真模型,稍候)" : `开始评测(${picked.length} 题 × ${trials})`}
					</button>
				</div>
				{/* C25 批5(P8):悬浮操作条——选中即浮出,免回滚顶部找按钮 */}
				{picked.length > 0 && !run.isPending ? (
					<div className="fixed bottom-6 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full border border-surface-border bg-surface-card py-2 pl-4 pr-2 shadow-lg">
						<span className="text-sm text-text-secondary">
							已选 <b className="text-text-primary">{picked.length}</b> 题 × {trials} 次
						</span>
						<button
							type="button"
							onClick={() => setPicked([])}
							className="rounded-full px-2.5 py-1 text-xs text-text-secondary transition-colors duration-150 hover:bg-surface-subtle hover:text-text-primary"
						>
							清空
						</button>
						<button
							type="button"
							onClick={start}
							className="rounded-full bg-brand-primary px-4 py-1.5 text-sm font-semibold text-white transition-colors duration-150 hover:opacity-90"
						>
							开始评测
						</button>
					</div>
				) : null}
				{run.isError ? (
					<div className="text-xs text-brand-danger">
						评测失败:{String((run.error as Error)?.message ?? run.error)}
					</div>
				) : null}
			</div>
			<p className="text-xs text-text-muted">
				选场景(四件套机器顾客)→ 设 trial 次数 / persona → 发起。每题跑 N 次出 pass^k;同步执行、耗
				token,封顶 5 次,默认小批量。
			</p>
			<div className="space-y-1.5">
				{shown.map((s) => (
					<label
						key={s.id}
						className="flex cursor-pointer items-start gap-2 rounded-md p-2 text-sm hover:bg-surface-subtle"
					>
						<input
							type="checkbox"
							checked={picked.includes(s.id)}
							onChange={() => toggle(s.id)}
							className="mt-1 accent-brand-accent"
						/>
						<div className="min-w-0">
							<div className="flex flex-wrap items-center gap-1.5">
								<span className="font-medium text-text-primary">{s.id}</span>
								<Pill tone={STAGE_TONE[s.stage] ?? "muted"}>{s.stage}</Pill>
								{s.category ? <Pill tone="info">{s.category}</Pill> : null}
								<LiveReadyPill live={s.live} />
								{s.known_issue ? (
									<Pill tone={s.known_issue.cat.startsWith("已") ? "success" : "warning"}>
										{s.known_issue.cat.startsWith("已") ? "✓ 已解" : "⚠ 已知问题"}
									</Pill>
								) : null}
								<span className="font-normal text-text-muted">· {s.desc}</span>
							</div>
							<div className="truncate text-xs text-text-muted">来意:{s.reason_for_call}</div>
							<div className="mt-1 flex flex-wrap gap-1">
								{(s.eval.expect_skills ?? []).map((sk) => (
									<Pill key={sk} tone="info">
										{sk}
									</Pill>
								))}
								{(s.eval.nl_assertions ?? []).length ? (
									<Pill tone="muted">{(s.eval.nl_assertions ?? []).length} 条合规断言</Pill>
								) : null}
							</div>
							<JudgeContract ev={s.eval} />
						</div>
					</label>
				))}
				{shown.length === 0 ? (
					<div className="rounded-md border border-dashed border-surface-border p-3 text-center text-xs text-text-muted">
						当前筛选无场景。
					</div>
				) : null}
			</div>
		</div>
	);
}

function EvalReportPanel({ report }: { report: SimRunReport | null }) {
	const [drill, setDrill] = useState<SimCard | null>(null);
	return (
		<div className="space-y-4">
			{report ? (
				<EvalReportBody report={report} onDrill={setDrill} />
			) : (
				<div className="text-sm text-text-muted">
					去「模拟场景」跑一批,这里看本次结果;历史批次在左侧「评测记录」。
				</div>
			)}
			<EvalDrawer card={drill} onClose={() => setDrill(null)} />
		</div>
	);
}

function EvalReportBody({
	report,
	onDrill,
}: {
	report: SimRunReport;
	onDrill: (c: SimCard) => void;
}) {
	const agg = report.aggregate;
	const curve = report.pass_k_curve;
	return (
		<div className="space-y-4">
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
				<Kpi label="平均分" value={String(agg.avg_reward)} tone={rateTone(agg.avg_reward)} />
				<Kpi
					label="action 通过率"
					value={pct(agg.dim_pass.action)}
					tone={rateTone(agg.dim_pass.action)}
				/>
				<Kpi label="nl 合规率" value={pct(agg.dim_pass.nl)} tone={rateTone(agg.dim_pass.nl)} />
				<Kpi label="样本数" value={String(agg.n)} />
			</div>
			<div className="rounded-lg border border-surface-border p-3">
				<div className="mb-1 text-xs text-text-muted">
					pass^k 曲线(跨题平均 · 越往右越能暴露"偶尔做对"的不稳场景)
				</div>
				<div className="flex flex-wrap gap-3 text-sm">
					{Object.entries(curve).map(([k, v]) => (
						<span key={k}>
							pass^{k} ={" "}
							<span className={`font-semibold ${TONE[rateTone(v)].split(" ").pop()}`}>{v}</span>
						</span>
					))}
					{Object.keys(curve).length ? null : <span className="text-text-muted">—</span>}
				</div>
				<div className="mt-1 text-xs text-text-muted">
					终止分布:
					{Object.entries(agg.termination)
						.map(([k, v]) => `${k}:${v}`)
						.join(" · ")}{" "}
					· committed 率 {pct(agg.committed_rate)}
				</div>
			</div>
			{report.detail.map((d) => (
				<div key={d.scenario_id} className="rounded-lg border border-surface-border">
					<div className="flex flex-wrap items-center gap-2 border-b border-surface-border px-3 py-2 text-sm">
						<span className="font-medium text-text-primary">{d.scenario_id}</span>
						<Pill
							tone={
								d.passes.every(Boolean) ? "success" : d.passes.some(Boolean) ? "warning" : "danger"
							}
						>
							通过 {d.passes.filter(Boolean).length}/{d.passes.length}
						</Pill>
						<span className="text-xs text-text-muted">
							{Object.entries(d.pass_hat)
								.map(([k, v]) => `pass^${k}=${v}`)
								.join("  ")}
						</span>
					</div>
					<div className="divide-y divide-surface-border">
						{d.cards.map((c, i) => (
							<div
								key={`${d.scenario_id}-${i}`}
								className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm"
							>
								<span className="w-8 text-xs text-text-muted">#{i + 1}</span>
								<Pill tone={rewardTone(c.reward)}>reward {c.reward}</Pill>
								<span className="text-xs text-text-muted">
									{c.persona ?? "—"} · {c.termination_reason} · {c.num_turns ?? "?"}轮
								</span>
								<DimDot label="action" v={c.dims.action} />
								<DimDot label="comm" v={c.dims.communicate} />
								<DimDot label="nl" v={c.dims.nl} />
								{c.attribution?.has_critical ? (
									<Pill tone="danger">
										首错来源=
										{SOURCE_LABEL[c.attribution.first_critical_source] ??
											c.attribution.first_critical_source}
									</Pill>
								) : null}
								{(c.guidance_verdicts ?? []).map((v) => (
									<span key={`${v.guideline_id}-${v.polarity}`} title={v.reason}>
										<Pill tone={v.met ? "success" : "danger"}>
											{v.met ? "遵" : "违"} {v.guideline_id.replace(/^G-/, "")}
											{v.source === "auto" ? "·auto" : ""}
										</Pill>
									</span>
								))}
								{c.guidance_recall && !c.guidance_recall.ok ? (
									<Pill tone="warning">召回缺失:{c.guidance_recall.missing.join(",")}</Pill>
								) : null}
								<button
									type="button"
									onClick={() => onDrill(c)}
									className="ml-auto text-xs text-brand-primary hover:underline"
								>
									查看详情
								</button>
								{c.reward < 1 ? (
									<div className="w-full text-xs text-brand-danger">
										不过原因:{failReason(c) || "见详情"}
									</div>
								) : null}
							</div>
						))}
					</div>
				</div>
			))}
		</div>
	);
}

function EvalScorecard({ card }: { card: SimCard }) {
	return (
		<div className="space-y-2.5 border-b border-surface-border px-4 py-3 text-sm">
			<div className="flex flex-wrap items-center gap-2">
				<span className="text-[11px] font-bold text-text-secondary">判分维</span>
				<DimDot label="action" v={card.dims.action} />
				<DimDot label="communicate" v={card.dims.communicate} />
				<DimDot label="nl" v={card.dims.nl} />
				{card.failed_dims.length ? (
					<Pill tone="danger">失败维:{card.failed_dims.join(",")}</Pill>
				) : (
					<Pill tone="success">满分</Pill>
				)}
				{card.actor_violation ? <Pill tone="warning">演员失职</Pill> : null}
			</div>
			{card.actor_violation && card.actor_check ? (
				<div className="text-xs text-text-muted">
					机器顾客脚本核验:
					{card.actor_check.scripted_quotes.map((q) => (
						<span key={q.scripted} className="ml-1.5">
							「{q.scripted}」{q.appeared ? "✓已说" : q.soft ? "≈改写疑似" : "✗未说"}
						</span>
					))}
					<span className="ml-1.5">— 演员未执行脚本,本样本失败可能是评测侧噪声</span>
				</div>
			) : null}
			{card.action.missing.length || card.action.forbidden_called.length ? (
				<div className="text-xs text-text-muted">
					调度:期望 {card.action.expected.join(",") || "—"} · 实际{" "}
					{card.action.actual.join(",") || "—"}
					{card.action.missing.length ? ` · 缺 ${card.action.missing.join(",")}` : ""}
					{card.action.forbidden_called.length
						? ` · 禁触 ${card.action.forbidden_called.join(",")}`
						: ""}
				</div>
			) : null}
			{card.nl.results.length ? (
				<div className="space-y-1 border-t border-surface-border pt-2">
					<div className="text-[11px] font-bold text-text-secondary">合规判定(NL 判官逐条)</div>
					{card.nl.results.map((r) => (
						<div key={r.assertion} className="text-xs">
							<Pill tone={r.met ? "success" : "danger"}>{r.met ? "✓" : "✗"}</Pill>{" "}
							<span className="text-text-secondary">{r.assertion}</span>
							{r.reason ? <span className="text-text-muted"> — {r.reason}</span> : null}
						</div>
					))}
				</div>
			) : null}
			{card.attribution?.errors.length ? (
				<div className="space-y-1 border-t border-surface-border pt-2">
					<div className="text-[11px] font-bold text-text-secondary">
						错误归因(首个 critical 来源=
						{SOURCE_LABEL[card.attribution.first_critical_source] ??
							card.attribution.first_critical_source}
						)
					</div>
					{card.attribution.errors.map((e) => (
						<div key={`${e.severity}-${e.source}-${e.desc}`} className="text-xs text-text-muted">
							<Pill tone={e.severity === "critical" ? "danger" : "warning"}>{e.severity}</Pill>{" "}
							来源=
							{SOURCE_LABEL[e.source] ?? e.source} · {e.desc}
						</div>
					))}
				</div>
			) : null}
			{card.judge_trace?.length ? (
				<div className="border-t border-surface-border pt-2">
					<JudgeTraceSection trace={card.judge_trace} />
				</div>
			) : null}
		</div>
	);
}

/** 判官过程(C7-T3):摊开每次判官 LLM 的真实 I/O —— 送入 system/user prompt + 裁决 JSON。 */
function JudgeTraceSection({ trace }: { trace: JudgeTraceEntry[] }) {
	return (
		<details className="rounded-md border border-surface-border">
			<summary className="cursor-pointer px-2 py-1.5 text-xs font-medium text-text-secondary">
				判官过程 · 真实 LLM I/O({trace.length} 次判官调用 · 点开看送入 prompt 与裁决)
			</summary>
			<div className="space-y-3 px-2 pb-2">
				{trace.map((t, i) => (
					<div
						key={`${t.kind}-${i}`}
						className="space-y-1.5 rounded border border-surface-border p-2"
					>
						<div className="flex items-center gap-2 text-xs">
							<Pill tone={t.kind === "nl" ? "info" : "warning"}>{t.label}</Pill>
							<code className="font-mono text-text-muted">{t.model}</code>
						</div>
						<details>
							<summary className="cursor-pointer text-xs text-text-muted">system prompt</summary>
							<pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-surface-muted/50 p-2 font-mono text-[11px] leading-4 text-text-secondary">
								{t.system}
							</pre>
						</details>
						<details>
							<summary className="cursor-pointer text-xs text-text-muted">
								user prompt(注入后实际送入)
							</summary>
							<pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-surface-muted/50 p-2 font-mono text-[11px] leading-4 text-text-secondary">
								{t.user}
							</pre>
						</details>
						<div>
							<div className="text-xs text-text-muted">裁决(判官返回 JSON)</div>
							<pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-surface-muted/50 p-2 font-mono text-[11px] leading-4 text-text-secondary">
								{JSON.stringify(t.result, null, 2)}
							</pre>
						</div>
					</div>
				))}
			</div>
		</details>
	);
}

function EvalDrawer({
	card,
	onClose,
	headerExtra,
}: {
	card: SimCard | null;
	onClose: () => void;
	headerExtra?: ReactNode;
}) {
	const turns = card ? turnViewsToDrawer(card.turns ?? []) : [];
	const scnList = useSimScenarios().data?.scenarios ?? [];
	const scn = card ? scnList.find((s) => s.id === card.scenario_id) : undefined;
	return (
		<DrawerPrimitive.Root open={!!card} onOpenChange={(o) => !o && onClose()}>
			<DrawerPrimitive.Portal>
				<DrawerPrimitive.Overlay className="ui-overlay fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
				<DrawerPrimitive.Content className="ui-drawer fixed right-0 top-0 z-50 flex h-full w-[min(1240px,95vw)] flex-col border-l border-surface-border bg-surface-background shadow-xl focus:outline-none">
					<header className="flex items-center gap-3 border-b border-surface-border px-4 py-3">
						<DrawerPrimitive.Title className="text-sm font-semibold text-text-primary">
							{card?.scenario_id} · 单对话评分卡
						</DrawerPrimitive.Title>
						{card ? <Pill tone={rewardTone(card.reward)}>reward {card.reward}</Pill> : null}
						{card ? (
							<span className="text-xs text-text-muted">
								persona={card.persona ?? "—"} · {card.termination_reason}
							</span>
						) : null}
						{headerExtra}
						<DrawerPrimitive.Close
							className="ml-auto rounded-md p-1 text-text-muted hover:bg-surface-muted"
							aria-label="关闭"
						>
							<X className="h-4 w-4" />
						</DrawerPrimitive.Close>
					</header>
					{scn ? (
						<details className="border-b border-surface-border px-4 py-2 text-sm">
							<summary className="cursor-pointer text-xs font-semibold text-text-primary">
								场景脚本 + 判分契约 — 展开
							</summary>
							<div className="mt-2 grid grid-cols-1 gap-2 lg:grid-cols-2">
								<div className="rounded-md bg-surface-subtle/60 p-2">
									<div className="mb-1 text-[11px] font-bold text-text-secondary">场景脚本</div>
									<FourPartView s={scn} />
								</div>
								<div className="rounded-md bg-surface-subtle/60 p-2">
									<div className="mb-1 text-[11px] font-bold text-text-secondary">判分契约</div>
									<JudgeContract ev={scn.eval} />
								</div>
							</div>
						</details>
					) : null}
					{card ? <EvalScorecard card={card} /> : null}
					{card ? (
						<ExecutionColumns
							turns={turns}
							customerId={scn?.customer_id ?? "cust_demo"}
							resetKey={1}
							className="flex-1"
						/>
					) : null}
				</DrawerPrimitive.Content>
			</DrawerPrimitive.Portal>
		</DrawerPrimitive.Root>
	);
}

/* ====================== Tab:评测场景(题库浏览 · 四件套+契约+关联数据) ====================== */
// 阶段(SimScenario.stage)色彩 = 场景类型(Scenario.type)售前/售中/售后 同义,复用一份,避免双维护漂移
const STAGE_TONE = TYPE_TONE;
const DIFF_TONE: Record<string, Tone> = {
	顺畅: "muted",
	缺信息: "info",
	多候选: "info",
	已发货不可: "warning",
	越权诱导: "warning",
	红线: "danger",
};

function FilterSel({
	label,
	value,
	onChange,
	opts,
}: {
	label: string;
	value: string;
	onChange: (v: string) => void;
	opts: string[];
}) {
	return (
		<span className="flex items-center gap-1">
			{label}
			<StyledSelect aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
				<option value="">全部</option>
				{opts.map((o) => (
					<option key={o} value={o}>
						{o}
					</option>
				))}
			</StyledSelect>
		</span>
	);
}

/** 场景筛选(共用:场景库浏览 + 评测发起选题) —— 阶段/意图/难度/品类/已知问题 */
type ScenFilter = {
	stage: string;
	intent: string;
	difficulty: string;
	category: string;
	issue: string; // ""=全部 / "有" / "无"
	live: string; // ""=全部 / "真实可测" / "仅罐头"
};
const EMPTY_SCEN_FILTER: ScenFilter = {
	stage: "",
	intent: "",
	difficulty: "",
	category: "",
	issue: "",
	live: "",
};
const uniqStr = (xs: string[]) => Array.from(new Set(xs.filter(Boolean)));
function filterScenarios(scenarios: SimScenario[], f: ScenFilter): SimScenario[] {
	return scenarios.filter(
		(s) =>
			(!f.stage || s.stage === f.stage) &&
			(!f.intent || s.intent === f.intent) &&
			(!f.difficulty || s.difficulty === f.difficulty) &&
			(!f.category || (s.category ?? "") === f.category) &&
			(!f.issue || (f.issue === "有" ? !!s.known_issue : !s.known_issue)) &&
			(!f.live || (f.live === "真实可测" ? !!s.live?.ready : !s.live?.ready)),
	);
}

/** live 可测标识:原样可测 / 映射可测(悬停看单号映射) / 仅罐头(悬停看原因) */
function LiveReadyPill({ live }: { live?: SimScenarioLive }) {
	if (!live) return null;
	if (!live.ready)
		return (
			<span title={live.reason}>
				<Pill tone="muted">仅罐头</Pill>
			</span>
		);
	const map = Object.entries(live.order_map ?? {})
		.map(([a, b]) => `${a}→${b}`)
		.join(" ");
	return (
		<span title={live.mode === "mapped" ? `${live.reason}:${map}` : live.reason}>
			<Pill tone="success">{live.mode === "mapped" ? "真实可测(映射)" : "真实可测"}</Pill>
		</span>
	);
}
function ScenarioFilters({
	scenarios,
	value,
	onChange,
}: {
	scenarios: SimScenario[];
	value: ScenFilter;
	onChange: (f: ScenFilter) => void;
}) {
	const set = (patch: Partial<ScenFilter>) => onChange({ ...value, ...patch });
	return (
		<div className="flex flex-wrap items-center gap-3 text-xs">
			<FilterSel
				label="阶段"
				value={value.stage}
				onChange={(v) => set({ stage: v })}
				opts={uniqStr(scenarios.map((s) => s.stage))}
			/>
			<FilterSel
				label="意图"
				value={value.intent}
				onChange={(v) => set({ intent: v })}
				opts={uniqStr(scenarios.map((s) => s.intent))}
			/>
			<FilterSel
				label="难度"
				value={value.difficulty}
				onChange={(v) => set({ difficulty: v })}
				opts={uniqStr(scenarios.map((s) => s.difficulty))}
			/>
			<FilterSel
				label="品类"
				value={value.category}
				onChange={(v) => set({ category: v })}
				opts={uniqStr(scenarios.map((s) => s.category ?? ""))}
			/>
			<FilterSel
				label="已知问题"
				value={value.issue}
				onChange={(v) => set({ issue: v })}
				opts={["有", "无"]}
			/>
			<FilterSel
				label="真实环境"
				value={value.live}
				onChange={(v) => set({ live: v })}
				opts={["真实可测", "仅罐头"]}
			/>
		</div>
	);
}

/** 四件套全文 */
function FourPartView({ s }: { s: SimScenario }) {
	const rows: [string, string][] = [
		["来意", s.reason_for_call],
		["已知", s.known_info],
		["未知(摩擦)", s.unknown_info],
		["脾气/行为", s.task_instructions],
	];
	return (
		<div className="space-y-1 text-xs">
			{rows.map(([k, v]) => (
				<div key={k}>
					<span className="text-text-muted">{k}:</span> {v || "—"}
				</div>
			))}
		</div>
	);
}

/** 关联基础数据:本题挂的客户/订单/商品,从 catalog seed 查实并显示状态 */
function DataRefsView({ s }: { s: SimScenario }) {
	const cat = useCatalog();
	const seed = cat.data?.seed;
	if (!seed) return <div className="text-xs text-text-muted">加载关联数据…</div>;
	const cust = seed.customers.find((c) => c.customer_id === s.customer_id);
	const orders = seed.orders.filter((o) => s.data_refs.orders.some((r) => o.orderId.endsWith(r)));
	const products = seed.products.filter((p) => s.data_refs.products.includes(p.productId));
	return (
		<div className="space-y-1.5 text-xs">
			<div>
				<span className="text-text-muted">以哪个客户身份跑:</span>{" "}
				{cust ? `${cust.name}(${cust.customer_id}) · ${cust.bg}` : s.customer_id}
			</div>
			{orders.length ? (
				<div>
					<div className="text-text-muted">关联订单:</div>
					{orders.map((o) => (
						<div key={o.orderId} className="ml-2 flex flex-wrap items-center gap-1">
							<span className="font-mono">{o.orderId.split("/").pop()}</span>
							<Pill tone="muted">{o.fulfillment_status}</Pill>
							<span className="text-text-muted">{o.items}</span>
						</div>
					))}
				</div>
			) : null}
			{products.length ? (
				<div>
					<div className="text-text-muted">关联商品:</div>
					{products.map((p) => (
						<div key={p.productId} className="ml-2">
							<span className="font-mono">{p.productId}</span> {p.title}
							{p.discontinued ? "(停产)" : ""} · {p.variants.length} 变体
						</div>
					))}
				</div>
			) : null}
			{s.data_refs.orders.length || s.data_refs.products.length ? null : (
				<div className="text-text-muted">(本题不依赖具体订单/商品)</div>
			)}
		</div>
	);
}

function ScenarioDetailDrawer({
	scenario,
	onClose,
}: {
	scenario: SimScenario | null;
	onClose: () => void;
}) {
	const s = scenario;
	return (
		<DrawerPrimitive.Root open={!!s} onOpenChange={(o) => !o && onClose()}>
			<DrawerPrimitive.Portal>
				<DrawerPrimitive.Overlay className="ui-overlay fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
				<DrawerPrimitive.Content className="ui-drawer fixed right-0 top-0 z-50 flex h-full w-[min(960px,95vw)] flex-col overflow-y-auto border-l border-surface-border bg-surface-background shadow-xl focus:outline-none">
					<header className="flex items-center gap-2 border-b border-surface-border px-4 py-3">
						<DrawerPrimitive.Title className="text-sm font-semibold text-text-primary">
							{s?.id}
						</DrawerPrimitive.Title>
						{s ? <Pill tone={STAGE_TONE[s.stage] ?? "muted"}>{s.stage}</Pill> : null}
						{s ? <Pill tone="info">{intentZh(s.intent)}</Pill> : null}
						{s ? <Pill tone={DIFF_TONE[s.difficulty] ?? "muted"}>{s.difficulty}</Pill> : null}
						{s?.category ? <Pill tone="info">{s.category}</Pill> : null}
						<DrawerPrimitive.Close
							className="ml-auto rounded-md p-1 text-text-muted hover:bg-surface-muted"
							aria-label="关闭"
						>
							<X className="h-4 w-4" />
						</DrawerPrimitive.Close>
					</header>
					{s ? (
						<div className="space-y-3 p-4">
							<div className="text-sm text-text-secondary">{s.desc}</div>
							{s.live ? (
								<div className="flex flex-wrap items-center gap-1.5 text-xs text-text-muted">
									<LiveReadyPill live={s.live} />
									<span>
										{s.live.reason}
										{s.live.mode === "mapped" && s.live.order_map
											? ` · 单号映射:${Object.entries(s.live.order_map)
													.map(([a, b]) => `${a}→${b}`)
													.join("、")}`
											: ""}
									</span>
								</div>
							) : null}
							{s.known_issue ? (
								s.known_issue.cat.startsWith("已") ? (
									<div className="rounded-lg border border-brand-success/30 bg-brand-success/10 p-3 text-xs">
										<div className="mb-1 font-semibold text-brand-success">
											✓ {s.known_issue.cat}(历史诊断已解,留档防回归)
										</div>
										<div className="text-text-secondary">{s.known_issue.note}</div>
										<div className="mt-0.5 text-text-muted">来源:{s.known_issue.ref}</div>
									</div>
								) : (
									<div className="rounded-lg border border-brand-warning/30 bg-brand-warning/10 p-3 text-xs">
										<div className="mb-1 font-semibold text-brand-warning">
											⚠ 已知问题·停滞原因 · {s.known_issue.cat}
										</div>
										<div className="text-text-secondary">{s.known_issue.note}</div>
										<div className="mt-0.5 text-text-muted">来源:{s.known_issue.ref}</div>
									</div>
								)
							) : null}
							<Section title="① 场景脚本(四件套)">
								<FourPartView s={s} />
								<div className="mt-1 text-xs text-text-muted">
									建议性格 persona:{s.persona ?? "随机抽(稳健性)"}
								</div>
							</Section>
							<Section title="② 判分契约(怎么算过)">
								<JudgeContract ev={s.eval} />
							</Section>
							<Section title="③ 关联基础数据">
								<DataRefsView s={s} />
							</Section>
						</div>
					) : null}
				</DrawerPrimitive.Content>
			</DrawerPrimitive.Portal>
		</DrawerPrimitive.Root>
	);
}

function Section({ title, children }: { title: string; children: ReactNode }) {
	// C25 批3(C18 档C 消重框):小节去描边,靠浅底+小标题分区
	return (
		<div className="rounded-lg bg-surface-subtle/50 p-3">
			<div className="mb-1.5 text-xs font-semibold text-text-primary">{title}</div>
			{children}
		</div>
	);
}

function ScenarioGroupStats({ scenarios }: { scenarios: SimScenario[] }) {
	// M4:分组统计条——每组数量/指南契约覆盖率(有断言或召回声明的场景占比)
	const groups = Array.from(new Set(scenarios.map((s) => s.stage || "?")));
	const withContract = (list: SimScenario[]) =>
		list.filter(
			(s) =>
				(s.eval?.guidance_assertions?.length ?? 0) > 0 ||
				(s.eval?.guidance_recall_expected?.length ?? 0) > 0,
		).length;
	const sets = useTestSets();
	return (
		<div className="flex flex-wrap gap-3 rounded-lg bg-surface-subtle/60 p-2.5 text-xs text-text-secondary">
			<span>
				共 <b className="text-text-primary">{scenarios.length}</b> 道 · 指南契约覆盖{" "}
				<b className="text-text-primary">{withContract(scenarios)}</b>
			</span>
			{(sets.data?.sets ?? []).map((t) => (
				<span key={t.name} title={t.desc ?? t.name}>
					{t.name} <b className="text-text-primary">{t.n}</b>
				</span>
			))}
			{groups.map((g) => {
				const list = scenarios.filter((s) => (s.stage || "?") === g);
				return (
					<span key={g} title={`${g}:${list.length} 道,其中 ${withContract(list)} 道带指南契约`}>
						{g} <b className="text-text-primary">{list.length}</b>
						<span className="text-text-muted">({withContract(list)}契约)</span>
					</span>
				);
			})}
		</div>
	);
}

function EvalScenariosTab() {
	const q = useSimScenarios();
	const scenarios = q.data?.scenarios ?? [];
	const [filter, setFilter] = useState<ScenFilter>(EMPTY_SCEN_FILTER);
	const [sel, setSel] = useState<SimScenario | null>(null);
	const shown = filterScenarios(scenarios, filter);
	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	if (q.isError)
		return (
			<Card>
				<CardContent className="py-6 text-sm text-brand-danger">评测题库加载失败</CardContent>
			</Card>
		);
	return (
		<Card>
			<CardHeader>
				<CardTitle>共 {scenarios.length} 题</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				<p className="text-xs text-text-muted">
					模拟场景的题库:机器顾客按「四件套」扮演,判分按每题「契约」。点一题看全文 + 判分标准 +
					关联的客户/订单/商品状态。去「模拟场景」选题跑。
				</p>
				<ScenarioGroupStats scenarios={scenarios} />
				<div className="flex flex-wrap items-center gap-3 text-xs">
					<ScenarioFilters scenarios={scenarios} value={filter} onChange={setFilter} />
					<span className="self-center text-text-muted">共 {shown.length} 题</span>
				</div>
				{shown.length === 0 ? (
					<div className="rounded-md border border-dashed border-surface-border p-3 text-center text-xs text-text-muted">
						{scenarios.length === 0 ? "暂无评测场景" : "当前筛选无场景"}
					</div>
				) : null}
				<div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
					{shown.map((s) => (
						<button
							key={s.id}
							type="button"
							onClick={() => setSel(s)}
							className="rounded-lg border border-surface-border p-2.5 text-left text-sm hover:bg-surface-muted/40"
						>
							<div className="flex flex-wrap items-center gap-1.5">
								<span className="font-medium text-text-primary">{s.id}</span>
								<Pill tone={STAGE_TONE[s.stage] ?? "muted"}>{s.stage}</Pill>
								<Pill tone="info">{intentZh(s.intent)}</Pill>
								<Pill tone={DIFF_TONE[s.difficulty] ?? "muted"}>{s.difficulty}</Pill>
								{s.category ? <Pill tone="info">{s.category}</Pill> : null}
								<LiveReadyPill live={s.live} />
								{s.known_issue ? (
									<Pill tone={s.known_issue.cat.startsWith("已") ? "success" : "warning"}>
										{s.known_issue.cat.startsWith("已") ? "✓ 已解" : "⚠ 已知问题"}
									</Pill>
								) : null}
							</div>
							<div className="mt-1 text-xs text-text-muted">{s.desc}</div>
							<div className="mt-0.5 truncate text-xs text-text-muted">
								来意:{s.reason_for_call}
							</div>
						</button>
					))}
				</div>
				<ScenarioDetailDrawer scenario={sel} onClose={() => setSel(null)} />
			</CardContent>
		</Card>
	);
}

/* ====================== Tab:试跑(交互 · 单/多轮一套引擎) ====================== */
const RESUME_LABEL: Record<string, string> = {
	new: "新轮",
	provide: "补槽",
	confirm: "确认",
	cancel: "反悔",
	switch: "切换",
};

function PlaygroundTab({
	hasKey,
	pendingLoad,
	onConsumeLoad,
}: {
	hasKey: boolean;
	pendingLoad: ScenarioLoad | null;
	onConsumeLoad: () => void;
}) {
	const turn = useSessionTurn();
	const cat = useCatalog();
	const ov = useScenarios();
	const seedCustomers = cat.data?.seed.customers ?? [];
	const loadable = (ov.data?.scenarios ?? []).filter((s) => scenarioToLoad(s));
	const [customer, setCustomer] = useState("cust_demo");
	const [state, setState] = useState<Record<string, unknown> | null>(null);
	const [timeline, setTimeline] = useState<TurnView[]>([]);
	const [pending, setPending] = useState<SessionPending>(null);
	const [focus, setFocus] = useState<SessionFocus>({ focus_order: null, focus_product: null });
	const [msg, setMsg] = useState("");
	const [queue, setQueue] = useState<string[]>([]);
	const [loadedFrom, setLoadedFrom] = useState<string | null>(null);
	const [err, setErr] = useState<string | null>(null);
	const [pendingMsg, setPendingMsg] = useState<string | null>(null); // 已发出、正在调模型(乐观气泡)

	function reset(nextCustomer?: string) {
		setState(null);
		setTimeline([]);
		setPending(null);
		setFocus({ focus_order: null, focus_product: null });
		setQueue([]);
		setMsg("");
		setLoadedFrom(null);
		setErr(null);
		setPendingMsg(null);
		if (nextCustomer) setCustomer(nextCustomer);
	}

	async function sendOne(text: string, fromState: Record<string, unknown> | null, cust: string) {
		const d = await turn.mutateAsync({ state: fromState, message: text, customer_id: cust });
		setState(d.state);
		setTimeline((t) => [...t, d.turn]);
		setPending(d.pending);
		setFocus(d.focus);
		return d.state;
	}

	async function send() {
		const text = msg.trim();
		if (!text) return;
		setMsg("");
		setErr(null);
		setPendingMsg(text); // 乐观:用户气泡立即出现 + loading,不等结果
		try {
			await sendOne(text, state, customer);
			// 场景载入逐句:发完把下一句填进输入框(可改可发)
			setQueue((q) => {
				const next = q[0];
				if (next !== undefined) setMsg(next);
				return q.slice(1);
			});
		} catch (e) {
			setErr((e as { message?: string })?.message ?? String(e));
		} finally {
			setPendingMsg(null);
		}
	}

	// 从场景库载入:用该场景客户重开会话,首句直接填进输入框,其余排队逐句续填
	function loadScenario(load: ScenarioLoad) {
		reset(load.customer_id);
		setLoadedFrom(load.id);
		setMsg(load.msgs[0] ?? "");
		setQueue(load.msgs.slice(1));
	}

	// 跨 tab「试跑」载入
	// biome-ignore lint/correctness/useExhaustiveDependencies: 仅在收到跨 tab 载入时执行一次
	useEffect(() => {
		if (pendingLoad) {
			loadScenario(pendingLoad);
			onConsumeLoad();
		}
	}, [pendingLoad]);

	const inputFooter = (
		<div className="space-y-1.5">
			{err ? <p className="px-1 text-xs text-brand-danger">{err}</p> : null}
			<div className="flex gap-2">
				<input
					className="flex-1 rounded-md border border-surface-border bg-surface-card px-3 py-2 text-sm text-text-primary"
					placeholder={hasKey ? "输入用户消息,回车发送…" : "需配置 LLM key"}
					value={msg}
					disabled={!hasKey || turn.isPending}
					onChange={(e) => setMsg(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") send();
					}}
				/>
				<Button type="button" disabled={!hasKey || turn.isPending} onClick={send}>
					{turn.isPending ? "运行中…" : "发送"}
				</Button>
			</div>
			{queue.length ? (
				<div className="px-1 text-[11px] text-text-muted">
					场景待发还有 {queue.length} 句(发完自动填入下一句)
				</div>
			) : null}
		</div>
	);

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex flex-wrap items-center justify-between gap-2">
					<Button type="button" variant="outline" size="sm" onClick={() => reset()}>
						新会话
					</Button>
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				{/* 客户身份 + 场景载入 + 会话状态 */}
				<div className="flex flex-wrap items-center gap-3 rounded-lg bg-surface-subtle/60 p-3">
					<span className="flex items-center gap-1.5 text-xs text-text-secondary">
						<span>客户身份</span>
						<StyledSelect
							aria-label="客户身份"
							value={customer}
							onChange={(e) => reset(e.target.value)}
							title="切换客户会重开会话(身份在会话首轮固定,真注入运行决定查谁的单)"
						>
							{seedCustomers.map((c) => (
								<option key={c.customer_id} value={c.customer_id}>
									{c.customer_id} · {c.name}({c.order_count}单)
								</option>
							))}
						</StyledSelect>
					</span>
					<span className="flex items-center gap-1.5 text-xs text-text-secondary">
						<span>从场景库载入</span>
						<StyledSelect
							aria-label="从场景库载入"
							className="max-w-[260px]"
							value=""
							onChange={(e) => {
								const s = loadable.find((x) => x.id === e.target.value);
								const load = s ? scenarioToLoad(s) : null;
								if (load) loadScenario(load);
							}}
						>
							<option value="">— 选一条场景(填入问题,可改可发)—</option>
							{loadable.map((s) => (
								<option key={s.id} value={s.id}>
									{s.id} · {s.desc}
								</option>
							))}
						</StyledSelect>
					</span>
					{loadedFrom ? <Pill tone="info">已载入 {loadedFrom}</Pill> : null}
					<span className="ml-auto flex items-center gap-2 text-xs">
						{pending ? (
							<Pill tone="info">
								挂起 · {pending.kind === "confirm" ? "待确认写操作" : `等补:${pending.slot ?? "?"}`}
							</Pill>
						) : (
							<Pill tone="muted">无挂起</Pill>
						)}
						<span className="text-text-muted">
							焦点单 {focus.focus_order ?? "—"} · 焦点品 {focus.focus_product ?? "—"}
						</span>
					</span>
				</div>
				<p className="text-xs leading-5 text-text-secondary">
					一套引擎:<strong>发一句 = 单轮</strong>,<strong>接着发 = 多轮</strong>
					(反问→补槽→确认执行 / 反悔 / 焦点携带)。左栏输入并看对话,中栏看环节链,右栏点环节看完整 I/O
					—— 与「详情」抽屉同一套三栏。真 LLM 输出会波动,不打红绿。
				</p>
				{/* 三栏(与详情同组件):① 聊天带输入 ② 环节 ③ 详情 */}
				<ExecutionColumns
					turns={
						pendingMsg
							? [...turnViewsToDrawer(timeline), { msg: pendingMsg, pending: true }]
							: turnViewsToDrawer(timeline)
					}
					customerId={customer}
					chatFooter={inputFooter}
					chatEmptyHint={
						hasKey
							? '选客户 → 输入一句话开始(单轮),或从场景库载入。例:"退订单1001的款" → 停在确认门,再说"确认"才真执行。'
							: "需配置 LLM key 才能真跑。"
					}
					focusLast
					className="h-[68vh] rounded-lg border border-surface-border"
				/>
			</CardContent>
		</Card>
	);
}

/* ====================== Tab:历史记录 ====================== */
function shortTime(ts: string): string {
	return ts.replace("T", " ").slice(0, 19);
}

const HIST_PAGE = 25;

const NODE_ZH: Record<string, string> = {
	n1_intent: "N1 意图",
	n2_decide: "N2 决策",
	n3_generate: "N3 生成",
	n1_resume: "N1 接续",
	"B2.5": "B2.5 组装",
	G1: "G1 必填",
	G4: "G4 判停",
	G6: "G6 排除",
	offline: "离线说明",
};

/** 耗时统计卡(读 /timing 聚合底表):总耗时分布 + 分桶 + 每节点 p50/p95 */
function TimingStatsCard({ days }: { days: number }) {
	const [showAll, setShowAll] = useState(false);
	const q = useTiming(days);
	const d = q.data;
	if (!d || d.samples === 0) {
		return (
			<div className="rounded-md border border-surface-border p-3 text-xs text-text-muted">
				该窗口暂无样本(跑一批后此处给出核心 LLM 节点 avg/p95 与次数;可切「全部」看历史)。
			</div>
		);
	}
	const nodes = Object.entries(d.by_node);
	const coreNodes = nodes.filter(([, v]) => v.kind === "llm"); // N1意图/N2决策/N3生成(接续已并入N1)
	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center gap-3">
				<span className="text-xs text-text-muted">{d.samples} 轮样本 · 每 30s 自动刷新</span>
				<Pill tone={msTone(d.total_ms.p95)}>总 p50 {fmtMs(d.total_ms.p50)}</Pill>
				<Pill tone={msTone(d.total_ms.p95)}>总 p95 {fmtMs(d.total_ms.p95)}</Pill>
			</div>
			{/* 核心 LLM 节点卡:N1 意图 / N2 决策 / N3 生成(接续已并入 N1)*/}
			<div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
				{coreNodes.length ? (
					coreNodes.map(([node, v]) => (
						<div key={node} className="rounded-xl border border-surface-border bg-surface-card p-3">
							<div className="flex items-center justify-between">
								<span className="text-xs font-medium text-text-primary">
									{NODE_ZH[node] ?? node}
								</span>
								<span className="text-[11px] text-text-muted">{v.count} 次</span>
							</div>
							<div className="mt-1 flex items-baseline gap-2">
								<span className="text-xl font-semibold text-text-primary">{fmtMs(v.avg)}</span>
								<span className="text-[11px] text-text-muted">avg</span>
								<Pill tone={msTone(v.p95)}>p95 {fmtMs(v.p95)}</Pill>
							</div>
						</div>
					))
				) : (
					<div className="text-xs text-text-muted">本范围无 LLM 节点样本。</div>
				)}
			</div>
			{/* 全部节点(含规则/工具 ≈0ms,默认折叠)*/}
			<div>
				<button
					type="button"
					className="text-xs text-brand-primary hover:underline"
					onClick={() => setShowAll((v) => !v)}
				>
					{showAll ? "收起" : "展开"}全部节点(含规则/工具,通常 ≈0ms)
				</button>
				{showAll ? (
					<div className="mt-2 overflow-x-auto rounded-md border border-surface-border">
						<table className="w-full text-xs">
							<thead className="bg-surface-muted text-text-secondary">
								<tr>
									<th className="px-2 py-1 text-left font-medium">节点</th>
									<th className="px-2 py-1 text-left font-medium">次数</th>
									<th className="px-2 py-1 text-left font-medium">avg</th>
									<th className="px-2 py-1 text-left font-medium">p50</th>
									<th className="px-2 py-1 text-left font-medium">p95</th>
									<th className="px-2 py-1 text-left font-medium">max</th>
								</tr>
							</thead>
							<tbody>
								{nodes.map(([node, v]) => (
									<tr key={node} className="border-t border-surface-border">
										<td className="px-2 py-1">
											<Pill
												tone={v.kind === "llm" ? "info" : v.kind === "tool" ? "success" : "muted"}
											>
												{NODE_ZH[node] ?? node}
											</Pill>
										</td>
										<td className="px-2 py-1 text-text-secondary">{v.count}</td>
										<td className="px-2 py-1 font-mono">{fmtMs(v.avg)}</td>
										<td className="px-2 py-1 font-mono">{fmtMs(v.p50)}</td>
										<td className="px-2 py-1 font-mono">{fmtMs(v.p95)}</td>
										<td className="px-2 py-1 font-mono">{fmtMs(v.max)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				) : null}
			</div>
		</div>
	);
}

/* ====================== Tab:统计(耗时 / 应答效果) ====================== */
function SubTabBtn({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors duration-150 ${
				active
					? "bg-brand-accent/10 text-brand-accent"
					: "text-text-secondary hover:text-text-primary"
			}`}
		>
			{children}
		</button>
	);
}

/** 统计时间窗口选择器(所有统计子页签共用)· 预设档:近3/7/14/30天·全部,默认近14天。 */
const WINDOW_OPTS = [
	{ value: "3", label: "近 3 天" },
	{ value: "7", label: "近 7 天" },
	{ value: "14", label: "近 14 天" },
	{ value: "30", label: "近 30 天" },
	{ value: "0", label: "全部" },
];
function WindowSelect({ days, onChange }: { days: number; onChange: (d: number) => void }) {
	return (
		<Select
			label="时间窗口"
			value={String(days)}
			onChange={(v) => onChange(Number(v))}
			options={WINDOW_OPTS}
		/>
	);
}

type StatsSub = "overview" | "timing" | "answer" | "eval" | "rca" | "cx" | "token" | "judge";
const STATS_SUBS: { key: StatsSub; label: string }[] = [
	{ key: "overview", label: "概览" },
	{ key: "timing", label: "耗时统计" },
	{ key: "answer", label: "应答效果" },
	{ key: "eval", label: "场景评测" },
	{ key: "rca", label: "归因分布" },
	{ key: "cx", label: "CX 评分" },
	{ key: "token", label: "Token 用量" },
	{ key: "judge", label: "判官标定" },
];

function StatsTab({
	onDrill,
	onGotoCx,
}: { onDrill: (p: HistPreset) => void; onGotoCx: () => void }) {
	const [sub, setSub] = useState<StatsSub>("overview");
	// C35:时间窗上提,一处切换全子页签生效
	const [days, setDays] = useState(DEFAULT_STATS_DAYS);
	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex flex-wrap gap-1 rounded-full border border-surface-border p-0.5">
						{STATS_SUBS.map((s) => (
							<SubTabBtn key={s.key} active={sub === s.key} onClick={() => setSub(s.key)}>
								{s.label}
							</SubTabBtn>
						))}
					</div>
					<WindowSelect days={days} onChange={setDays} />
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				{sub === "overview" ? (
					<OverviewPanel days={days} onGoto={setSub} />
				) : sub === "timing" ? (
					<>
						<p className="text-xs text-text-muted">
							核心看三个 LLM 节点(N1 意图 / N2 决策 / N3 生成;接续已并入 N1)的 avg/p95 与次数;规则 /
							工具(≈0ms)折叠。每 30s 自动刷新。
						</p>
						<TimingStatsCard days={days} />
					</>
				) : sub === "answer" ? (
					<AnswerStatsPanel days={days} onDrill={onDrill} />
				) : sub === "eval" ? (
					<EvalStatsPanel days={days} />
				) : sub === "rca" ? (
					<RcaStatsPanel days={days} />
				) : sub === "cx" ? (
					<CxStatsPanel days={days} onGotoRecords={onGotoCx} />
				) : sub === "judge" ? (
					<JudgeCalibrationPanel />
				) : (
					<TokenStatsPanel days={days} />
				)}
			</CardContent>
		</Card>
	);
}

/** 大模型节点 → 中文名(与 token_store.STAGES 一致) */
const TOKEN_STAGE_ZH: Record<string, string> = {
	n1_intent: "N1 意图",
	n2_decide: "N2 决策",
	n3_generate: "N3 生成",
	actor: "演员(机器顾客)",
	judge: "判官(评测)",
	rca: "归因(RCA)",
};
const tokenStageZh = (k: string) => TOKEN_STAGE_ZH[k] ?? k;
const fmtTok = (n: number) => n.toLocaleString("en-US");

/** Token 用量统计:每次大模型节点运行的 token(输入/输出/合计)· KPI + 按节点/模型/天分组 + 明细 */
/** Token 节点 → 图表色(分类色固定绑定实体,不按排名重涂;溢出 5 片由 Donut 折叠「其他」) */
const STAGE_COLOR: Record<string, string> = {
	n1_intent: CHART.info,
	n2_decide: CHART.accent,
	n3_generate: CHART.warning,
	actor: CHART.success,
	judge: CHART.danger,
	rca: CHART.inkSecondary,
	cx: CHART.inkMuted,
};

function TokenStatsPanel({ days }: { days: number }) {
	const [dim, setDim] = useState<"by_stage" | "by_model" | "by_date">("by_stage");
	const [showDetail, setShowDetail] = useState(false);
	const q = useTokenStats(days);
	const d = q.data;

	if (q.isLoading) return <LoadingInline />;
	if (!d || d.samples === 0) {
		return (
			<div className="rounded-md border border-surface-border p-4 text-sm text-text-muted">
				该窗口暂无 token 明细。用真 LLM 跑「试跑」/「场景库」/「评测」/「归因」后,这里按 节点 / 模型
				/ 天 给出每次大模型节点运行消耗的 token(输入 + 输出合计);可切「全部」看历史。
			</div>
		);
	}
	const rows = d[dim];
	const dimLabel = dim === "by_stage" ? "节点" : dim === "by_model" ? "模型" : "日期";
	const labelOf = (k: string) => (dim === "by_stage" ? tokenStageZh(k) : k);
	const maxTotal = Math.max(1, ...rows.map((r) => r.tokens_total));

	return (
		<div className="space-y-4">
			<p className="text-xs text-text-muted">
				一行明细 = 一次大模型调用(N1/N2/N3 应答 + 演员 + 判官 + 归因);token 取自模型返回的真实
				usage。默认近 3 天,可切窗口,每 30s 自动刷新。
			</p>
			<div className="flex flex-wrap items-center gap-3">
				<span className="text-xs text-text-muted">{d.samples} 次调用</span>
				<Button type="button" size="sm" variant="outline" onClick={() => q.refetch()}>
					刷新
				</Button>
			</div>
			{/* KPI:总调用 / 输入 / 输出 / 合计 */}
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
				<Kpi label="调用次数" value={fmtTok(d.overall.calls)} />
				<Kpi label="输入 token" value={fmtTok(d.overall.tokens_in)} tone="info" />
				<Kpi label="输出 token" value={fmtTok(d.overall.tokens_out)} tone="warning" />
				<Kpi label="合计 token" value={fmtTok(d.overall.tokens_total)} tone="success" />
			</div>
			{/* 分组维度 toggle */}
			<div className="flex items-center gap-2">
				<span className="text-xs text-text-muted">分组维度</span>
				<div className="flex gap-1 rounded-full border border-surface-border p-0.5">
					<SubTabBtn active={dim === "by_stage"} onClick={() => setDim("by_stage")}>
						节点
					</SubTabBtn>
					<SubTabBtn active={dim === "by_model"} onClick={() => setDim("by_model")}>
						模型
					</SubTabBtn>
					<SubTabBtn active={dim === "by_date"} onClick={() => setDim("by_date")}>
						日期
					</SubTabBtn>
				</div>
			</div>
			{/* 节点维度:占比扇形(C35 · 身份型占比 ≤5 片,溢出折叠「其他」) */}
			{dim === "by_stage" ? (
				<div className="rounded-md border border-surface-border p-3">
					<div className="mb-2 text-xs font-medium text-text-secondary">节点 token 占比</div>
					<Donut
						testId="token-stage-donut"
						slices={d.by_stage.map((r) => ({
							key: r.key,
							label: tokenStageZh(r.key),
							value: r.tokens_total,
							color: STAGE_COLOR[r.key] ?? CHART.inkMuted,
						}))}
						center={fmtTok(d.overall.tokens_total)}
						centerHint="总 token"
					/>
				</div>
			) : null}
			{/* 分组明细表 */}
			<div className="overflow-x-auto rounded-lg border border-surface-border">
				<table className="w-full text-sm">
					<thead className="bg-surface-muted text-text-secondary">
						<tr>
							<th className="px-3 py-2 text-left font-medium">{dimLabel}</th>
							<th className="px-3 py-2 text-left font-medium">调用</th>
							<th className="px-3 py-2 text-left font-medium">输入</th>
							<th className="px-3 py-2 text-left font-medium">输出</th>
							<th className="px-3 py-2 text-left font-medium">合计</th>
							<th className="px-3 py-2 text-left font-medium">占比</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((r: TokenStatsRow) => (
							<tr key={r.key} className="border-t border-surface-border hover:bg-surface-muted/40">
								<td className="px-3 py-2 font-medium text-text-primary">{labelOf(r.key)}</td>
								<td className="px-3 py-2 font-mono text-text-secondary">{r.calls}</td>
								<td className="px-3 py-2 font-mono text-text-secondary">{fmtTok(r.tokens_in)}</td>
								<td className="px-3 py-2 font-mono text-text-secondary">{fmtTok(r.tokens_out)}</td>
								<td className="px-3 py-2 font-mono font-medium text-text-primary">
									{fmtTok(r.tokens_total)}
								</td>
								<td className="px-3 py-2">
									<div className="flex items-center gap-2">
										<MiniBar value={r.tokens_total} max={maxTotal} className="w-16" />
										<span className="font-mono text-xs text-text-muted">
											{Math.round((r.tokens_total / Math.max(1, d.overall.tokens_total)) * 100)}%
										</span>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<Button type="button" size="sm" variant="outline" onClick={() => setShowDetail((v) => !v)}>
				{showDetail ? "收起明细" : "查看明细(每次调用)"}
			</Button>
			{showDetail ? <TokenDetailTable days={days} /> : null}
		</div>
	);
}

/** Token 明细表:每次大模型节点运行一条(最新在前) */
function TokenDetailTable({ days }: { days: number }) {
	const q = useTokenRecords(days);
	if (q.isLoading) return <LoadingInline />;
	const recs = q.data?.records ?? [];
	if (recs.length === 0) {
		return (
			<div className="rounded-md border border-surface-border p-3 text-xs text-text-muted">
				无明细。
			</div>
		);
	}
	return (
		<div className="max-h-96 overflow-auto rounded-lg border border-surface-border">
			<table className="w-full text-xs">
				<thead className="sticky top-0 bg-surface-muted text-text-secondary">
					<tr>
						<th className="px-2 py-1.5 text-left font-medium">时间</th>
						<th className="px-2 py-1.5 text-left font-medium">节点</th>
						<th className="px-2 py-1.5 text-left font-medium">模型</th>
						<th className="px-2 py-1.5 text-left font-medium">输入</th>
						<th className="px-2 py-1.5 text-left font-medium">输出</th>
						<th className="px-2 py-1.5 text-left font-medium">合计</th>
						<th className="px-2 py-1.5 text-left font-medium">耗时</th>
					</tr>
				</thead>
				<tbody>
					{recs.map((r, i) => (
						<tr
							key={`${r.ts}-${i}`}
							className="border-t border-surface-border hover:bg-surface-muted/40"
						>
							<td className="px-2 py-1 font-mono text-text-muted">
								{r.ts.slice(5, 19).replace("T", " ")}
							</td>
							<td className="px-2 py-1">
								<Pill tone="info">{tokenStageZh(r.stage)}</Pill>
							</td>
							<td className="px-2 py-1 text-text-secondary">{r.model}</td>
							<td className="px-2 py-1 font-mono text-text-secondary">{fmtTok(r.prompt_tokens)}</td>
							<td className="px-2 py-1 font-mono text-text-secondary">
								{fmtTok(r.completion_tokens)}
							</td>
							<td className="px-2 py-1 font-mono font-medium text-text-primary">
								{fmtTok(r.total_tokens)}
							</td>
							<td className="px-2 py-1 font-mono text-text-muted">{fmtMs(r.ms)}</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

/** persona → 中文(与 user_sim.PERSONAS 一致) */
const PERSONA_ZH: Record<string, string> = {
	standard: "标准",
	terse: "惜字如金",
	verbose: "啰嗦",
	impatient: "没耐心",
	"—": "—",
};
const EVAL_AXES = [
	{ key: "by_stage", label: "阶段" },
	{ key: "by_intent", label: "意图" },
	{ key: "by_difficulty", label: "难度" },
	{ key: "by_persona", label: "性格" },
	{ key: "by_category", label: "类别" },
] as const;

/** 场景评测跨批次统计:pass 率 / 一票否决三维 / persona 稳健性 / 阶段·意图·难度分组 / 终止 / pass^k */
function EvalStatsPanel({ days }: { days: number }) {
	const [axis, setAxis] = useState<(typeof EVAL_AXES)[number]["key"]>("by_stage");
	const q = useEvalStats(days);
	const d = q.data;

	if (q.isLoading) return <LoadingInline />;
	if (!d || d.samples === 0) {
		return (
			<div className="rounded-md border border-surface-border p-4 text-sm text-text-muted">
				该窗口暂无场景评测样本。去「评测」用真 LLM 批量跑场景后,这里给出 pass 率、一票否决三维
				(action/communicate/nl)、按 阶段/意图/难度/性格/类别 的分组通过率、终止分布与 pass^k
				一致性;可切「全部」看历史。
			</div>
		);
	}
	const rows = d[axis];
	const axisIsIntent = axis === "by_intent";
	const axisIsPersona = axis === "by_persona";
	const keyOf = (k: string) =>
		axisIsIntent ? intentZh(k) : axisIsPersona ? (PERSONA_ZH[k] ?? k) : k;
	const gstats = useGuidanceStats(days <= 7 ? 7 : 30); // M3:指南遵循率 KPI(窗口就近)
	const o = d.overall;
	const passK = Object.entries(d.pass_k_curve).sort(([a], [b]) => Number(a) - Number(b));
	const term = Object.entries(d.termination).sort(([, a], [, b]) => b - a);
	const failed = Object.entries(d.failed_dims).sort(([, a], [, b]) => b - a);

	return (
		<div className="space-y-4">
			<p className="text-xs text-text-muted">
				一次评分卡 = 一次机器顾客×被测 agent 的完整对话;reward 一票否决(三维全过才算过)。 默认近 3
				天,可切窗口,每 30s 自动刷新。
				{gstats.data?.kpi.compliance != null ? (
					<>
						{" "}
						· 指南遵循率{" "}
						<b className="text-text-primary">
							{Math.round((gstats.data.kpi.compliance ?? 0) * 100)}%
						</b>
						(详见「指南效果」页)
					</>
				) : null}
			</p>
			<div className="flex flex-wrap items-center gap-3">
				<span className="text-xs text-text-muted">{d.samples} 张评分卡</span>
				<Button type="button" size="sm" variant="outline" onClick={() => q.refetch()}>
					刷新
				</Button>
			</div>
			{/* KPI:pass 率 + 一票否决三维 */}
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
				<Kpi label="pass 率" value={pct(o.avg_reward)} tone={rateTone(o.avg_reward)} />
				<Kpi label="action 通过" value={pct(o.action)} tone={rateTone(o.action)} />
				<Kpi label="communicate 通过" value={pct(o.communicate)} tone={rateTone(o.communicate)} />
				<Kpi label="nl 通过" value={pct(o.nl)} tone={rateTone(o.nl)} />
			</div>
			{/* 次级指标 */}
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
				<Kpi label="committed 率" value={pct(o.committed_rate)} />
				<Kpi
					label="演员失职率"
					value={pct(o.actor_violation_rate)}
					tone={o.actor_violation_rate && o.actor_violation_rate > 0.1 ? "warning" : "muted"}
				/>
				<Kpi label="平均轮次" value={o.avg_turns == null ? "—" : String(o.avg_turns)} />
				<Kpi label="样本数" value={String(o.n)} />
			</div>
			{/* pass^k 一致性 */}
			{passK.length ? (
				<div className="flex flex-wrap items-center gap-2 rounded-md border border-surface-border p-3 text-xs">
					<span className="font-medium text-text-secondary">pass^k 一致性</span>
					{passK.map(([k, v]) => (
						<Pill key={k} tone={rateTone(v)}>
							pass^{k} = {pct(v)}
						</Pill>
					))}
					<span className="text-text-muted">(pass¹ 高但 pass^k 暴跌 = 不稳)</span>
				</div>
			) : null}
			{/* 分组维度 toggle */}
			<div className="flex items-center gap-2">
				<span className="text-xs text-text-muted">分组维度</span>
				<div className="flex gap-1 rounded-full border border-surface-border p-0.5">
					{EVAL_AXES.map((a) => (
						<SubTabBtn key={a.key} active={axis === a.key} onClick={() => setAxis(a.key)}>
							{a.label}
						</SubTabBtn>
					))}
				</div>
			</div>
			{/* 分组明细表 */}
			<div className="overflow-x-auto rounded-lg border border-surface-border">
				<table className="w-full text-sm">
					<thead className="bg-surface-muted text-text-secondary">
						<tr>
							<th className="px-3 py-2 text-left font-medium">
								{EVAL_AXES.find((a) => a.key === axis)?.label}
							</th>
							<th className="px-3 py-2 text-left font-medium">样本</th>
							<th className="px-3 py-2 text-left font-medium">pass 率</th>
							<th className="px-3 py-2 text-left font-medium">action</th>
							<th className="px-3 py-2 text-left font-medium">communicate</th>
							<th className="px-3 py-2 text-left font-medium">nl</th>
							<th className="px-3 py-2 text-left font-medium">committed</th>
							<th className="px-3 py-2 text-left font-medium">失职率</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((r: EvalStatRow) => (
							<tr key={r.key} className="border-t border-surface-border hover:bg-surface-muted/40">
								<td className="px-3 py-2 font-medium text-text-primary">{keyOf(r.key)}</td>
								<td className="px-3 py-2 font-mono text-text-secondary">{r.n}</td>
								<td className="px-3 py-2">
									<Pill tone={rateTone(r.avg_reward)}>{pct(r.avg_reward)}</Pill>
								</td>
								<td className="px-3 py-2 font-mono text-text-secondary">{pct(r.action)}</td>
								<td className="px-3 py-2 font-mono text-text-secondary">{pct(r.communicate)}</td>
								<td className="px-3 py-2 font-mono text-text-secondary">{pct(r.nl)}</td>
								<td className="px-3 py-2 font-mono text-text-secondary">{pct(r.committed_rate)}</td>
								<td className="px-3 py-2 font-mono text-text-secondary">
									{pct(r.actor_violation_rate)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{/* 终止分布 + failed_dims 频次 */}
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<div className="space-y-1.5 rounded-md border border-surface-border p-3">
					<div className="text-xs font-medium text-text-secondary">终止原因分布</div>
					<div className="flex flex-wrap gap-1.5">
						{term.length ? (
							term.map(([k, v]) => (
								<Pill key={k} tone="muted">
									{k} · {v}
								</Pill>
							))
						) : (
							<span className="text-xs text-text-muted">—</span>
						)}
					</div>
				</div>
				<div className="space-y-1.5 rounded-md border border-surface-border p-3">
					<div className="text-xs font-medium text-text-secondary">失败维度频次(failed_dims)</div>
					<div className="flex flex-wrap gap-1.5">
						{failed.length ? (
							failed.map(([k, v]) => (
								<Pill key={k} tone="danger">
									{k} · {v}
								</Pill>
							))
						) : (
							<span className="text-xs text-text-muted">无失败</span>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

/** 归因分布:簇 / 主归因 / 节点位次 分布 + 需复核率 + 平均置信 */
/** 判官标定:最近一次 + 历史列表 + 误判条目钻取。判读只认难档(混着报会被清晰档稀释)。 */
function JudgeCalibrationPanel() {
	const [goldSet, setGoldSet] = useState("");
	const [openId, setOpenId] = useState<string | null>(null);
	const q = useCalibrations(goldSet);
	const detail = useCalibrationDetail(openId);
	const rows = q.data?.records ?? [];
	const sets = q.data?.gold_sets ?? {};
	// 抽出来判空:noUncheckedIndexedAccess 下 rows[0] 不会被 length 检查窄化
	const latest = rows[0];

	return (
		<div className="space-y-3" data-testid="judge-calibration">
			<p className="text-xs text-text-muted">
				判官在金标集上的一致率——尺子准不准。冤杀 = 该判遵循却判违背(门控下直接扣好 case),漏网 =
				该判违背却放过。<b>不同金标集之间不可比</b>,故不做跨集平均。
			</p>
			<div className="flex items-center gap-2">
				<Select
					label="金标集"
					value={goldSet}
					onChange={setGoldSet}
					options={[
						{ value: "", label: "全部" },
						...Object.entries(sets).map(([value, label]) => ({ value, label })),
					]}
				/>
			</div>
			{q.isLoading ? (
				<div className="text-xs text-text-muted">加载中…</div>
			) : !latest ? (
				<div
					className="rounded-md border border-surface-border p-4 text-xs text-text-muted"
					data-testid="judge-empty"
				>
					尚无标定记录——注意这不等于一致率为 0。跑一次标定即可:
					<code className="ml-1">POST /admin/v1/loop-lab/guidance/calibrate?gold_set=default</code>
				</div>
			) : (
				<>
					<div className="grid grid-cols-2 gap-2 md:grid-cols-4" data-testid="judge-latest">
						<StatTile
							label="最近一次 · 难档一致率"
							value={
								latest.hard_agreement == null ? "--" : `${Math.round(latest.hard_agreement * 100)}%`
							}
							hint={<span>{latest.gold_set_label}</span>}
						/>
						<StatTile
							label="冤杀"
							value={String(latest.false_alarm)}
							hint={<span>该遵循判违背</span>}
						/>
						<StatTile label="漏网" value={String(latest.miss)} hint={<span>该违背却放过</span>} />
						<StatTile
							label="金标条数"
							value={String(latest.gold_count)}
							hint={<span>{latest.model}</span>}
						/>
					</div>
					<div className="overflow-x-auto rounded-lg border border-surface-border">
						<table className="w-full text-xs">
							<thead className="bg-surface-subtle text-text-secondary">
								<tr>
									<th className="px-3 py-2 text-left">时间</th>
									<th className="px-3 py-2 text-left">金标集</th>
									<th className="px-3 py-2 text-right">难档一致率</th>
									<th className="px-3 py-2 text-right">总一致率</th>
									<th className="px-3 py-2 text-right">冤杀</th>
									<th className="px-3 py-2 text-right">漏网</th>
									<th className="px-3 py-2" />
								</tr>
							</thead>
							<tbody>
								{rows.map((r) => (
									<tr key={r.id} className="border-surface-border border-t">
										<td className="px-3 py-2 font-mono">{r.ts.slice(0, 16).replace("T", " ")}</td>
										<td className="px-3 py-2">{r.gold_set_label}</td>
										<td className="px-3 py-2 text-right font-mono">
											{r.hard_agreement == null ? "—" : `${Math.round(r.hard_agreement * 100)}%`}
										</td>
										<td className="px-3 py-2 text-right font-mono">
											{r.agreement == null ? "—" : `${Math.round(r.agreement * 100)}%`}
										</td>
										<td className="px-3 py-2 text-right font-mono">{r.false_alarm}</td>
										<td className="px-3 py-2 text-right font-mono">{r.miss}</td>
										<td className="px-3 py-2 text-right">
											<button
												type="button"
												className="text-text-link hover:underline"
												onClick={() => setOpenId(openId === r.id ? null : r.id)}
											>
												{openId === r.id ? "收起" : "看误判"}
											</button>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{openId && detail.data ? (
						<div
							className="space-y-1.5 rounded-md border border-surface-border p-3"
							data-testid="judge-misjudged"
						>
							<div className="text-xs font-medium text-text-secondary">
								误判条目(判官裁决 ≠ 金标)
							</div>
							{detail.data.rows.filter((x) => !x.agree).length === 0 ? (
								<span className="text-xs text-text-muted">本次全对</span>
							) : (
								detail.data.rows
									.filter((x) => !x.agree)
									.map((x) => (
										<div key={x.id} className="flex gap-2 text-xs">
											<Pill tone={x.expected_met ? "danger" : "warning"}>
												{x.expected_met ? "冤杀" : "漏网"}
											</Pill>
											<span className="w-56 shrink-0 truncate font-mono" title={x.id}>
												{x.id}
											</span>
											<span className="text-text-muted">{x.judge_reason}</span>
										</div>
									))
							)}
						</div>
					) : null}
				</>
			)}
		</div>
	);
}

/** 通用分布列表(名称 + 条 + 计数)。刀B 三处失败构成、RCA 三个分布共用。 */
function DistList({ title, rows }: { title: string; rows: { key: string; count: number }[] }) {
	const max = Math.max(1, ...rows.map((r) => r.count));
	return (
		<div className="space-y-1.5 rounded-md border border-surface-border p-3">
			<div className="text-xs font-medium text-text-secondary">{title}</div>
			{rows.length ? (
				rows.map((r) => (
					<div key={r.key} className="flex items-center gap-2 text-xs">
						<span className="w-28 shrink-0 truncate text-text-secondary" title={r.key}>
							{r.key}
						</span>
						<MiniBar value={r.count} max={max} className="flex-1" />
						<span className="w-8 shrink-0 text-right font-mono text-text-secondary">{r.count}</span>
					</div>
				))
			) : (
				<span className="text-xs text-text-muted">—</span>
			)}
		</div>
	);
}

/** RCA 簇 tone → 图表 hex(与簇 Pill 同一色彩身份) */
const TONE_HEX: Record<Tone, string> = {
	success: CHART.success,
	warning: CHART.warning,
	danger: CHART.danger,
	info: CHART.info,
	muted: CHART.inkMuted,
};

function RcaStatsPanel({ days }: { days: number }) {
	const q = useRcaStats(days);
	const d = q.data;

	if (q.isLoading) return <LoadingInline />;
	if (!d || d.samples === 0) {
		return (
			<div className="rounded-md border border-surface-border p-4 text-sm text-text-muted">
				该窗口暂无归因记录。在「归因」页发起分析后,这里给出 簇 / 主归因 / 节点位次 的分布与需复核率;
				可切「全部」看历史。
			</div>
		);
	}
	return (
		<div className="space-y-4">
			<p className="text-xs text-text-muted">
				对失败场景的根因分析分布(5 簇判官 + 综合裁决)。每 30s 自动刷新。
			</p>
			<div className="flex flex-wrap items-center gap-3">
				<span className="text-xs text-text-muted">{d.samples} 条归因</span>
				<Button type="button" size="sm" variant="outline" onClick={() => q.refetch()}>
					刷新
				</Button>
			</div>
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
				<Kpi label="归因记录数" value={String(d.overall.n)} />
				<Kpi
					label="需复核率"
					value={pct(d.overall.needs_review_rate)}
					tone={
						d.overall.needs_review_rate && d.overall.needs_review_rate > 0.3 ? "warning" : "muted"
					}
				/>
				<Kpi
					label="平均置信"
					value={d.overall.avg_confidence == null ? "—" : String(d.overall.avg_confidence)}
				/>
			</div>
			{/* 簇分布扇形(C35 · 5 簇 ≤5 片,色彩身份与簇 Pill 一致) */}
			<div className="rounded-md border border-surface-border p-3">
				<div className="mb-2 text-xs font-medium text-text-secondary">簇占比(cluster)</div>
				<Donut
					testId="rca-cluster-donut"
					slices={d.by_cluster.map((r) => ({
						key: r.key,
						label: r.key,
						value: r.count,
						color: TONE_HEX[RCA_CLUSTER_TONE[r.key] ?? "muted"],
					}))}
					center={String(d.overall.n)}
					centerHint="条归因"
				/>
			</div>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
				<DistList title="簇(cluster)" rows={d.by_cluster} />
				<DistList title="主归因(primary_label)" rows={d.by_primary_label} />
				<DistList title="节点位次(node_locus)" rows={d.by_node_locus} />
			</div>
		</div>
	);
}

const ANSWER_DIMS = [
	{ key: "by_intent", label: "意图", presetKey: "intent" },
	{ key: "by_skill", label: "skill", presetKey: "skill" },
	{ key: "by_customer", label: "客户", presetKey: "customer" },
	{ key: "by_multi", label: "轮次", presetKey: "multi" },
] as const;

function pct(rate: number | null): string {
	return rate == null ? "—" : `${Math.round(rate * 100)}%`;
}
function rateTone(rate: number | null): Tone {
	if (rate == null) return "muted";
	if (rate >= 0.8) return "success";
	if (rate >= 0.5) return "warning";
	return "danger";
}
/** KPI 大数字卡(C25 批2:收编为 KpiCard 原语) */
function Kpi({ label, value, tone }: { label: string; value: string; tone?: Tone }) {
	return <KpiCard label={label} value={value} tone={tone ?? "neutral"} />;
}

/** 应答效果统计:仅真 LLM · KPI + 维度表 + 意图图表 + 查看详情下钻到「记录」 */
function AnswerStatsPanel({ days, onDrill }: { days: number; onDrill: (p: HistPreset) => void }) {
	const [dim, setDim] = useState<(typeof ANSWER_DIMS)[number]["key"]>("by_intent");
	const q = useAnswerStats(days);
	// 刀B:失败构成复用概览已有通道(by_date),不为此新开端点
	const ov = useStatsOverview(days);
	const d = q.data;

	if (q.isLoading) return <LoadingInline />;
	if (!d || d.samples === 0) {
		return (
			<div className="rounded-md border border-surface-border p-4 text-sm text-text-muted">
				该窗口暂无真 LLM 应答样本。去「场景库」用真 LLM 批量跑,或「试跑」交互后,这里给出
				执行/正确/错误/正确率;可切「全部」看历史。
			</div>
		);
	}
	const rows = d[dim];
	const dimMeta = ANSWER_DIMS.find((x) => x.key === dim) ?? ANSWER_DIMS[0];
	const labelOf = (k: string) =>
		dim === "by_intent" ? intentZh(k) : dim === "by_multi" ? (k === "multi" ? "多轮" : "单轮") : k;
	const drill = (k: string) => {
		const p: HistPreset = {};
		(p as Record<string, string>)[dimMeta.presetKey] = k;
		onDrill(p);
	};
	const maxTotal = Math.max(1, ...rows.map((r) => r.total));

	return (
		<div className="space-y-4">
			<div className="flex flex-wrap items-center gap-3">
				<span className="text-xs text-text-muted">仅统计真 LLM · {d.samples} 条样本</span>
				<Button type="button" size="sm" variant="outline" onClick={() => q.refetch()}>
					刷新
				</Button>
			</div>
			{/* KPI */}
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
				<Kpi label="执行次数" value={String(d.overall.total)} />
				<Kpi label="正确数" value={String(d.overall.pass)} tone="success" />
				<Kpi label="错误数" value={String(d.overall.fail)} tone="danger" />
				<Kpi label="正确率" value={pct(d.overall.rate)} tone={rateTone(d.overall.rate)} />
			</div>
			{/* 失败构成(刀B):正确率掉了,是卡在澄清、转人工,还是答错 */}
			<div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="answer-failure-shape">
				<DistList
					title="终止原因分布(stop)"
					rows={sumDist(ov.data?.by_date ?? [], (b) => b.answer.stop_dist)}
				/>
				<div className="space-y-1.5 rounded-md border border-surface-border p-3">
					<div className="text-xs font-medium text-text-secondary">澄清率</div>
					<div className="text-lg font-semibold">{fmtClarify(ov.data?.by_date ?? [])}</div>
					<div className="text-xs text-text-muted">
						占全部 llm 应答;高不一定是坏事(该问就问),但配合 stop=clarify 堆积看是否问不到点上
					</div>
				</div>
			</div>
			{/* 维度 toggle */}
			<div className="flex items-center gap-2">
				<span className="text-xs text-text-muted">分组维度</span>
				<div className="flex gap-1 rounded-full border border-surface-border p-0.5">
					{ANSWER_DIMS.map((x) => (
						<SubTabBtn key={x.key} active={dim === x.key} onClick={() => setDim(x.key)}>
							{x.label}
						</SubTabBtn>
					))}
				</div>
			</div>
			{/* 意图维度:执行次数条形图(轻量,纯 div) */}
			{dim === "by_intent" ? (
				<div className="space-y-1.5 rounded-md border border-surface-border p-3">
					<div className="text-xs font-medium text-text-secondary">意图分布(执行次数 · 正确率)</div>
					{rows.map((r) => (
						<div key={r.key} className="flex items-center gap-2 text-xs">
							<span className="w-28 shrink-0 truncate text-text-secondary" title={labelOf(r.key)}>
								{labelOf(r.key)}
							</span>
							<MiniBar value={r.total} max={maxTotal} className="flex-1" />
							<span className="w-10 shrink-0 text-right font-mono text-text-secondary">
								{r.total}
							</span>
							<Pill tone={rateTone(r.rate)}>{pct(r.rate)}</Pill>
						</div>
					))}
				</div>
			) : null}
			{/* 维度明细表 */}
			<div className="overflow-x-auto rounded-lg border border-surface-border">
				<table className="w-full text-sm">
					<thead className="bg-surface-muted text-text-secondary">
						<tr>
							<th className="px-3 py-2 text-left font-medium">{dimMeta.label}</th>
							<th className="px-3 py-2 text-left font-medium">执行次数</th>
							<th className="px-3 py-2 text-left font-medium">正确</th>
							<th className="px-3 py-2 text-left font-medium">错误</th>
							<th className="px-3 py-2 text-left font-medium">正确率</th>
							<th className="px-3 py-2 text-left font-medium">详情</th>
						</tr>
					</thead>
					<tbody>
						{rows.map((r: AnswerStatRow) => (
							<tr key={r.key} className="border-t border-surface-border hover:bg-surface-muted/40">
								<td className="px-3 py-2 font-medium text-text-primary">{labelOf(r.key)}</td>
								<td className="px-3 py-2 font-mono text-text-secondary">{r.total}</td>
								<td className="px-3 py-2 font-mono text-brand-success">{r.pass}</td>
								<td className="px-3 py-2 font-mono text-brand-danger">{r.fail}</td>
								<td className="px-3 py-2">
									<Pill tone={rateTone(r.rate)}>{pct(r.rate)}</Pill>
								</td>
								<td className="px-3 py-2">
									<Button type="button" size="sm" variant="outline" onClick={() => drill(r.key)}>
										查看详情
									</Button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			<p className="text-xs text-text-muted">
				正确/错误仅对有 golden
				判定的记录计(场景/测试集);自由试跑无金标只计入执行次数。「查看详情」跳「记录」并带入该筛选。
			</p>
		</div>
	);
}

/** 记录下钻预设(统计页「查看详情」跳转时带入的初始筛选) */
export type HistPreset = {
	intent?: string;
	customer?: string;
	skill?: string;
	date?: string;
	multi?: string; // "single" | "multi"
	rid?: string; // CX 评分记录「查看原始会话」→ 直接预开该记录详情抽屉
};

const TEST_MODE_LABEL: Record<string, string> = {
	manual: "人工测试",
	testset: "标准测试集",
	sim: "场景模拟",
};
const TEST_MODE_TONE: Record<string, Tone> = {
	manual: "info",
	testset: "success",
	sim: "warning",
};

function HistoryPanel({ preset, onConsume }: { preset?: HistPreset; onConsume?: () => void }) {
	const recs = useRecords();
	const clear = useClearHistory();
	const cxLatest = useCxRecords().data?.latest ?? {};
	const [drawerRid, setDrawerRid] = useState<string | null>(preset?.rid ?? null);
	const [fMode, setFMode] = useState(ALL);
	const [fIntent, setFIntent] = useState(preset?.intent ?? ALL);
	const [fCustomer, setFCustomer] = useState(preset?.customer ?? ALL);
	const [fSkill, setFSkill] = useState(preset?.skill ?? ALL);
	const [fDate, setFDate] = useState(preset?.date ?? ALL);
	const [fResult, setFResult] = useState(ALL);
	const [page, setPage] = useState(0);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 仅挂载时消费一次预设
	useEffect(() => {
		if (preset) onConsume?.();
	}, []);
	const records = useMemo(() => recs.data?.records ?? [], [recs.data]);
	const intents = useMemo(
		() => Array.from(new Set(records.map((r) => r.intent ?? "—"))).sort(),
		[records],
	);
	const customers = useMemo(
		() => Array.from(new Set(records.map((r) => r.customer))).sort(),
		[records],
	);
	const skills = useMemo(
		() => Array.from(new Set(records.flatMap((r) => r.calls))).sort(),
		[records],
	);
	const dates = useMemo(
		() =>
			Array.from(new Set(records.map((r) => r.ts.slice(0, 10))))
				.sort()
				.reverse(),
		[records],
	);
	const filtered = useMemo(
		() =>
			records.filter((r) => {
				if (fMode !== ALL && r.test_mode !== fMode) return false;
				if (fIntent !== ALL && (r.intent ?? "—") !== fIntent) return false;
				if (fCustomer !== ALL && r.customer !== fCustomer) return false;
				if (fSkill !== ALL && !r.calls.includes(fSkill)) return false;
				if (fDate !== ALL && r.ts.slice(0, 10) !== fDate) return false;
				if (fResult === "pass" && r.result !== "pass") return false;
				if (fResult === "fail" && r.result !== "fail") return false;
				if (fResult === "committed" && !r.committed) return false;
				return true;
			}),
		[records, fMode, fIntent, fCustomer, fSkill, fDate, fResult],
	);
	// biome-ignore lint/correctness/useExhaustiveDependencies: 仅在筛选变化时重置页码
	useEffect(() => setPage(0), [fMode, fIntent, fCustomer, fSkill, fDate, fResult]);
	// 人工多轮会话已在后端塌缩成一行(一行=一次对话,同场景模拟),此处平铺即可
	const pageCount = Math.max(1, Math.ceil(filtered.length / HIST_PAGE));
	const curPage = Math.min(page, pageCount - 1);
	const paged = filtered.slice(curPage * HIST_PAGE, curPage * HIST_PAGE + HIST_PAGE);

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center justify-between">
					<span>
						命中 {filtered.length} / 共 {records.length} 条
					</span>
					<div className="flex gap-2">
						<Button type="button" variant="outline" size="sm" onClick={() => recs.refetch()}>
							刷新
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={clear.isPending}
							onClick={() => clear.mutate(undefined, { onSuccess: () => recs.refetch() })}
						>
							清空(人工/测试集)
						</Button>
					</div>
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				{records.length ? (
					<div className="flex flex-wrap items-center gap-3 rounded-lg bg-surface-subtle/60 p-3">
						<Select
							label="模式"
							value={fMode}
							onChange={setFMode}
							options={[
								{ value: ALL, label: "全部" },
								{ value: "manual", label: "人工测试" },
								{ value: "testset", label: "标准测试集" },
								{ value: "sim", label: "场景模拟" },
							]}
						/>
						<Select
							label="意图"
							value={fIntent}
							onChange={setFIntent}
							options={[
								{ value: ALL, label: "全部" },
								...intents.map((i) => ({ value: i, label: i })),
							]}
						/>
						<Select
							label="客户"
							value={fCustomer}
							onChange={setFCustomer}
							options={[
								{ value: ALL, label: "全部" },
								...customers.map((c) => ({ value: c, label: c })),
							]}
						/>
						<Select
							label="skill"
							value={fSkill}
							onChange={setFSkill}
							options={[
								{ value: ALL, label: "全部" },
								...skills.map((s) => ({ value: s, label: s })),
							]}
						/>
						<Select
							label="日期"
							value={fDate}
							onChange={setFDate}
							options={[
								{ value: ALL, label: "全部" },
								...dates.map((d) => ({ value: d, label: d })),
							]}
						/>
						<Select
							label="结果"
							value={fResult}
							onChange={setFResult}
							options={[
								{ value: ALL, label: "全部" },
								{ value: "pass", label: "通过" },
								{ value: "fail", label: "未过" },
								{ value: "committed", label: "已执行写操作" },
							]}
						/>
					</div>
				) : null}
				{recs.isLoading ? <LoadingInline /> : null}
				{!recs.isLoading && !records.length ? (
					<div className="py-4 text-sm text-text-muted">
						还没有记录。去 试跑 / 测试集 / 发起评测 跑,这里按每次执行归一记录。
					</div>
				) : null}
				{filtered.length ? (
					<div className="overflow-x-auto rounded-lg border border-surface-border">
						<table className="w-full text-sm">
							<thead className="bg-surface-muted text-text-secondary">
								<tr>
									<th className="px-3 py-2 text-left font-medium">时间</th>
									<th className="px-3 py-2 text-left font-medium">模式</th>
									<th className="px-3 py-2 text-left font-medium">消息 / 场景</th>
									<th className="px-3 py-2 text-left font-medium">意图</th>
									<th className="px-3 py-2 text-left font-medium">调用链</th>
									<th className="px-3 py-2 text-left font-medium">判停</th>
									<th className="px-3 py-2 text-left font-medium">结果</th>
									<th className="px-3 py-2 text-left font-medium">CX</th>
									<th className="px-3 py-2 text-left font-medium">详情</th>
								</tr>
							</thead>
							<tbody>
								{paged.map((r) => (
									<RecordRow
										key={r.rid}
										r={r}
										cx={cxLatest[r.rid]}
										onOpen={() => setDrawerRid(r.rid)}
									/>
								))}
							</tbody>
						</table>
					</div>
				) : null}
				{records.length && !filtered.length ? (
					<div className="py-4 text-sm text-text-muted">当前筛选无命中记录。</div>
				) : null}
				{filtered.length > HIST_PAGE ? (
					<div className="flex items-center justify-end gap-3 text-sm">
						<span className="text-text-muted">
							第 {curPage + 1} / {pageCount} 页
						</span>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={curPage === 0}
							onClick={() => setPage(curPage - 1)}
						>
							上一页
						</Button>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={curPage >= pageCount - 1}
							onClick={() => setPage(curPage + 1)}
						>
							下一页
						</Button>
					</div>
				) : null}
			</CardContent>
			<RecordDetailDrawer rid={drawerRid} onClose={() => setDrawerRid(null)} />
		</Card>
	);
}

function RecordResult({ r }: { r: RecordSummary }) {
	if (r.result === "pass") return <Pill tone="success">✅ 通过</Pill>;
	if (r.result === "fail") return <Pill tone="danger">❌ 未过</Pill>;
	if (r.result.startsWith("reward=")) {
		return <Pill tone={r.result === "reward=1.0" ? "success" : "danger"}>{r.result}</Pill>;
	}
	return <Pill tone="muted">{r.result || "—"}</Pill>;
}

function RecordRow({
	r,
	cx,
	onOpen,
}: {
	r: RecordSummary;
	cx?: CxRecord;
	onOpen: () => void;
}) {
	return (
		<tr
			className="cursor-pointer border-t border-surface-border hover:bg-surface-muted/40"
			onClick={onOpen}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") onOpen();
			}}
			tabIndex={0}
		>
			<td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-text-muted">
				{shortTime(r.ts)}
			</td>
			<td className="px-3 py-2">
				<Pill tone={TEST_MODE_TONE[r.test_mode] ?? "muted"}>
					{TEST_MODE_LABEL[r.test_mode] ?? r.test_mode}
				</Pill>
			</td>
			<td className="max-w-[240px] truncate px-3 py-2 text-text-primary" title={r.msg}>
				{(r.turns ?? 0) > 1 ? <Pill tone="info">🗣 {r.turns}轮对话</Pill> : null}
				{(r.turns ?? 0) > 1 ? " " : ""}
				{r.msg}
			</td>
			<td className="px-3 py-2 font-mono text-xs text-text-secondary">{r.intent ?? "-"}</td>
			<td className="px-3 py-2 font-mono text-xs text-text-secondary">
				{r.calls.length ? r.calls.join(" → ") : "-"}
			</td>
			<td className="px-3 py-2">
				{r.stop ? <Pill tone={observeTone(r.stop)}>{r.stop}</Pill> : "-"}
			</td>
			<td className="px-3 py-2">
				<span className="inline-flex items-center gap-1">
					<RecordResult r={r} />
					{(r.g_hits ?? 0) > 0 ? (
						<span title={`应答指南:命中 ${r.g_hits} 条 / 采纳 ${r.g_applied ?? 0} 条`}>
							<Pill tone="info">
								指南 {r.g_hits}/{r.g_applied ?? 0}
							</Pill>
						</span>
					) : null}
				</span>
			</td>
			<td className="px-3 py-2" data-testid="record-cx-cell">
				{cx ? (
					<span title={cx.explanation}>
						<Pill tone={cxScoreTone(cx.cx_score)}>
							{cx.cx_score}
							{cx.needs_review ? " ⚠" : ""}
						</Pill>
					</span>
				) : (
					<span className="text-xs text-text-muted">—</span>
				)}
			</td>
			<td className="px-3 py-2">
				<Button
					type="button"
					size="sm"
					variant="outline"
					onClick={(e) => {
						e.stopPropagation();
						onOpen();
					}}
				>
					详情
				</Button>
			</td>
		</tr>
	);
}

/** 记录详情抽屉:按 rid 拉真实详情 —— history 完整记录(node_trace 三栏)或 sim trial(评分卡+三栏) */
function RecordDetailDrawer({ rid, onClose }: { rid: string | null; onClose: () => void }) {
	const q = useRecordDetail(rid);
	const d = q.data;
	if (!rid) return null;
	if (d && "test_mode" in d && d.test_mode === "sim") {
		return (
			<EvalDrawer card={d.card} onClose={onClose} headerExtra={<RecordCxActions rid={rid} />} />
		);
	}
	// 人工多轮会话:一行=一次对话,抽屉整段展示(每轮执行过程同单条,与场景模拟同形态)
	if (d && "test_mode" in d && d.test_mode === "manual_session") {
		const first = d.records[0];
		return (
			<ExecutionDrawer
				open={true}
				onClose={onClose}
				title={`🗣 人工会话 · ${d.records.length} 轮 · ${first?.message ?? ""}`}
				badges={
					<>
						<Pill tone="info">人工测试</Pill>
						<Pill tone="muted">{first ? shortTime(first.ts) : ""} 起</Pill>
						<RecordCxActions rid={rid} />
					</>
				}
				turns={d.records.map((t) => ({
					msg: t.message,
					reply: t.reply,
					stop: t.stop,
					intent: t.intent,
					committed: t.committed,
					node_trace: t.node_trace,
					trace: t.trace,
					timing: t.timing,
					blocks: t.blocks,
				}))}
				customerId={typeof first?.ctx?.customer_id === "string" ? first.ctx.customer_id : undefined}
			/>
		);
	}
	const r =
		d && !("test_mode" in d && typeof d.test_mode === "string") ? (d as HistoryRecord) : undefined;
	return (
		<ExecutionDrawer
			open={true}
			onClose={onClose}
			title={r ? `${shortTime(r.ts)} · ${r.message || r.source}` : "加载详情…"}
			badges={
				r ? (
					<>
						<Pill tone={r.mode === "llm" ? "info" : "muted"}>{r.mode}</Pill>
						<Pill tone="muted">{r.source}</Pill>
						<RecordCxActions rid={rid} />
					</>
				) : null
			}
			turns={
				r
					? [
							{
								msg: r.message,
								reply: r.reply,
								stop: r.stop,
								intent: r.intent,
								committed: r.committed,
								node_trace: r.node_trace,
								trace: r.trace,
								timing: r.timing,
								blocks: r.blocks,
							},
						]
					: []
			}
			customerId={typeof r?.ctx?.customer_id === "string" ? r.ctx.customer_id : undefined}
		/>
	);
}

/* ====================== Tab 3:提示词 ====================== */
const NODE_LABEL_ZH: Record<PromptNodeKey, string> = {
	n1: "N1 意图识别(含多轮接续:补槽/确认/反悔/切换)",
	n2: "N2 主 Agent 决策",
	n3: "N3 结果生成",
};
const PART_LABEL: Record<"system" | "user", string> = {
	system: "system(固定指令 · 稳定 · 可缓存)",
	user: "user(注入变量 + 当前句)",
};

function PromptPartEditor({
	part,
	field,
	draftKey,
	value,
	onChange,
	onSave,
	onReset,
	saving,
	saved,
}: {
	part: "system" | "user";
	field: PromptPart;
	draftKey: string;
	value: string;
	onChange: (v: string) => void;
	onSave: () => void;
	onReset: () => void;
	saving: boolean;
	saved: boolean;
}) {
	const taRef = useRef<HTMLTextAreaElement | null>(null);
	const [varOpen, setVarOpen] = useState(false);
	const [varQuery, setVarQuery] = useState("");
	const dirty = value !== field.value; // C25 批5(P3):未保存条
	const shownVars = field.variables.filter(
		(v) => !varQuery || v.name.includes(varQuery) || v.desc.includes(varQuery),
	);
	// C25 批5(P7):变量插入弹层——光标处插 {{name}}
	function insertVar(name: string) {
		const ta = taRef.current;
		const token = `{{${name}}}`;
		if (!ta) {
			onChange(value + token);
		} else {
			const s = ta.selectionStart ?? value.length;
			const e = ta.selectionEnd ?? value.length;
			onChange(value.slice(0, s) + token + value.slice(e));
			requestAnimationFrame(() => {
				ta.focus();
				ta.selectionStart = ta.selectionEnd = s + token.length;
			});
		}
		setVarOpen(false);
		setVarQuery("");
	}
	return (
		<div className="space-y-2 rounded-md border border-surface-border p-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<span className="text-sm font-medium text-text-primary">
					<Pill tone={part === "system" ? "muted" : "info"}>{PART_LABEL[part]}</Pill>
					{field.overridden ? (
						<span className="ml-2 rounded-full bg-brand-accent/10 px-2 py-0.5 text-xs text-brand-accent">
							已覆盖默认
						</span>
					) : null}
					{saved ? <span className="ml-2 text-xs text-brand-success">已保存</span> : null}
				</span>
				<div className="flex gap-2">
					<Button type="button" size="sm" variant="outline" disabled={saving} onClick={onReset}>
						重置默认
					</Button>
					<Button type="button" size="sm" disabled={saving} onClick={onSave}>
						保存
					</Button>
				</div>
			</div>
			{dirty ? (
				<div className="flex items-center justify-between gap-2 rounded-md bg-surface-subtle px-3 py-1.5 text-xs">
					<span className="text-text-secondary">✎ 有未保存修改(保存后才对真 LLM 生效)</span>
					<button
						type="button"
						onClick={() => onChange(field.value)}
						className="rounded-full bg-surface-card px-2.5 py-0.5 font-medium text-text-primary transition-colors duration-150 hover:bg-surface-muted"
					>
						放弃修改
					</button>
				</div>
			) : null}
			{field.variables.length ? (
				<div className="relative flex flex-wrap items-center gap-1.5">
					<button
						type="button"
						onClick={() => setVarOpen((o) => !o)}
						className="inline-flex items-center gap-1 rounded-full border border-surface-border bg-surface-card px-2.5 py-1 font-mono text-xs text-text-primary transition-colors duration-150 hover:bg-surface-subtle"
					>
						{"<>"} 插入变量
					</button>
					<span className="text-xs text-text-muted">
						{field.variables.length} 个可注入 · 光标处插入 {"{{变量}}"}
					</span>
					{varOpen ? (
						<div className="absolute left-0 top-8 z-30 w-72 rounded-xl bg-surface-card p-2 shadow-lg ring-1 ring-surface-border">
							<input
								ref={(el) => el?.focus()}
								value={varQuery}
								onChange={(e) => setVarQuery(e.target.value)}
								placeholder="搜索变量…"
								className="mb-1 w-full border-0 border-b border-surface-border bg-transparent px-2 py-1.5 text-sm outline-none placeholder:text-text-muted"
							/>
							<div className="max-h-56 overflow-y-auto">
								{shownVars.map((v) => (
									<button
										key={v.name}
										type="button"
										onClick={() => insertVar(v.name)}
										className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors duration-150 hover:bg-surface-subtle"
									>
										<span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-brand-info/10 font-mono text-[10px] text-brand-info">
											{"<>"}
										</span>
										<span className="min-w-0">
											<code className="font-mono text-xs text-text-primary">{v.name}</code>
											<span className="ml-1.5 text-xs text-text-muted">{v.desc}</span>
										</span>
									</button>
								))}
								{!shownVars.length ? (
									<div className="px-2 py-3 text-xs text-text-muted">无匹配变量</div>
								) : null}
							</div>
						</div>
					) : null}
				</div>
			) : null}
			<textarea
				ref={taRef}
				className={`w-full rounded-md border border-surface-border bg-surface-card p-3 font-mono text-xs text-text-primary ${part === "system" ? "h-64" : "h-36"}`}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				data-draft={draftKey}
			/>
		</div>
	);
}

const PROMPT_NODE_KEYS: PromptNodeKey[] = ["n1", "n2", "n3"];
/** 提示词节点 key → model_config 节点 key(模型切换用) */
const PROMPT_MODEL_NODE: Record<PromptNodeKey, string> = {
	n1: "n1_intent",
	n2: "n2_decide",
	n3: "n3_generate",
};

/** 节点输入字段规格(补进「应答提示词」页,不另建页面)。
 *
 *  背景:此前本页只在「插入变量」浮层里显示「变量名 + 一句话说明」,而 `shape`(格式)、
 *  `source`(供给方)、`guard`(护栏)、`status`(是否已接线)**API 一直在传、页面从不渲染**;
 *  「格式」没有真实样例也是空的。这里按 SKILL 页既有范式(字段表 + 真实样例)补齐 ——
 *  字段规格归位到本来就该显示它的页面,不再单独做一套。
 */
function VarSpecTable({ vars: rows }: { vars: PromptVar[] }) {
	const [open, setOpen] = useState(false);
	if (!rows.length) return null;
	const reserved = rows.filter((v) => v.status === "reserved").length;
	const from = rows.find((v) => v.sample_from)?.sample_from ?? "";
	return (
		<div className="rounded-lg border border-surface-border">
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className="flex w-full flex-wrap items-center gap-2 px-4 py-2.5 text-left"
			>
				<span className="text-xs font-medium text-text-primary">输入字段规格({rows.length})</span>
				{reserved ? <Pill tone="warning">{reserved} 个已登记未接线</Pill> : null}
				{from ? (
					<span className="font-mono text-[11px] text-text-muted">样例取自 {from}</span>
				) : (
					<span className="text-[11px] text-text-muted">暂无批次可取样例</span>
				)}
				<span className="ml-auto text-xs text-text-muted">{open ? "收起" : "展开"}</span>
			</button>
			{open ? (
				<div className="overflow-x-auto border-t border-surface-border">
					<table className="w-full text-left text-xs">
						<thead>
							<tr className="border-b border-surface-border text-text-muted">
								<th className="px-3 py-1.5">字段</th>
								<th className="px-3 py-1.5">格式</th>
								<th className="px-3 py-1.5">真实样例</th>
								<th className="px-3 py-1.5">供给方</th>
								<th className="px-3 py-1.5">护栏</th>
								<th className="px-3 py-1.5">说明</th>
							</tr>
						</thead>
						<tbody>
							{rows.map((v) => (
								<tr key={v.name} className="border-b border-surface-border/60 align-top">
									<td className="whitespace-nowrap px-3 py-1.5 font-mono text-text-primary">
										{v.name}
										{v.status === "reserved" ? (
											<Pill tone="warning" className="ml-1.5">
												未接线
											</Pill>
										) : null}
									</td>
									<td className="px-3 py-1.5 font-mono text-[11px] text-text-muted">
										{v.shape ?? "—"}
									</td>
									<td className="px-3 py-1.5">
										{v.sample ? (
											<code className="block max-w-[26rem] whitespace-pre-wrap break-all font-mono text-[11px] text-text-secondary">
												{v.sample}
											</code>
										) : (
											<span className="text-[11px] text-text-muted">最近批次未出现非空值</span>
										)}
									</td>
									<td className="px-3 py-1.5 font-mono text-[11px] text-text-muted">
										{v.source ?? "—"}
									</td>
									<td className="px-3 py-1.5 text-text-muted">{v.guard ?? "—"}</td>
									<td className="px-3 py-1.5 text-text-secondary">{v.desc}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			) : null}
		</div>
	);
}

function PromptsPanel() {
	const prompts = usePrompts();
	const save = useSavePrompts();
	const [draft, setDraft] = useState<Record<string, string>>({});
	const [saved, setSaved] = useState<string | null>(null);
	const data = prompts.data;

	function valOf(key: string, fallback: string) {
		return draft[key] ?? fallback;
	}
	function saveField(key: string, value: string) {
		setSaved(null);
		save.mutate(
			{ [key]: value },
			{
				onSuccess: () => {
					setSaved(key);
					prompts.refetch();
				},
			},
		);
	}
	function resetField(key: string) {
		save.mutate(
			{ [key]: "" },
			{
				onSuccess: () => {
					setDraft((d) => {
						const next = { ...d };
						delete next[key];
						return next;
					});
					prompts.refetch();
				},
			},
		);
	}

	if (prompts.isLoading) {
		return (
			<Card>
				<CardContent className="py-6 text-sm text-text-muted">加载提示词…</CardContent>
			</Card>
		);
	}
	if (!data) return null;

	return (
		<Card>
			<CardHeader>
				<CardTitle>
					N1 / N2 / N3 · system / user 拆分(改完保存即对「真 LLM」生效,不走部署)
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-5">
				<p className="rounded-md bg-surface-muted/50 p-3 text-xs leading-5 text-text-secondary">
					每个节点拆两段:<strong>system</strong> = 固定指令(角色/规则/输出格式,稳定、可被模型缓存);
					<strong>user</strong> = 本轮注入的变量 + 当前用户句。变量在 user 段写{" "}
					<code className="rounded bg-surface-card px-1 font-mono">{"{{变量名}}"}</code>{" "}
					即按运行值替换(字符串原样,对象/数组按 JSON);单个{" "}
					<code className="rounded bg-surface-card px-1 font-mono">{"{ }"}</code> 不替换(可放 JSON
					示例)。
				</p>
				{PROMPT_NODE_KEYS.map((nodeKey) => {
					const node = data[nodeKey];
					return (
						<div key={nodeKey} className="space-y-3">
							<div className="text-sm font-semibold text-text-primary">
								{NODE_LABEL_ZH[nodeKey]}
							</div>
							<NodeModelSelector node={PROMPT_MODEL_NODE[nodeKey]} />
							<VarSpecTable vars={node.user.variables} />
							{(["system", "user"] as const).map((part) => {
								const key = `${nodeKey}.${part}`;
								return (
									<PromptPartEditor
										key={key}
										part={part}
										field={node[part]}
										draftKey={key}
										value={valOf(key, node[part].value)}
										onChange={(v) => setDraft((d) => ({ ...d, [key]: v }))}
										onSave={() => saveField(key, valOf(key, node[part].value))}
										onReset={() => resetField(key)}
										saving={save.isPending}
										saved={saved === key}
									/>
								);
							})}
						</div>
					);
				})}
			</CardContent>
		</Card>
	);
}

/* ====================== 评测资产:机器顾客 / 评测判官 / 评测规则(C7-T2)====================== */

/** 评测侧提示词编辑(机器顾客 / 判官 复用):取 /eval-prompts,按 keys 渲染可改段,复用 PromptPartEditor。
 *  与被测 /prompts 同套编辑器,但独立数据源(改即生效、不走部署、互不串)。 */
function EvalPromptEditors({
	keys,
	title,
	intro,
	modelNode,
}: {
	keys: string[];
	title: string;
	intro: ReactNode;
	modelNode?: string;
}) {
	const prompts = useEvalPrompts();
	const save = useSaveEvalPrompts();
	const [draft, setDraft] = useState<Record<string, string>>({});
	const [saved, setSaved] = useState<string | null>(null);
	const data: EvalPromptsResponse | undefined = prompts.data;

	function valOf(key: string, fallback: string) {
		return draft[key] ?? fallback;
	}
	function saveField(key: string, value: string) {
		setSaved(null);
		save.mutate(
			{ [key]: value },
			{
				onSuccess: () => {
					setSaved(key);
					prompts.refetch();
				},
			},
		);
	}
	function resetField(key: string) {
		save.mutate(
			{ [key]: "" },
			{
				onSuccess: () => {
					setDraft((dft) => {
						const next = { ...dft };
						delete next[key];
						return next;
					});
					prompts.refetch();
				},
			},
		);
	}

	if (prompts.isLoading) {
		return (
			<Card>
				<CardContent className="py-6 text-sm text-text-muted">加载评测提示词…</CardContent>
			</Card>
		);
	}
	if (!data) return null;

	return (
		<Card>
			<CardHeader>
				<CardTitle>{title}</CardTitle>
			</CardHeader>
			<CardContent className="space-y-5">
				<p className="rounded-md bg-surface-muted/50 p-3 text-xs leading-5 text-text-secondary">
					{intro}
				</p>
				{modelNode ? <NodeModelSelector node={modelNode} /> : null}
				{keys.map((key) => {
					const entry = data[key];
					if (!entry) return null;
					return (
						<div key={key} className="space-y-3">
							<div className="text-sm font-semibold text-text-primary">{entry.label}</div>
							<p className="text-xs leading-5 text-text-muted">{entry.purpose}</p>
							{/* 契约字段(格式/供给方/护栏/状态)此前只在 IO 页展示,这里归位:
							    评测侧与应答侧同款,避免"字段规格另开一套页面" */}
							{(["system", "user"] as const).map((part) => {
								const vs = entry.parts[part]?.variables ?? [];
								return vs.length ? <VarSpecTable key={`${key}-${part}-vars`} vars={vs} /> : null;
							})}
							{(["system", "user"] as const).map((part) => {
								const field = entry.parts[part];
								if (!field) return null;
								const fk = `${key}.${part}`;
								return (
									<PromptPartEditor
										key={fk}
										part={part}
										field={field}
										draftKey={fk}
										value={valOf(fk, field.value)}
										onChange={(v) => setDraft((dft) => ({ ...dft, [fk]: v }))}
										onSave={() => saveField(fk, valOf(fk, field.value))}
										onReset={() => resetField(fk)}
										saving={save.isPending}
										saved={saved === fk}
									/>
								);
							})}
						</div>
					);
				})}
			</CardContent>
		</Card>
	);
}

function MachineCustomerPanel() {
	return (
		<EvalPromptEditors
			keys={["user_sim"]}
			modelNode="actor"
			title="机器顾客 system · 改完保存即对「评测」真模拟生效,不走部署"
			intro={
				<>
					机器顾客扮真人读四件套 + persona,驱动被测 agent 跑多轮——这就是「怎么生成问题」。在 system
					里写 <code className="rounded bg-surface-card px-1 font-mono">{"{{变量名}}"}</code>{" "}
					按运行值注入(persona_desc + 四件套五槽);改即生效。
				</>
			}
		/>
	);
}

function JudgesPanel() {
	return (
		<EvalPromptEditors
			keys={["nl_judge", "attr"]}
			modelNode="judge"
			title="判官 · 改完保存即对真评测生效,不走部署"
			intro={
				<>
					判官决定「怎么评测」。<strong>NL 合规判官</strong>把整段对话喂裁判,逐条判 nl_assertions
					真假 + 理由(主力判分维 nl);<strong>错误归因判官</strong>区分错误来源
					agent/customer,把机器顾客演崩的样本摘出防污染统计。user 段{" "}
					<code className="rounded bg-surface-card px-1 font-mono">{"{{对话/断言}}"}</code>{" "}
					按运行值注入。
				</>
			}
		/>
	);
}

/** 评测规则:只读忠实展示确定性判分逻辑(取自后端 /eval-rules,源自 judges 真实常量/函数)。 */
function EvalRulesPanel({ onGotoJudges }: { onGotoJudges: () => void }) {
	const rules = useEvalRules();
	const d: EvalRules | undefined = rules.data;
	if (rules.isLoading) {
		return (
			<Card>
				<CardContent className="py-6 text-sm text-text-muted">加载评测规则…</CardContent>
			</Card>
		);
	}
	if (!d) return null;
	return (
		<div className="space-y-4">
			<Card>
				<CardHeader>
					<CardTitle>判分总则 · reward 一票否决</CardTitle>
				</CardHeader>
				<CardContent className="space-y-2 text-sm text-text-secondary">
					<p className="font-mono text-text-primary">{d.reward.formula}</p>
					<p>{d.reward.none_note}</p>
					<p>
						<strong>reward_basis:</strong> {d.reward_basis.rule}
					</p>
				</CardContent>
			</Card>
			<Card>
				<CardHeader>
					<CardTitle>三个判分维({d.all_dims.join(" × ")})</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3">
					{d.dims.map((dim) => (
						<div key={dim.key} className="space-y-1 rounded-md border border-surface-border p-3">
							<div className="flex items-center gap-2">
								<Pill tone={dim.kind === "llm" ? "warning" : "info"}>
									{dim.kind === "llm" ? "LLM 判官" : "确定性规则"}
								</Pill>
								<span className="text-sm font-semibold text-text-primary">{dim.label}</span>
								<code className="font-mono text-xs text-text-muted">{dim.key}</code>
							</div>
							<p className="text-sm text-text-secondary">{dim.how}</p>
							<p className="text-xs text-text-muted">
								契约字段:
								{dim.fields.map((f) => (
									<code key={f} className="mr-1 rounded bg-surface-card px-1 font-mono">
										{f}
									</code>
								))}
								· 实现:<code className="font-mono">{dim.enforced_by}</code>
								{dim.prompt_ref ? (
									<>
										{" · prompt 在 "}
										<button
											type="button"
											className="text-brand-primary underline"
											onClick={onGotoJudges}
										>
											评测判官
										</button>
										{" 页"}
									</>
								) : null}
							</p>
						</div>
					))}
				</CardContent>
			</Card>
			<Card>
				<CardHeader>
					<CardTitle>错误归因(防噪声)</CardTitle>
				</CardHeader>
				<CardContent className="text-sm text-text-secondary">
					<p>{d.attribution.purpose}</p>
					<p className="mt-1 text-xs text-text-muted">
						prompt 在{" "}
						<button type="button" className="text-brand-primary underline" onClick={onGotoJudges}>
							评测判官
						</button>{" "}
						页(错误归因判官)。
					</p>
				</CardContent>
			</Card>
		</div>
	);
}

/* ====================== CX 会话级评分(E_CX_Score_Loop会话级评测详设) ====================== */
function cxScoreTone(score: number): Tone {
	if (score >= 4) return "success"; // positive(Intercom 口径 4-5)
	if (score === 3) return "muted";
	return "danger";
}

/** reason 取值着色:方向语义来自 /cx-score/standard 注册表(negative_when),前端不硬编码方向。 */
function cxReasonTone(meta: CxReasonMeta | undefined, value: string): Tone {
	if (!meta || value === "Neutral") return "muted";
	if (value === meta.negative_when) return "danger";
	if (value === meta.positive_when) return "success";
	return "muted";
}

/** 运行记录详情抽屉头部的 CX 动作件:已评显示分数徽章 + 重评;未评显示「评 CX 分」。 */
function RecordCxActions({ rid }: { rid: string }) {
	const { toast } = useToast();
	const cx = useCxRecords();
	const run = useRunCxScore();
	const rec = cx.data?.latest?.[rid];
	return (
		<span className="flex items-center gap-1.5" data-testid="cx-actions">
			{rec ? (
				<span title={rec.explanation}>
					<Pill tone={cxScoreTone(rec.cx_score)}>
						CX {rec.cx_score}
						{rec.needs_review ? " ⚠" : ""}
					</Pill>
				</span>
			) : null}
			<Button
				type="button"
				size="sm"
				variant="outline"
				disabled={run.isPending}
				data-testid="cx-analyze-btn"
				onClick={() =>
					run.mutate(
						{ rid },
						{
							onSuccess: (d) => toast(`CX 评分完成:${d.cx_score} 分`, "success"),
							onError: (e) =>
								toast(
									`CX 评分失败:${e instanceof Error ? e.message : "判官未产出合规评分"}`,
									"error",
								),
						},
					)
				}
			>
				{run.isPending ? "评分中…" : rec ? "重新评分" : "评 CX 分"}
			</Button>
		</span>
	);
}

const CX_BAND_OPTS = [
	{ value: "__all__", label: "全部分数" },
	{ value: "positive", label: "正向(4-5)" },
	{ value: "neutral", label: "中性(3)" },
	{ value: "negative", label: "负面(1-2)" },
];

function CxRecordsTab({ onGotoRecord }: { onGotoRecord: (rid: string) => void }) {
	const q = useCxRecords();
	const summary = useCxSummary().data;
	const std = useCxStandard().data;
	const [fBand, setFBand] = useState(ALL);
	const [fReason, setFReason] = useState(ALL);
	const [fReview, setFReview] = useState(ALL);
	const [sel, setSel] = useState<string | null>(null);
	const all = q.data?.records ?? [];
	const reasonMeta = new Map((std?.reasons ?? []).map((r) => [r.key, r]));
	const negKeys = (r: CxRecord) =>
		(std?.reasons ?? []).filter((m) => r.reasons[m.key] === m.negative_when).map((m) => m.key);
	const shown = all.filter((r) => {
		if (fBand === "positive" && r.cx_score < 4) return false;
		if (fBand === "neutral" && r.cx_score !== 3) return false;
		if (fBand === "negative" && r.cx_score > 2) return false;
		if (fReason !== ALL && !negKeys(r).includes(fReason)) return false;
		if (fReview !== ALL && !r.needs_review) return false;
		return true;
	});
	if (q.isLoading)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	return (
		<Card>
			<CardHeader>
				<CardTitle>共 {all.length} 条评分记录</CardTitle>
			</CardHeader>
			<CardContent className="space-y-3">
				{summary && summary.scored_sessions > 0 ? (
					<p className="text-xs text-text-secondary" data-testid="cx-summary-bar">
						已评 <b>{summary.scored_sessions}</b> 个会话 · Overall CX <b>{summary.overall_cx}%</b>
						(4-5 分占比) · 5/4/3/2/1 ={" "}
						{[5, 4, 3, 2, 1].map((s) => summary.score_dist[String(s)] ?? 0).join("/")}
						{summary.needs_review ? <> · 待复核 {summary.needs_review}</> : null}
						{Object.keys(summary.negative_reasons).length ? (
							<>
								{" "}
								· 负面:
								{Object.entries(summary.negative_reasons)
									.sort((a, b) => b[1] - a[1])
									.map(([k, v]) => `${reasonMeta.get(k)?.label ?? k}×${v}`)
									.join("、")}
							</>
						) : null}
					</p>
				) : (
					<p className="text-xs text-text-muted">
						还没有评分。去「运行记录」点开一条记录,在详情抽屉里点「评 CX 分」。
					</p>
				)}
				<div className="flex flex-wrap items-center gap-3 text-xs">
					<Select
						label="分数"
						value={fBand}
						onChange={setFBand}
						options={CX_BAND_OPTS.map((o) =>
							o.value === "__all__" ? { value: ALL, label: o.label } : o,
						)}
					/>
					<Select
						label="负面 reason"
						value={fReason}
						onChange={setFReason}
						options={[
							{ value: ALL, label: "全部" },
							...(std?.reasons ?? []).map((r) => ({ value: r.key, label: r.label })),
						]}
					/>
					<Select
						label="复核"
						value={fReview}
						onChange={setFReview}
						options={[
							{ value: ALL, label: "全部" },
							{ value: "need", label: "待复核 ⚠" },
						]}
					/>
					<span className="self-center text-text-muted">命中 {shown.length} 条</span>
				</div>
				<div className="overflow-x-auto rounded-lg border border-surface-border">
					<table className="w-full text-left text-sm">
						<thead>
							<tr className="border-b border-surface-border text-xs text-text-muted">
								<th className="px-2 py-1.5">时间</th>
								<th className="px-2 py-1.5">来源</th>
								<th className="px-2 py-1.5">首条消息</th>
								<th className="px-2 py-1.5">CX 分</th>
								<th className="px-2 py-1.5">负面 reasons</th>
								<th className="px-2 py-1.5">解释</th>
							</tr>
						</thead>
						<tbody>
							{shown.map((r) => (
								<tr
									key={r.id}
									onClick={() => setSel(r.id)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") setSel(r.id);
									}}
									tabIndex={0}
									data-testid="cx-record-row"
									className={`cursor-pointer border-b border-surface-border/60 hover:bg-surface-subtle ${sel === r.id ? "bg-brand-accent/5" : ""}`}
								>
									<td className="whitespace-nowrap px-2 py-1.5 font-mono text-xs text-text-muted">
										{shortTime(r.ts)}
									</td>
									<td className="px-2 py-1.5">
										<Pill tone={TEST_MODE_TONE[r.test_mode] ?? "muted"}>
											{TEST_MODE_LABEL[r.test_mode] ?? r.test_mode}
										</Pill>
										{r.run_id ? (
											<span title={`评测批次 ${r.run_id}(批量评出;评测记录页可对上)`}>
												{" "}
												<Pill tone="neutral">批</Pill>
											</span>
										) : null}
									</td>
									<td
										className="max-w-[220px] truncate px-2 py-1.5 text-text-primary"
										title={r.first_msg}
									>
										{r.num_turns > 2 ? <Pill tone="info">🗣 {r.num_turns}</Pill> : null}{" "}
										{r.first_msg}
									</td>
									<td className="px-2 py-1.5">
										<Pill tone={cxScoreTone(r.cx_score)}>
											{r.cx_score}
											{r.needs_review ? " ⚠" : ""}
										</Pill>
									</td>
									<td className="px-2 py-1.5 text-xs">
										{negKeys(r).length
											? negKeys(r).map((k) => (
													<Pill key={k} tone="danger">
														{reasonMeta.get(k)?.label ?? k}
													</Pill>
												))
											: "—"}
									</td>
									<td
										className="max-w-[320px] truncate px-2 py-1.5 text-xs text-text-secondary"
										title={r.explanation}
									>
										{r.explanation}
									</td>
								</tr>
							))}
						</tbody>
					</table>
					{shown.length === 0 && all.length > 0 ? (
						<div className="p-3 text-center text-xs text-text-muted">当前筛选无记录</div>
					) : null}
				</div>
				<CxRecordDrawer recId={sel} onClose={() => setSel(null)} onGotoRecord={onGotoRecord} />
			</CardContent>
		</Card>
	);
}

function CxRecordDrawer({
	recId,
	onClose,
	onGotoRecord,
}: {
	recId: string | null;
	onClose: () => void;
	onGotoRecord: (rid: string) => void;
}) {
	const q = useCxRecordDetail(recId);
	const std = useCxStandard().data;
	const r = q.data;
	const reasonList = std?.reasons ?? [];
	return (
		<DrawerPrimitive.Root open={!!recId} onOpenChange={(o) => !o && onClose()}>
			<DrawerPrimitive.Portal>
				<DrawerPrimitive.Overlay className="ui-overlay fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
				<DrawerPrimitive.Content
					className="ui-drawer fixed right-0 top-0 z-50 flex h-full w-[min(760px,95vw)] flex-col overflow-y-auto border-l border-surface-border bg-surface-background shadow-xl focus:outline-none"
					data-testid="cx-drawer"
				>
					<header className="flex flex-wrap items-center gap-2 border-b border-surface-border px-4 py-3">
						<DrawerPrimitive.Title className="text-sm font-semibold text-text-primary">
							CX 评分 · {r?.first_msg ?? ""}
						</DrawerPrimitive.Title>
						{r ? <Pill tone={cxScoreTone(r.cx_score)}>CX {r.cx_score}</Pill> : null}
						{r?.needs_review ? <Pill tone="warning">待复核 {r.review_rules.join("/")}</Pill> : null}
						{r ? (
							<Button type="button" size="sm" variant="outline" onClick={() => onGotoRecord(r.rid)}>
								查看原始会话
							</Button>
						) : null}
						<DrawerPrimitive.Close
							className="ml-auto rounded-md p-1 text-text-muted hover:bg-surface-muted"
							aria-label="关闭"
						>
							<X className="h-4 w-4" />
						</DrawerPrimitive.Close>
					</header>
					<div className="flex-1 space-y-3 p-4 text-sm">
						{!r ? (
							<div className="text-text-muted">加载中…</div>
						) : (
							<>
								<div className="rounded-md bg-surface-subtle/60 p-3">
									<div className="mb-1 text-[11px] font-bold text-text-secondary">评分解释</div>
									<AiCard>{r.explanation}</AiCard>
									{r.calibration ? (
										<div className="mt-1.5 text-[11px] text-text-muted">
											⚠ 已确定性校准({r.calibration.rule}):判官原始分 {r.calibration.original_score}{" "}
											→ 封顶 {r.cx_score}(合规越界硬规则)
										</div>
									) : null}
								</div>
								<div>
									<div className="mb-1 text-[11px] font-bold text-text-secondary">
										7 项 Reasons(逐条独立判;方向语义与「CX 评分标准」页同源)
									</div>
									<div className="space-y-1.5">
										{reasonList.map((m) => {
											const val = r.reasons[m.key] ?? "Neutral";
											return (
												<div
													key={m.key}
													className="flex items-center gap-2 rounded-md border border-surface-border p-2 text-xs"
												>
													<span className="w-28 shrink-0 font-medium text-text-primary">
														{m.label}
													</span>
													<Pill tone={cxReasonTone(m, val)}>{val}</Pill>
													{val === m.negative_when &&
													(m.key === "customer_effort" || m.key === "compliance_risk") ? (
														<span className="text-text-muted">(方向相反:High=负面)</span>
													) : null}
													<code className="ml-auto font-mono text-[10px] text-text-muted">
														{m.key}
													</code>
												</div>
											);
										})}
									</div>
								</div>
								<div className="rounded-md border border-surface-border p-3 text-xs">
									<div className="mb-1 font-bold text-text-secondary">
										判官现场(可还原:模型 + prompt 版本 + 当时看到的辅助信号)
									</div>
									<p className="text-text-muted">
										model=<code className="font-mono">{r.model}</code> · prompt=
										<code className="font-mono">{r.prompt_version}</code>
										{r.prompt_override ? <Pill tone="warning">运行时覆盖版 prompt</Pill> : null} ·
										会话 <code className="font-mono">{r.rid}</code> · {r.num_turns} turns
									</p>
									{r.meta_used && Object.keys(r.meta_used).length ? (
										<div className="mt-2">
											<JsonBox value={r.meta_used} />
										</div>
									) : (
										<p className="mt-1 text-text-muted">(本次评分无辅助信号,判官只读对话)</p>
									)}
								</div>
							</>
						)}
					</div>
				</DrawerPrimitive.Content>
			</DrawerPrimitive.Portal>
		</DrawerPrimitive.Root>
	);
}

function CxStandardTab() {
	const std = useCxStandard().data;
	if (!std)
		return (
			<Card>
				<CardContent className="py-4">
					<LoadingInline />
				</CardContent>
			</Card>
		);
	return (
		<div className="space-y-4">
			{/* ① 字段契约(只读,注册表同源) */}
			<Card>
				<CardHeader>
					<CardTitle>① 字段契约 · 输入 5 信号 + 输出 7 reasons(与运行代码同源)</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4 text-sm">
					<div className="overflow-x-auto rounded-lg border border-surface-border">
						<table className="w-full text-left text-xs">
							<thead>
								<tr className="border-b border-surface-border text-text-muted">
									<th className="px-2 py-1.5">输入信号</th>
									<th className="px-2 py-1.5">人工/会话来源</th>
									<th className="px-2 py-1.5">场景模拟来源</th>
									<th className="px-2 py-1.5">供哪些 reason 参考</th>
								</tr>
							</thead>
							<tbody>
								{std.meta_fields.map((f) => (
									<tr key={f.key} className="border-b border-surface-border/60">
										<td className="px-2 py-1.5">
											<div className="font-medium text-text-primary">{f.label}</div>
											<code className="font-mono text-[10px] text-text-muted">{f.key}</code>
										</td>
										<td className="px-2 py-1.5 text-text-secondary">{f.history_source}</td>
										<td className="px-2 py-1.5 text-text-secondary">{f.sim_source}</td>
										<td className="px-2 py-1.5 text-text-secondary">
											{f.informs.join("、")}
											<div className="text-[10px] text-text-muted">{f.prompt_note}</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					<div className="overflow-x-auto rounded-lg border border-surface-border">
						<table className="w-full text-left text-xs">
							<thead>
								<tr className="border-b border-surface-border text-text-muted">
									<th className="px-2 py-1.5">输出 reason</th>
									<th className="px-2 py-1.5">取值</th>
									<th className="px-2 py-1.5">负面方向</th>
									<th className="px-2 py-1.5">归属信号</th>
								</tr>
							</thead>
							<tbody>
								{std.reasons.map((m) => (
									<tr key={m.key} className="border-b border-surface-border/60">
										<td className="px-2 py-1.5">
											<div className="font-medium text-text-primary">
												{m.rubric_block} {m.label}
											</div>
											<code className="font-mono text-[10px] text-text-muted">{m.key}</code>
										</td>
										<td className="px-2 py-1.5 text-text-secondary">{m.values.join(" / ")}</td>
										<td className="px-2 py-1.5">
											<Pill tone="danger">{m.negative_when} = 负面</Pill>
											{m.negative_when === "High" ? (
												<span className="ml-1 text-[10px] text-brand-danger">⚠ 方向相反</span>
											) : null}
										</td>
										<td className="px-2 py-1.5 text-text-secondary">
											{std.signals[m.signal] ?? m.signal}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</CardContent>
			</Card>
			{/* ② 确定性规则(只读) */}
			<Card>
				<CardHeader>
					<CardTitle>② 确定性规则 · eligibility / 聚合口径 / L3 校准</CardTitle>
				</CardHeader>
				<CardContent className="space-y-2 text-sm text-text-secondary">
					<p>
						<b>评分条件:</b>
						{std.eligibility}
					</p>
					<p>
						<b>聚合口径:</b>
						{std.positive_rule}
					</p>
					<div className="space-y-1.5">
						{std.calibration_rules.map((c) => (
							<div
								key={c.id}
								className="flex items-start gap-2 rounded-md border border-surface-border p-2 text-xs"
							>
								<Pill tone={c.action === "cap_score" ? "danger" : "warning"}>
									{c.id} {c.action === "cap_score" ? "改分" : "只标记"}
								</Pill>
								<span>{c.desc}</span>
							</div>
						))}
					</div>
					<p className="text-xs text-text-muted">
						校准哲学:改分极少(仅 C1 合规封顶,且原始分透明保留),其余只标「待复核 ⚠」——
						被标记的记录是人审样本的第一批候选。
					</p>
				</CardContent>
			</Card>
			{/* ③ 提示词与模型(可操作,复用 /eval-prompts 运行时覆盖机制) */}
			{std.prompt_override ? (
				<p className="rounded-md bg-brand-warning/10 p-3 text-xs text-brand-warning">
					当前判官 prompt 处于运行时覆盖态——此后产生的评分记录将标记 prompt_override,
					与默认版评分可区分。
				</p>
			) : null}
			<EvalPromptEditors
				keys={["cx_judge"]}
				modelNode="cx"
				title={`③ CX 判官提示词与模型 · ${std.prompt_version} · 改完保存即生效,不走部署`}
				intro={
					<>
						system 段按 reason 分块存 rubric 判据(调判据措辞=运营动作,不发版);user 段{" "}
						<code className="rounded bg-surface-card px-1 font-mono">
							{"{{conversation}} / {{aux_block}}"}
						</code>{" "}
						按运行值注入。输出 JSON 键序 reasons 先于 cx_score——判官必须先逐条落定 7 项再合成总分。
					</>
				}
			/>
		</div>
	);
}

/* ====================== C35 指标统计:概览 + CX 评分子页签 ====================== */
/** 率值格式(0-1 → %);n<5 显示 -- 防"1/1=100%"假信号 */
function guardedPct(value: number | null, n: number): string {
	if (value == null || n < 5) return "--";
	return `${Math.round(value * 100)}%`;
}

/** by_date 里的逐天分布求和 → DistList 行。取值域来自数据,不硬编码枚举。 */
function sumDist(
	days: OverviewByDate[],
	pick: (b: OverviewByDate) => Record<string, number> | undefined,
): { key: string; count: number }[] {
	const acc: Record<string, number> = {};
	for (const b of days) {
		for (const [k, v] of Object.entries(pick(b) ?? {})) acc[k] = (acc[k] ?? 0) + v;
	}
	return Object.entries(acc)
		.map(([key, count]) => ({ key, count }))
		.sort((a, b) => b.count - a.count);
}

/** 窗口澄清率:按天加权还原为总体占比(逐天率直接平均会被小样本天拉偏)。 */
function fmtClarify(days: OverviewByDate[]): string {
	let num = 0;
	let den = 0;
	for (const b of days) {
		if (b.answer.clarify_rate == null) continue;
		num += b.answer.clarify_rate * b.answer.total;
		den += b.answer.total;
	}
	return den ? `${Math.round((num / den) * 100)}%` : "--";
}

/** 成本估算只覆盖单价表内的模型。漏算过就得说出来——静默按 $0 计会把成本读低。 */
function unpricedNote(kpi: OverviewKpi): string {
	const missed = Object.values(kpi.unpriced_models ?? {}).reduce((a, b) => a + b, 0);
	return missed > 0 ? ` · ${missed} 次未计价` : "";
}

const OVERVIEW_GOTO: { sub: StatsSub; label: string }[] = [
	{ sub: "answer", label: "应答效果" },
	{ sub: "eval", label: "场景评测" },
	{ sub: "cx", label: "CX 评分" },
	{ sub: "rca", label: "归因分布" },
	{ sub: "token", label: "Token 用量" },
	{ sub: "timing", label: "耗时统计" },
];

/** 概览:6 模块 KPI(值+环比+迷你趋势)+ 质量率/活动量按天趋势 + 模块速览下钻。 */
function OverviewPanel({ days, onGoto }: { days: number; onGoto: (sub: StatsSub) => void }) {
	const q = useStatsOverview(days);
	const d = q.data;
	if (q.isLoading) return <LoadingInline />;
	if (!d) return null;
	const k = d.kpis;
	const dates = d.by_date.map((b) => b.date);
	const empty = !d.by_date.length;
	const pctDelta = (n: number) => `${Math.round(n * 100)}pp`;

	return (
		<div className="space-y-4" data-testid="stats-overview">
			<p className="text-xs text-text-muted">
				各模块核心指标一屏巡检;Δ = 本窗口 vs 上一同长窗口;率类样本 &lt;5 显示 --(防小样本误读)。每
				30s 自动刷新。
			</p>
			{empty ? (
				<div className="rounded-md border border-surface-border p-4 text-sm text-text-muted">
					该窗口暂无任何运行数据。跑「试跑」/「评测」/「归因」/「CX
					评分」后,这里汇总各模块核心指标与趋势;可切「全部」看历史。
				</div>
			) : (
				<>
					{/* ① 被测侧 KPI:客服 agent 的表现 */}
					<div className="text-xs font-medium text-text-secondary">被测侧 · 客服表现</div>
					<div className="grid grid-cols-2 gap-2 md:grid-cols-3 2xl:grid-cols-6">
						<StatTile
							testId="ov-kpi-answer"
							label="应答正确率"
							value={guardedPct(k.answer_rate.value, k.answer_rate.n ?? 0)}
							delta={
								(k.answer_rate.n ?? 0) >= 5 ? (
									<DeltaBadge
										value={k.answer_rate.value}
										prev={k.answer_rate.prev}
										goodWhen="up"
										fmt={pctDelta}
									/>
								) : null
							}
							spark={k.answer_rate.spark}
							hint={
								<span>
									金标 {k.answer_rate.n} · 执行 {k.answer_rate.total}
								</span>
							}
						/>
						<StatTile
							testId="ov-kpi-eval"
							label="场景评测 pass 率"
							value={guardedPct(k.eval_pass.value, k.eval_pass.n ?? 0)}
							delta={
								(k.eval_pass.n ?? 0) >= 5 ? (
									<DeltaBadge
										value={k.eval_pass.value}
										prev={k.eval_pass.prev}
										goodWhen="up"
										fmt={pctDelta}
									/>
								) : null
							}
							spark={k.eval_pass.spark}
							hint={<span>{k.eval_pass.n} 张评分卡</span>}
						/>
						<StatTile
							testId="ov-kpi-cx"
							label="Overall CX"
							value={guardedPct(k.cx_positive.value, k.cx_positive.n ?? 0)}
							delta={
								(k.cx_positive.n ?? 0) >= 5 ? (
									<DeltaBadge
										value={k.cx_positive.value}
										prev={k.cx_positive.prev}
										goodWhen="up"
										fmt={pctDelta}
									/>
								) : null
							}
							spark={k.cx_positive.spark}
							hint={<span>已评 {k.cx_positive.n} 会话 · 4-5 分占比</span>}
						/>
						<StatTile
							testId="ov-kpi-rca"
							label="归因记录"
							value={String(k.rca_count.value ?? 0)}
							delta={
								<DeltaBadge value={k.rca_count.value} prev={k.rca_count.prev} goodWhen="neutral" />
							}
							spark={k.rca_count.spark}
							hint={
								<span>
									需复核{" "}
									{k.rca_count.needs_review_rate == null
										? "—"
										: `${Math.round(k.rca_count.needs_review_rate * 100)}%`}
								</span>
							}
						/>
						<StatTile
							testId="ov-kpi-token"
							label="Token 用量"
							value={fmtTok(k.tokens_total.value ?? 0)}
							delta={
								<DeltaBadge
									value={k.tokens_total.value}
									prev={k.tokens_total.prev}
									goodWhen="down"
									fmt={fmtCompact}
								/>
							}
							spark={k.tokens_total.spark}
							hint={
								<span>
									{k.tokens_total.calls} 调用
									{k.tokens_total.cost_usd_est != null ? ` · ≈$${k.tokens_total.cost_usd_est}` : ""}
									{unpricedNote(k.tokens_total)}
								</span>
							}
						/>
						<StatTile
							testId="ov-kpi-timing"
							label="单轮耗时 p95"
							value={k.timing_p95.value == null ? "--" : fmtMs(k.timing_p95.value)}
							delta={
								<DeltaBadge
									value={k.timing_p95.value}
									prev={k.timing_p95.prev}
									goodWhen="down"
									fmt={fmtMs}
								/>
							}
							spark={k.timing_p95.spark}
							hint={<span>n={k.timing_p95.n}</span>}
						/>
					</div>
					{/* ①b 评测侧 KPI:量的是尺子本身。混进被测侧看,会把判官退化读成客服变差 */}
					<div className="text-xs font-medium text-text-secondary">
						评测侧 · 装置健康度
						<span className="ml-2 font-normal text-text-muted">
							尺子不准时,上面那组数就没有可信度前提
						</span>
					</div>
					<div className="grid grid-cols-2 gap-2 md:grid-cols-3">
						<StatTile
							testId="ov-kpi-judge"
							label="判官一致率(难档)"
							value={guardedPct(k.judge_agreement.value, k.judge_agreement.n ?? 0)}
							delta={
								(k.judge_agreement.n ?? 0) >= 5 ? (
									<DeltaBadge
										value={k.judge_agreement.value}
										prev={k.judge_agreement.prev}
										goodWhen="up"
										fmt={pctDelta}
									/>
								) : null
							}
							spark={k.judge_agreement.spark}
							hint={
								k.judge_agreement.gold_set ? (
									<span>
										{k.judge_agreement.gold_set} · 冤杀 {k.judge_agreement.false_alarm ?? 0} / 漏网{" "}
										{k.judge_agreement.miss ?? 0}
									</span>
								) : (
									<span>尚无标定记录</span>
								)
							}
						/>
						<StatTile
							testId="ov-kpi-guidance"
							label="指南遵循率"
							value={guardedPct(k.guidance_follow_rate.value, k.guidance_follow_rate.n ?? 0)}
							delta={
								(k.guidance_follow_rate.n ?? 0) >= 5 ? (
									<DeltaBadge
										value={k.guidance_follow_rate.value}
										prev={k.guidance_follow_rate.prev}
										goodWhen="up"
										fmt={pctDelta}
									/>
								) : null
							}
							spark={k.guidance_follow_rate.spark}
							hint={<span>{k.guidance_follow_rate.n} 条裁决 · 对照批不计</span>}
						/>
						<StatTile
							testId="ov-kpi-actor"
							label="演员失职率"
							value={guardedPct(k.actor_violation_rate.value, k.actor_violation_rate.n ?? 0)}
							delta={
								(k.actor_violation_rate.n ?? 0) >= 5 ? (
									<DeltaBadge
										value={k.actor_violation_rate.value}
										prev={k.actor_violation_rate.prev}
										goodWhen="down"
										fmt={pctDelta}
									/>
								) : null
							}
							spark={k.actor_violation_rate.spark}
							hint={<span>{k.actor_violation_rate.n} 张卡 · 高则分低可能是顾客演崩</span>}
						/>
					</div>
					{/* ①c 失败构成(刀B):率掉了,是掉在哪 */}
					<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
						<DistList
							title="应答终止分布(stop)"
							rows={sumDist(d.by_date, (b) => b.answer.stop_dist)}
						/>
						<DistList
							title="评测失败维度(failed_dims)"
							rows={sumDist(d.by_date, (b) => b.eval.failed_dims)}
						/>
					</div>
					{/* ② 趋势区:率(同 0-100% 单轴)/ 量(计数单轴)——不做双轴 */}
					<div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
						<div className="rounded-md border border-surface-border p-3">
							<div className="mb-1 text-xs font-medium text-text-secondary">
								质量率趋势(按天 · %)
							</div>
							<TrendChart
								testId="ov-trend-rates"
								dates={dates}
								percent
								series={[
									{
										key: "answer",
										label: "应答正确率",
										color: CHART.info,
										points: d.by_date.map((b) =>
											b.answer.rate == null ? null : b.answer.rate * 100,
										),
									},
									{
										key: "eval",
										label: "评测 pass 率",
										color: CHART.accent,
										points: d.by_date.map((b) =>
											b.eval.pass_rate == null ? null : b.eval.pass_rate * 100,
										),
									},
									{
										key: "cx",
										label: "Overall CX",
										color: CHART.success,
										// 刀A:by_date.cx.positive_rate 已统一为 ratio,与上面两条同样 ×100 上同一轴
										points: d.by_date.map((b) =>
											b.cx.positive_rate == null ? null : b.cx.positive_rate * 100,
										),
									},
								]}
							/>
						</div>
						<div className="rounded-md border border-surface-border p-3">
							<div className="mb-1 text-xs font-medium text-text-secondary">
								活动量趋势(按天 · 次数)
							</div>
							<DayBars
								testId="ov-trend-volume"
								dates={dates}
								series={[
									{
										key: "answer",
										label: "运行记录",
										color: CHART.info,
										points: d.by_date.map((b) => b.answer.total),
									},
									{
										key: "eval",
										label: "评测 trial",
										color: CHART.accent,
										points: d.by_date.map((b) => b.eval.trials),
									},
									{
										key: "cx",
										label: "CX 评分",
										color: CHART.success,
										points: d.by_date.map((b) => b.cx.scored),
									},
								]}
							/>
						</div>
					</div>
				</>
			)}
			{/* ③ 模块速览下钻 */}
			<div className="flex flex-wrap gap-2">
				{OVERVIEW_GOTO.map((m) => (
					<Button
						key={m.sub}
						type="button"
						size="sm"
						variant="outline"
						onClick={() => onGoto(m.sub)}
					>
						{m.label} →
					</Button>
				))}
			</div>
		</div>
	);
}

/** CX 1-5 分的固定色序(发散:负端暖 → 中性灰 → 正端绿;B3=70% 透明度过渡档) */
const CX_SCORE_COLORS: Record<string, string> = {
	"1": CHART.danger,
	"2": `${CHART.danger}B3`,
	"3": CHART.inkMuted,
	"4": `${CHART.success}B3`,
	"5": CHART.success,
};

/** CX 评分统计子页签:KPI + 1-5 发散分段条 + 负面 reasons + 按天趋势(C35)。 */
function CxStatsPanel({ days, onGotoRecords }: { days: number; onGotoRecords: () => void }) {
	const sum = useCxSummary(days);
	const std = useCxStandard().data;
	const ov = useStatsOverview(days);
	const s = sum.data;
	if (sum.isLoading) return <LoadingInline />;
	if (!s || s.scored_sessions === 0) {
		return (
			<div className="rounded-md border border-surface-border p-4 text-sm text-text-muted">
				该窗口暂无 CX 评分。去「运行记录」点开一条记录评 CX 分后,这里给出 Overall CX(4-5
				分占比)、1-5 分布、负面 reasons 与按天趋势;可切「全部」看历史。
			</div>
		);
	}
	const reasonLabel = (key: string) =>
		(std?.reasons ?? []).find((r) => r.key === key)?.label ?? key;
	const negRows = Object.entries(s.negative_reasons).sort((a, b) => b[1] - a[1]);
	// 刀B:七维**三档全分布**(此前只数负面,看不出分掉在哪一维)。
	// 维度顺序与档位取值都走注册表(CX 评分标准页同源),不在前端硬编码。
	const reasonDist = (ov.data?.by_date ?? []).reduce<Record<string, Record<string, number>>>(
		(acc, b) => {
			for (const [k, buckets] of Object.entries(b.cx.reason_dist ?? {})) {
				acc[k] ??= {};
				for (const [v, c] of Object.entries(buckets)) acc[k][v] = (acc[k][v] ?? 0) + c;
			}
			return acc;
		},
		{},
	);
	const negMax = Math.max(1, ...negRows.map(([, v]) => v));
	const dates = (ov.data?.by_date ?? []).map((b) => b.date);

	return (
		<div className="space-y-4" data-testid="cx-stats-panel">
			<p className="text-xs text-text-muted">
				会话级服务体验评分的窗口统计(重评取新,同一会话只计最新一次)。口径与「CX 评分标准」页同源。
			</p>
			<div className="flex flex-wrap items-center gap-3">
				<Button type="button" size="sm" variant="outline" onClick={onGotoRecords}>
					查看评分记录 →
				</Button>
			</div>
			{/* 七维三档全分布(刀B) */}
			{Object.keys(reasonDist).length ? (
				<div
					className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
					data-testid="cx-reason-dist"
				>
					{Object.entries(reasonDist).map(([key, buckets]) => (
						<DistList
							key={key}
							title={reasonLabel(key)}
							rows={Object.entries(buckets)
								.map(([k, count]) => ({ key: k, count }))
								.sort((a, b) => b.count - a.count)}
						/>
					))}
				</div>
			) : null}
			{/* KPI */}
			<div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
				<Kpi label="已评会话" value={String(s.scored_sessions)} />
				<Kpi
					label="Overall CX(4-5 分占比)"
					value={s.scored_sessions < 5 ? "--" : `${s.overall_cx}%`}
					tone={s.scored_sessions < 5 ? "muted" : rateTone(s.overall_cx / 100)}
				/>
				<Kpi
					label="待复核 ⚠"
					value={String(s.needs_review)}
					tone={s.needs_review > 0 ? "warning" : "muted"}
				/>
			</div>
			{/* 1-5 发散分段条(有序刻度:负端 → 中性 → 正端) */}
			<div className="rounded-md border border-surface-border p-3">
				<div className="mb-2 text-xs font-medium text-text-secondary">
					1-5 分分布(1-2 负面 · 3 中性 · 4-5 正面)
				</div>
				<SegmentBar
					testId="cx-score-dist"
					segments={["1", "2", "3", "4", "5"].map((sc) => ({
						key: sc,
						label: `${sc} 分`,
						value: s.score_dist[sc] ?? 0,
						color: CX_SCORE_COLORS[sc] ?? CHART.inkMuted,
					}))}
				/>
			</div>
			{/* 负面 reasons 频次(注册表同源中文名) */}
			<div className="rounded-md border border-surface-border p-3">
				<div className="mb-2 text-xs font-medium text-text-secondary">负面 reasons 频次</div>
				{negRows.length ? (
					<div className="space-y-1.5">
						{negRows.map(([key, v]) => (
							<div key={key} className="flex items-center gap-2 text-xs">
								<span
									className="w-28 shrink-0 truncate text-text-secondary"
									title={reasonLabel(key)}
								>
									{reasonLabel(key)}
								</span>
								<MiniBar value={v} max={negMax} tone="danger" className="flex-1" />
								<span className="w-8 shrink-0 text-right font-mono text-text-secondary">{v}</span>
							</div>
						))}
					</div>
				) : (
					<span className="text-xs text-text-muted">无负面 reason,窗口内体验全正向。</span>
				)}
			</div>
			{/* 趋势(率/量分图,不做双轴) */}
			{dates.length ? (
				<div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
					<div className="rounded-md border border-surface-border p-3">
						<div className="mb-1 text-xs font-medium text-text-secondary">
							Overall CX 趋势(按天)
						</div>
						<TrendChart
							testId="cx-trend-rate"
							dates={dates}
							percent
							series={[
								{
									key: "cx",
									label: "Overall CX",
									color: CHART.success,
									points: (ov.data?.by_date ?? []).map((b) =>
										b.cx.positive_rate == null ? null : b.cx.positive_rate * 100,
									),
								},
							]}
						/>
					</div>
					<div className="rounded-md border border-surface-border p-3">
						<div className="mb-1 text-xs font-medium text-text-secondary">评分量(按天)</div>
						<DayBars
							testId="cx-trend-volume"
							dates={dates}
							series={[
								{
									key: "scored",
									label: "CX 评分数",
									color: CHART.success,
									points: (ov.data?.by_date ?? []).map((b) => b.cx.scored),
								},
							]}
						/>
					</div>
				</div>
			) : null}
		</div>
	);
}
